import type { AreaDef, RoleProfile, WorkspaceSpec } from "../types/domain";
import { LANGUAGE_OPTIONS } from "../../../shared/languages";

// Canonical fictional fixtures for all Institution routes. Keep network concerns in services.

export const AREAS: AreaDef[] = [
  {
    id: "command",
    label: "Institution Command",
    shortLabel: "Command",
    icon: "home",
    accent: "violet",
    routes: [
      {
        id: "command-overview",
        title: "Executive Overview",
        description:
          "A unified view of academic delivery, student success, institutional risk, capacity, quality and connected learning platforms.",
        group: "Institution Command",
        icon: "home",
      },
      {
        id: "action-centre",
        title: "Priority Action Centre",
        description:
          "Approvals, risks, exceptions and time-sensitive institutional work requiring named owners.",
        group: "Institution Command",
        icon: "bolt",
        badge: "12",
      },
      {
        id: "institution-health",
        title: "Institutional Health",
        description:
          "Monitor academic, operational, financial, quality and learner-success indicators together.",
        group: "Institution Command",
        icon: "pulse",
      },
      {
        id: "academic-calendar",
        title: "Academic Calendar",
        description:
          "Terms, intakes, examinations, live classes, Labs windows, review panels and institution milestones.",
        group: "Institution Command",
        icon: "calendar",
      },
      {
        id: "approvals",
        title: "Approvals & Decisions",
        description:
          "Curriculum, assessment, access, policy, funding and connected-platform approvals with audit history.",
        group: "Institution Command",
        icon: "check",
        badge: "8",
      },
    ],
  },
  {
    id: "structure",
    label: "Academic Structure",
    shortLabel: "Structure",
    icon: "building",
    accent: "blue",
    routes: [
      {
        id: "institution-profile",
        title: "Institution Profile",
        description:
          "Institution identity, accreditation references, operating model, contacts and public academic information.",
        group: "Institution Structure",
        icon: "building",
      },
      {
        id: "campuses",
        title: "Campuses & Centres",
        description:
          "Manage campuses, satellite centres, learning hubs, time zones, accessibility and local contacts.",
        group: "Institution Structure",
        icon: "map",
      },
      {
        id: "schools-faculties",
        title: "Schools & Faculties",
        description:
          "Academic schools and faculties with leadership, programs, quality owners and reporting lines.",
        group: "Institution Structure",
        icon: "layers",
      },
      {
        id: "departments",
        title: "Departments",
        description: "Departments, subject areas, academic leads, staff capacity and program relationships.",
        group: "Institution Structure",
        icon: "network",
      },
      {
        id: "academic-units",
        title: "Academic Units",
        description: "Institutes, centres, research units, libraries and specialist teaching entities.",
        group: "Institution Structure",
        icon: "grid",
      },
      {
        id: "committees",
        title: "Committees & Boards",
        description: "Senate, academic boards, assessment boards, ethics committees and quality committees.",
        group: "Governance Structure",
        icon: "users",
      },
      {
        id: "terms-calendars",
        title: "Terms & Academic Years",
        description:
          "Academic years, semesters, terms, teaching weeks, examination periods and closure dates.",
        group: "Academic Calendar",
        icon: "calendar",
      },
    ],
  },
  {
    id: "people",
    label: "People & Access",
    shortLabel: "People",
    icon: "users",
    accent: "cyan",
    routes: [
      {
        id: "people-directory",
        title: "Institution Directory",
        description:
          "Students, instructors, academic leaders, advisors, administrators and authorized external reviewers.",
        group: "People",
        icon: "users",
      },
      {
        id: "students",
        title: "Students & Learners",
        description:
          "Enrolment, program status, attendance, progress, support, accommodations and credential readiness.",
        group: "People",
        icon: "student",
      },
      {
        id: "instructors",
        title: "Instructors & Faculty",
        description:
          "Teaching assignments, workload, class access, assessment scope, Labs role and professional development.",
        group: "People",
        icon: "teacher",
      },
      {
        id: "advisors-support",
        title: "Advisors & Support Staff",
        description:
          "Academic advisors, counsellors, accessibility teams, librarians and student-success staff.",
        group: "People",
        icon: "heart",
      },
      {
        id: "external-reviewers",
        title: "External Examiners & Reviewers",
        description:
          "Time-bound external access for moderation, accreditation, ethics and credential review.",
        group: "People",
        icon: "shield",
      },
      {
        id: "invitations",
        title: "Invitations & Onboarding",
        description:
          "Invite, import and onboard people with role-aware steps and connected-platform preparation.",
        group: "Access",
        icon: "mail",
        badge: "7",
      },
      {
        id: "roles-permissions",
        title: "Roles & Permissions",
        description:
          "Institutional roles, scoped permissions, segregation of duties and least-privilege access.",
        group: "Access",
        icon: "lock",
      },
      {
        id: "access-reviews",
        title: "Access Reviews",
        description:
          "Periodic reviews, inactive accounts, role changes, exceptions and immediate revocation.",
        group: "Access",
        icon: "eye",
        badge: "5",
      },
      {
        id: "directory-imports",
        title: "Directory Imports",
        description:
          "SIS, HRIS, CSV and API imports with mapping, validation, duplicate handling and audit evidence.",
        group: "Provisioning",
        icon: "upload",
      },
    ],
  },
  {
    id: "academics",
    label: "Programs & Curriculum",
    shortLabel: "Academics",
    icon: "book",
    accent: "green",
    routes: [
      {
        id: "program-portfolio",
        title: "Program Portfolio",
        description: "Degrees, diplomas, certificates, short courses and Academy-connected programs.",
        group: "Programs",
        icon: "book",
      },
      {
        id: "curriculum-maps",
        title: "Curriculum Maps",
        description:
          "Program outcomes, modules, courses, prerequisites, assessments, Labs and credential evidence.",
        group: "Curriculum",
        icon: "map",
      },
      {
        id: "course-catalogue",
        title: "Course Catalogue",
        description: "Course specifications, credits, delivery modes, owners, versions and availability.",
        group: "Curriculum",
        icon: "library",
      },
      {
        id: "cohorts-intakes",
        title: "Cohorts & Intakes",
        description:
          "Create and monitor intakes, cohorts, capacity, instructors, schedules and student progression.",
        group: "Delivery",
        icon: "users",
      },
      {
        id: "timetables",
        title: "Timetables",
        description: "Classes, rooms, instructors, online sessions, Labs windows and conflict detection.",
        group: "Delivery",
        icon: "calendar",
      },
      {
        id: "learning-pathways",
        title: "Learning Pathways",
        description: "Core, elective, bridging, remedial, professional and multidisciplinary pathways.",
        group: "Curriculum",
        icon: "path",
      },
      {
        id: "curriculum-approvals",
        title: "Curriculum Approvals",
        description: "Proposal, review, senate approval, versioning, publication and retirement workflows.",
        group: "Governance",
        icon: "check",
        badge: "4",
      },
      {
        id: "course-equivalencies",
        title: "Equivalencies & Credit Transfer",
        description:
          "Course matching, recognition of prior learning, exemptions and credit-transfer decisions.",
        group: "Records",
        icon: "swap",
      },
      {
        id: "academic-resources",
        title: "Teaching & Learning Resources",
        description:
          "Approved readings, media, datasets, briefs, templates and accessibility-ready resources.",
        group: "Resources",
        icon: "folder",
      },
    ],
  },
  {
    id: "class",
    label: "Chatoner Class",
    shortLabel: "Class",
    icon: "video",
    accent: "purple",
    routes: [
      {
        id: "class-connection",
        title: "Class Connection",
        description:
          "Independent Chatoner Class connection, signed handoff readiness and institutional visibility boundaries.",
        group: "Chatoner Class",
        icon: "link",
      },
      {
        id: "live-classes",
        title: "Live Classes",
        description:
          "Upcoming and active classes with automatically prepared links and codes for signed-in users.",
        group: "Chatoner Class",
        icon: "video",
        badge: "3",
      },
      {
        id: "class-schedule",
        title: "Class Schedule",
        description: "Institution, campus, program, cohort and instructor live-session schedule.",
        group: "Chatoner Class",
        icon: "calendar",
      },
      {
        id: "create-class",
        title: "Create Class",
        description:
          "Create a session, assign its instructor and cohort, and generate a unique Class link and code.",
        group: "Teaching Operations",
        icon: "plus",
      },
      {
        id: "attendance",
        title: "Attendance",
        description:
          "Session attendance summaries, late arrivals, excused absences and privacy-aware interventions.",
        group: "Teaching Operations",
        icon: "check",
      },
      {
        id: "recordings",
        title: "Recordings & Captions",
        description: "Authorized recordings, transcripts, captions, languages, retention and access scope.",
        group: "Teaching Operations",
        icon: "play",
      },
      {
        id: "office-hours",
        title: "Office Hours & Tutorials",
        description: "Instructor office hours, advising sessions, tutorials and smaller-group huddles.",
        group: "Teaching Operations",
        icon: "clock",
      },
      {
        id: "class-policies",
        title: "Class Policies",
        description: "Recording, attendance, captions, language, safeguarding and data-retention policies.",
        group: "Governance",
        icon: "shield",
      },
    ],
  },
  {
    id: "labs",
    label: "Chatoner AI Labs",
    shortLabel: "Labs",
    icon: "flask",
    accent: "aqua",
    routes: [
      {
        id: "labs-connection",
        title: "Labs Connection",
        description:
          "Independent labs.chatoner.com connection, entitlement sync and institutional data boundaries.",
        group: "Chatoner AI Labs",
        icon: "link",
      },
      {
        id: "eligible-labs",
        title: "Eligible & Intended Labs",
        description: "Labs connected to programs, courses, assignments and learner entitlements.",
        group: "Chatoner AI Labs",
        icon: "flask",
      },
      {
        id: "lab-entitlements",
        title: "Entitlements & Capacity",
        description:
          "Seats, time, compute, tokens, program access and sponsored practical-learning capacity.",
        group: "Administration",
        icon: "key",
      },
      {
        id: "lab-assignments",
        title: "Lab Assignments",
        description: "Map intended Labs to outcomes, courses, assignments, cohorts and credential evidence.",
        group: "Academic Coordination",
        icon: "clipboard",
      },
      {
        id: "synced-runs",
        title: "Synced Cohort Runs",
        description: "Read-only operational status returned from Labs for authorized institution roles.",
        group: "Academic Coordination",
        icon: "pulse",
      },
      {
        id: "returned-evidence",
        title: "Returned Evidence",
        description: "Consented completion evidence, verification status and Academy record relationships.",
        group: "Evidence",
        icon: "certificate",
      },
      {
        id: "labs-analytics",
        title: "Aggregate Labs Analytics",
        description:
          "Privacy-thresholded practical-learning participation, completion, safety and capacity trends.",
        group: "Reporting",
        icon: "chart",
      },
      {
        id: "labs-safety-sync",
        title: "Access & Safety Sync",
        description:
          "Policy, entitlement, monitoring mode, data-scope and emergency-revocation synchronization.",
        group: "Governance",
        icon: "shield",
      },
      {
        id: "open-labs-workspace",
        title: "Open Labs Workspace",
        description:
          "Secure handoff to the independent Labs workspace appropriate to the user’s authorized role.",
        group: "Chatoner AI Labs",
        icon: "external",
      },
    ],
  },
  {
    id: "build",
    label: "Build Rooms",
    shortLabel: "Build Rooms",
    icon: "message",
    accent: "orange",
    routes: [
      {
        id: "room-portfolio",
        title: "Build Room Portfolio",
        description: "Parent class rooms, course rooms, project rooms and connected learning evidence.",
        group: "Build Rooms",
        icon: "message",
      },
      {
        id: "parent-rooms",
        title: "Parent Rooms",
        description:
          "Shared class context, announcements, requirements, plans, resources and whole-group decisions.",
        group: "Build Rooms",
        icon: "users",
      },
      {
        id: "smaller-groups",
        title: "Smaller Groups",
        description:
          "Instructor-formed subgroups within a parent class, with institution-approved privacy and AI boundaries.",
        group: "Build Rooms",
        icon: "network",
      },
      {
        id: "cross-group-progress",
        title: "Cross-group Progress",
        description:
          "Aggregate progress, evidence, blockers and review status without exposing private conversations.",
        group: "Oversight",
        icon: "chart",
      },
      {
        id: "evidence-coverage",
        title: "Evidence Coverage",
        description: "Requirement-to-artifact-to-test-to-evidence traceability across rooms and groups.",
        group: "Evidence",
        icon: "certificate",
      },
      {
        id: "room-templates",
        title: "Room Templates",
        description: "Institution-approved room, subgroup, permission, evidence and review templates.",
        group: "Configuration",
        icon: "layout",
      },
      {
        id: "room-approvals",
        title: "Room Policies & Approvals",
        description:
          "Approve high-risk templates, external access, AI boundaries and safeguarding exceptions.",
        group: "Governance",
        icon: "check",
        badge: "3",
      },
      {
        id: "room-review-queue",
        title: "Review Queue",
        description: "Instructor and authorized reviewer work awaiting academic judgement.",
        group: "Oversight",
        icon: "inbox",
        badge: "11",
      },
      {
        id: "room-members",
        title: "Member Directory",
        description:
          "Large-room searchable directory, role filters, group assignments and scoped contact actions.",
        group: "Build Rooms",
        icon: "users",
      },
    ],
  },
  {
    id: "assessment",
    label: "Assessment & Records",
    shortLabel: "Assessment",
    icon: "clipboard",
    accent: "red",
    routes: [
      {
        id: "assessment-calendar",
        title: "Assessment Calendar",
        description:
          "Assignments, examinations, practicals, panels, moderation windows and publication dates.",
        group: "Assessment",
        icon: "calendar",
      },
      {
        id: "assignments",
        title: "Assignments",
        description:
          "Institution-approved assignments, outcomes, briefs, rubrics, evidence and delivery relationships.",
        group: "Assessment",
        icon: "clipboard",
      },
      {
        id: "gradebook",
        title: "Gradebook",
        description:
          "Authorized marks, statuses, moderation indicators, missing results and publication readiness.",
        group: "Assessment",
        icon: "table",
      },
      {
        id: "rubrics",
        title: "Rubrics & Standards",
        description: "Outcome-aligned rubrics, grade descriptors, calibration and version control.",
        group: "Assessment",
        icon: "sliders",
      },
      {
        id: "examinations",
        title: "Examinations",
        description: "Exam timetables, venues, access arrangements, invigilation and controlled materials.",
        group: "Assessment",
        icon: "file",
      },
      {
        id: "moderation",
        title: "Moderation & External Review",
        description: "Sampling, second marking, external examination, disagreements and decision records.",
        group: "Quality",
        icon: "shield",
        badge: "6",
      },
      {
        id: "academic-appeals",
        title: "Appeals & Special Consideration",
        description: "Documented student requests, evidence, panel decisions and protected records.",
        group: "Student Rights",
        icon: "gavel",
      },
      {
        id: "academic-records",
        title: "Academic Records",
        description: "Official results, progression, standing, awards, changes and immutable record history.",
        group: "Records",
        icon: "archive",
      },
      {
        id: "transcripts",
        title: "Transcripts & Statements",
        description: "Authorized transcript generation, statements of results and verification references.",
        group: "Records",
        icon: "file",
      },
      {
        id: "progression-awards",
        title: "Progression & Awards",
        description: "Progression rules, boards, completion decisions, awards and graduation readiness.",
        group: "Records",
        icon: "award",
      },
    ],
  },
  {
    id: "research",
    label: "Research, Projects & Credentials",
    shortLabel: "Research",
    icon: "rocket",
    accent: "violet",
    routes: [
      {
        id: "research-overview",
        title: "Research Overview",
        description: "Student and faculty research portfolio, supervision, ethics, milestones and outputs.",
        group: "Research",
        icon: "rocket",
      },
      {
        id: "research-projects",
        title: "Research Projects",
        description: "Project proposals, teams, supervisors, timelines, evidence and dissemination.",
        group: "Research",
        icon: "folder",
      },
      {
        id: "ethics-approvals",
        title: "Research Ethics",
        description: "Applications, risk classification, consent, data management and committee decisions.",
        group: "Research Governance",
        icon: "shield",
        badge: "4",
      },
      {
        id: "industry-partnerships",
        title: "Industry & Community Partnerships",
        description: "External briefs, placements, simulations, sponsors, permissions and outcomes.",
        group: "Partnerships",
        icon: "handshake",
      },
      {
        id: "capstones",
        title: "Capstones & Final Projects",
        description: "Capstone requirements, panels, demos, repositories, evidence and final decisions.",
        group: "Projects",
        icon: "trophy",
      },
      {
        id: "publications",
        title: "Publications & Outputs",
        description:
          "Approved publications, posters, datasets, demonstrations and institutional repository links.",
        group: "Research Outputs",
        icon: "library",
      },
      {
        id: "credentials",
        title: "Credentials & Awards",
        description: "Academy and institution credentials, evidence readiness, decisions and verification.",
        group: "Credentials",
        icon: "certificate",
      },
      {
        id: "credential-verification",
        title: "Credential Verification",
        description: "Verification IDs, status, issuer authority, evidence boundaries and employer checks.",
        group: "Credentials",
        icon: "check",
      },
    ],
  },
  {
    id: "success",
    label: "Student Success & Wellbeing",
    shortLabel: "Success",
    icon: "heart",
    accent: "green",
    routes: [
      {
        id: "student-success-dashboard",
        title: "Student Success Overview",
        description: "Engagement, attendance, progression, support, wellbeing and intervention indicators.",
        group: "Student Success",
        icon: "heart",
      },
      {
        id: "advising",
        title: "Academic Advising",
        description: "Advisor caseloads, plans, referrals, appointments and documented next actions.",
        group: "Support",
        icon: "message",
      },
      {
        id: "risk-interventions",
        title: "Risk & Interventions",
        description: "Privacy-aware signals, triage, named owners, consent, action plans and follow-up.",
        group: "Support",
        icon: "alert",
        badge: "9",
      },
      {
        id: "wellbeing",
        title: "Wellbeing & Safeguarding",
        description:
          "Protected support pathways, urgent escalation, safeguarding and minimum necessary access.",
        group: "Support",
        icon: "shield",
      },
      {
        id: "accessibility",
        title: "Accessibility & Accommodations",
        description:
          "Approved adjustments, accessible materials, Class captions, assessment arrangements and Labs access.",
        group: "Inclusion",
        icon: "accessibility",
      },
      {
        id: "scholarships-aid",
        title: "Scholarships & Student Aid",
        description:
          "Eligibility, applications, awards, disbursement milestones and protected finance boundaries.",
        group: "Funding",
        icon: "wallet",
      },
      {
        id: "career-readiness",
        title: "Career Readiness",
        description:
          "Skills evidence, portfolios, placements, mentoring, interviews and employer validation.",
        group: "Progression",
        icon: "briefcase",
      },
      {
        id: "alumni",
        title: "Alumni & Continuing Learning",
        description: "Alumni records, verified credentials, continuing programs and community participation.",
        group: "Community",
        icon: "users",
      },
    ],
  },
  {
    id: "quality",
    label: "Quality, Accreditation & Governance",
    shortLabel: "Quality",
    icon: "shield",
    accent: "blue",
    routes: [
      {
        id: "accreditation",
        title: "Accreditation",
        description: "Standards, evidence, owners, visits, findings, actions and renewal timelines.",
        group: "Quality Assurance",
        icon: "award",
      },
      {
        id: "quality-reviews",
        title: "Quality Reviews",
        description: "Program, course, teaching, assessment and student-experience review cycles.",
        group: "Quality Assurance",
        icon: "search",
      },
      {
        id: "outcomes-mapping",
        title: "Outcomes Mapping",
        description: "Institution, program, course, assessment, Lab and credential outcome coverage.",
        group: "Quality Assurance",
        icon: "map",
      },
      {
        id: "policy-centre",
        title: "Policy Centre",
        description: "Approved academic, assessment, data, AI, safeguarding and operational policies.",
        group: "Governance",
        icon: "book",
      },
      {
        id: "compliance",
        title: "Compliance",
        description: "Regulatory obligations, controls, attestations, evidence and exceptions.",
        group: "Governance",
        icon: "check",
      },
      {
        id: "audit-log",
        title: "Audit Log",
        description:
          "Attributable activity, decisions, permission changes, exports and connected-platform handoffs.",
        group: "Governance",
        icon: "history",
      },
      {
        id: "privacy-consent",
        title: "Privacy & Consent",
        description: "Purpose, lawful basis, consent, data scope, access, retention and subject rights.",
        group: "Data Governance",
        icon: "lock",
      },
      {
        id: "responsible-ai",
        title: "Responsible AI",
        description:
          "AI use boundaries, human authority, evaluation, transparency, escalation and emergency stop.",
        group: "AI Governance",
        icon: "bot",
      },
      {
        id: "incidents",
        title: "Incidents & Safeguarding",
        description:
          "Incident intake, severity, containment, communication, evidence and post-incident review.",
        group: "Risk",
        icon: "alert",
        badge: "2",
      },
      {
        id: "retention-deletion",
        title: "Retention & Deletion",
        description: "Record classes, retention schedules, legal holds, deletion workflows and evidence.",
        group: "Data Governance",
        icon: "trash",
      },
    ],
  },
  {
    id: "analytics",
    label: "Analytics & Reports",
    shortLabel: "Analytics",
    icon: "chart",
    accent: "cyan",
    routes: [
      {
        id: "executive-analytics",
        title: "Executive Analytics",
        description: "Academic outcomes, student success, quality, capacity, finance and platform adoption.",
        group: "Analytics",
        icon: "chart",
      },
      {
        id: "enrolment-analytics",
        title: "Admissions & Enrolment",
        description: "Applications, offers, enrolments, yield, capacity and demographic fairness indicators.",
        group: "Analytics",
        icon: "users",
      },
      {
        id: "learning-outcomes",
        title: "Learning Outcomes",
        description: "Outcome attainment, evidence coverage, course performance and improvement actions.",
        group: "Analytics",
        icon: "target",
      },
      {
        id: "attendance-engagement",
        title: "Attendance & Engagement",
        description: "Class, Build Room and Labs participation with privacy-aware thresholds.",
        group: "Analytics",
        icon: "pulse",
      },
      {
        id: "completion-progression",
        title: "Completion & Progression",
        description: "Retention, progression, completion forecasts, award readiness and time to completion.",
        group: "Analytics",
        icon: "path",
      },
      {
        id: "faculty-workload",
        title: "Faculty Workload",
        description: "Teaching, advising, assessment, supervision and review workload with capacity alerts.",
        group: "Analytics",
        icon: "briefcase",
      },
      {
        id: "platform-activity",
        title: "Class, Labs & Build Rooms",
        description: "Cross-platform aggregate usage, handoff success, evidence return and service health.",
        group: "Analytics",
        icon: "network",
      },
      {
        id: "report-builder",
        title: "Report Builder",
        description: "Compose governed reports with filters, minimum-group thresholds and export controls.",
        group: "Reporting",
        icon: "sliders",
      },
      {
        id: "scheduled-reports",
        title: "Scheduled Reports",
        description: "Audience, frequency, secure delivery, expiry, owners and audit history.",
        group: "Reporting",
        icon: "clock",
      },
      {
        id: "benchmarking-forecasts",
        title: "Benchmarking & Forecasts",
        description: "Internal trends, approved benchmarks, scenario assumptions and capacity forecasts.",
        group: "Planning",
        icon: "sparkles",
      },
    ],
  },
  {
    id: "resources",
    label: "Resources & Funding",
    shortLabel: "Resources",
    icon: "library",
    accent: "orange",
    routes: [
      {
        id: "library",
        title: "Library & Repository",
        description: "Learning resources, institutional repository, licences, access and usage.",
        group: "Academic Resources",
        icon: "library",
      },
      {
        id: "facilities",
        title: "Facilities & Learning Spaces",
        description: "Rooms, studios, accessibility, equipment, capacity and maintenance.",
        group: "Operations",
        icon: "building",
      },
      {
        id: "technology-assets",
        title: "Technology & Devices",
        description: "Devices, software entitlements, lending, support and responsible-use controls.",
        group: "Operations",
        icon: "monitor",
      },
      {
        id: "funding-budgets",
        title: "Funding & Budgets",
        description: "Program budgets, departments, cost centres, commitments and utilization.",
        group: "Finance",
        icon: "wallet",
      },
      {
        id: "scholarships-funding",
        title: "Scholarships & Grants",
        description: "Institutional funds, donor conditions, research grants and student aid.",
        group: "Finance",
        icon: "gift",
      },
      {
        id: "procurement",
        title: "Procurement",
        description: "Requests, approvals, suppliers, purchase orders and delivery evidence.",
        group: "Finance",
        icon: "cart",
      },
      {
        id: "billing",
        title: "Billing & Invoices",
        description: "Academy subscriptions, seats, Class and Labs capacity, invoices and finance contacts.",
        group: "Finance",
        icon: "receipt",
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations & Data",
    shortLabel: "Integrations",
    icon: "link",
    accent: "aqua",
    routes: [
      {
        id: "connected-apps",
        title: "Connected Applications",
        description: "Class, Labs, SIS, LMS, identity, library, finance and approved institutional systems.",
        group: "Integrations",
        icon: "link",
      },
      {
        id: "sis",
        title: "Student Information System",
        description: "Student, program, enrolment, result and academic-record synchronization.",
        group: "Institution Systems",
        icon: "database",
      },
      {
        id: "lms",
        title: "Learning Management System",
        description: "Course, roster, resource, activity and assessment-exchange boundaries.",
        group: "Institution Systems",
        icon: "book",
      },
      {
        id: "identity-sso",
        title: "Identity, SSO & SCIM",
        description: "Single sign-on, lifecycle provisioning, groups, claims and emergency revocation.",
        group: "Identity",
        icon: "key",
      },
      {
        id: "apis-webhooks",
        title: "APIs & Webhooks",
        description: "Scoped credentials, event subscriptions, schemas, replay, idempotency and health.",
        group: "Developer",
        icon: "code",
      },
      {
        id: "data-exports",
        title: "Data Exports",
        description: "Purpose-limited exports, approvals, masking, expiry and downloadable evidence.",
        group: "Data",
        icon: "download",
      },
      {
        id: "integration-health",
        title: "Integration Health",
        description: "Connection status, latency, failures, retries, last sync and named owners.",
        group: "Operations",
        icon: "pulse",
      },
      {
        id: "data-dictionary",
        title: "Data Dictionary & Lineage",
        description: "Definitions, ownership, sensitivity, source, transformations and destination lineage.",
        group: "Data",
        icon: "database",
      },
    ],
  },
  {
    id: "settings",
    label: "Institution Settings",
    shortLabel: "Settings",
    icon: "settings",
    accent: "purple",
    routes: [
      {
        id: "settings-profile",
        title: "Institution Profile & Branding",
        description: "Names, logos, colours, domains, contacts and public identity.",
        group: "Settings",
        icon: "building",
      },
      {
        id: "settings-structure",
        title: "Campuses & Academic Structure",
        description: "Default structure, codes, naming and delegated administration.",
        group: "Settings",
        icon: "network",
      },
      {
        id: "settings-language",
        title: "Language & Accessibility",
        description: "Primary and secondary languages, translation, captions and accessibility defaults.",
        group: "Settings",
        icon: "globe",
      },
      {
        id: "settings-academic",
        title: "Academic Rules",
        description: "Terms, credit values, grading scales, progression rules and award classifications.",
        group: "Settings",
        icon: "sliders",
      },
      {
        id: "settings-notifications",
        title: "Notifications",
        description: "Institution, role, risk, class, Labs, assessment and report notification preferences.",
        group: "Settings",
        icon: "bell",
      },
      {
        id: "settings-security",
        title: "Security & Sessions",
        description: "Session policy, MFA, allowed domains, high-risk actions and emergency access.",
        group: "Settings",
        icon: "lock",
      },
      {
        id: "settings-data",
        title: "Data Regions & Retention",
        description: "Data-region requirements, record classes, retention and deletion defaults.",
        group: "Settings",
        icon: "database",
      },
      {
        id: "settings-connected-platforms",
        title: "Connected Platform Policies",
        description: "Class, Labs and Build Room handoff, visibility, consent and evidence rules.",
        group: "Settings",
        icon: "link",
      },
      {
        id: "settings-credentials",
        title: "Credential & Verification Policies",
        description: "Issuer authority, evidence, approvals, signatures, revocation and verification.",
        group: "Settings",
        icon: "certificate",
      },
    ],
  },
];
export const APP_DATA = {
  people: [
    {
      name: "Amina Nanyonga",
      id: "STU-20481",
      role: "Student",
      unit: "School of Computing",
      program: "BSc Applied AI",
      status: "Active",
      risk: "On track",
      presence: "online",
      email: "amina.n@institution.edu",
    },
    {
      name: "Daniel Okello",
      id: "STU-20496",
      role: "Student",
      unit: "School of Business",
      program: "Diploma in AI for Business",
      status: "Active",
      risk: "Needs attention",
      presence: "away",
      email: "daniel.o@institution.edu",
    },
    {
      name: "Grace Namusoke",
      id: "INS-0042",
      role: "Instructor",
      unit: "Department of Data & AI",
      program: "3 teaching assignments",
      status: "Active",
      risk: "Healthy workload",
      presence: "online",
      email: "grace.n@institution.edu",
    },
    {
      name: "Michael Chen",
      id: "INS-0057",
      role: "Instructor",
      unit: "School of Engineering",
      program: "2 teaching assignments",
      status: "Active",
      risk: "Review load high",
      presence: "busy",
      email: "m.chen@institution.edu",
    },
    {
      name: "Sarah Atim",
      id: "ADV-0018",
      role: "Academic Advisor",
      unit: "Student Success",
      program: "42 advisees",
      status: "Active",
      risk: "3 urgent cases",
      presence: "online",
      email: "sarah.atim@institution.edu",
    },
    {
      name: "Dr. Ibrahim Musa",
      id: "HOD-0011",
      role: "Department Head",
      unit: "Department of Data & AI",
      program: "Academic leadership",
      status: "Active",
      risk: "On track",
      presence: "online",
      email: "i.musa@institution.edu",
    },
    {
      name: "Lydia Nsubuga",
      id: "REG-0007",
      role: "Registrar",
      unit: "Academic Registry",
      program: "Institution records",
      status: "Active",
      risk: "2 approvals due",
      presence: "away",
      email: "lydia.n@institution.edu",
    },
    {
      name: "Prof. Eleanor Wright",
      id: "EXT-0012",
      role: "External Examiner",
      unit: "External",
      program: "BSc Applied AI",
      status: "Time-limited",
      risk: "Expires in 12 days",
      presence: "offline",
      email: "e.wright@external.edu",
    },
    {
      name: "Peter Mugisha",
      id: "STU-20513",
      role: "Student",
      unit: "School of Computing",
      program: "BSc Applied AI",
      status: "Active",
      risk: "At risk",
      presence: "offline",
      email: "peter.m@institution.edu",
    },
    {
      name: "Fatima Al-Hassan",
      id: "STU-20522",
      role: "Student",
      unit: "School of Engineering",
      program: "MSc Intelligent Systems",
      status: "Active",
      risk: "On track",
      presence: "online",
      email: "fatima.a@institution.edu",
    },
    {
      name: "Joseph Kato",
      id: "LIB-0009",
      role: "Librarian",
      unit: "University Library",
      program: "Academic resources",
      status: "Active",
      risk: "On track",
      presence: "online",
      email: "j.kato@institution.edu",
    },
    {
      name: "Rita Achieng",
      id: "ACC-0003",
      role: "Accessibility Officer",
      unit: "Student Success",
      program: "38 active plans",
      status: "Active",
      risk: "4 reviews due",
      presence: "online",
      email: "rita.a@institution.edu",
    },
  ],
  programs: [
    {
      name: "BSc Applied Artificial Intelligence",
      code: "BAAI",
      school: "School of Computing",
      level: "Undergraduate",
      students: 486,
      progress: 72,
      status: "Active",
      quality: "Review due Oct 2026",
    },
    {
      name: "MSc Intelligent Systems",
      code: "MSIS",
      school: "School of Engineering",
      level: "Postgraduate",
      students: 128,
      progress: 64,
      status: "Active",
      quality: "Healthy",
    },
    {
      name: "Diploma in AI for Business",
      code: "DAIB",
      school: "School of Business",
      level: "Diploma",
      students: 242,
      progress: 81,
      status: "Active",
      quality: "Healthy",
    },
    {
      name: "Certificate in Responsible AI",
      code: "CRAI",
      school: "Institute for Digital Trust",
      level: "Professional",
      students: 319,
      progress: 76,
      status: "Active",
      quality: "Accreditation evidence 88%",
    },
    {
      name: "AI Practical Skills Foundation",
      code: "AIPSF",
      school: "Continuing Education",
      level: "Short course",
      students: 614,
      progress: 69,
      status: "Active",
      quality: "Curriculum refresh proposed",
    },
    {
      name: "MEd AI-Enabled Teaching",
      code: "MAET",
      school: "Faculty of Education",
      level: "Postgraduate",
      students: 94,
      progress: 58,
      status: "Active",
      quality: "External review scheduled",
    },
  ],
  sessions: [
    {
      id: "CLS-2026-091",
      title: "Responsible AI: Human Authority in Practice",
      code: "CA-RAI-7K4P",
      date: "25 Aug 2026",
      time: "10:00 EAT",
      cohort: "CRAI — Aug 2026",
      instructor: "Grace Namusoke",
      status: "Ready",
      attendance: "86 expected",
      language: "English + Luganda",
    },
    {
      id: "CLS-2026-094",
      title: "Building Reliable Agent Workflows",
      code: "CA-AWF-9M2Q",
      date: "25 Aug 2026",
      time: "14:00 EAT",
      cohort: "BAAI Year 3",
      instructor: "Michael Chen",
      status: "Ready",
      attendance: "62 expected",
      language: "English",
    },
    {
      id: "CLS-2026-097",
      title: "AI for Business Decision Design",
      code: "CA-BIZ-4T8N",
      date: "26 Aug 2026",
      time: "09:00 EAT",
      cohort: "DAIB — Cohort 12",
      instructor: "Nadia Birungi",
      status: "Draft",
      attendance: "48 expected",
      language: "English + Swahili",
    },
    {
      id: "CLS-2026-102",
      title: "Research Methods Clinic",
      code: "CA-RMC-6H3D",
      date: "27 Aug 2026",
      time: "16:00 EAT",
      cohort: "MSIS Research",
      instructor: "Dr. Ibrahim Musa",
      status: "Ready",
      attendance: "31 expected",
      language: "English",
    },
  ],
  labs: [
    {
      id: "lab-ai-app-builder",
      name: "AI-App Builder Lab",
      slug: "ai-app-builder",
      program: "BSc Applied AI",
      eligible: 486,
      active: 72,
      completion: 74,
      evidence: 86,
      safety: "Healthy",
      entitlement: "AAB-INSTITUTION-2026",
    },
    {
      id: "lab-agentic",
      name: "Agentic AI Lab",
      slug: "agentic-ai",
      program: "MSc Intelligent Systems",
      eligible: 128,
      active: 34,
      completion: 68,
      evidence: 79,
      safety: "Healthy",
      entitlement: "AGT-MSCI-2026",
    },
    {
      id: "lab-data",
      name: "Data & Predictive AI Lab",
      slug: "data-predictive-ai",
      program: "BSc Applied AI",
      eligible: 312,
      active: 58,
      completion: 81,
      evidence: 91,
      safety: "Healthy",
      entitlement: "DPA-BAAI-2026",
    },
    {
      id: "lab-conversational",
      name: "Conversational AI Lab",
      slug: "conversational-ai",
      program: "Diploma in AI for Business",
      eligible: 242,
      active: 47,
      completion: 77,
      evidence: 83,
      safety: "Healthy",
      entitlement: "CAI-DAIB-2026",
    },
    {
      id: "lab-science",
      name: "AI Science Simulation Lab",
      slug: "ai-science-simulation",
      program: "School of Engineering",
      eligible: 156,
      active: 19,
      completion: 61,
      evidence: 72,
      safety: "Review scheduled",
      entitlement: "SCI-ENG-2026",
    },
    {
      id: "lab-graphics",
      name: "Graphics, 3D & CAD AI Lab",
      slug: "graphics-3d-cad",
      program: "Design & Engineering",
      eligible: 98,
      active: 23,
      completion: 70,
      evidence: 76,
      safety: "Healthy",
      entitlement: "G3D-2026",
    },
    {
      id: "lab-deep-learning",
      name: "Deep Learning Lab",
      slug: "deep-learning",
      program: "MSc Intelligent Systems",
      eligible: 94,
      active: 28,
      completion: 66,
      evidence: 74,
      safety: "Healthy",
      entitlement: "DL-MSIS-2026",
    },
    {
      id: "lab-space",
      name: "Space Systems & Mission AI Lab",
      slug: "space-systems-mission-ai",
      program: "Research & Innovation",
      eligible: 42,
      active: 11,
      completion: 54,
      evidence: 63,
      safety: "Restricted compute",
      entitlement: "SPACE-RI-2026",
    },
  ],
  groups: [
    {
      id: "grp-access",
      name: "Accessibility & Experience",
      focus: "Inclusive experience, language and assistive technology",
      members: 6,
      lead: "Amina Nanyonga",
      instructor: "Grace Namusoke",
      progress: 78,
      evidence: 82,
      branch: "group/accessibility-experience",
      huddle: "CA-GRP-A11Y",
      lab: "AI-App Builder Lab",
      status: "On track",
    },
    {
      id: "grp-trust",
      name: "Trust, Safety & Consent",
      focus: "Consent, privacy, safeguards and human authority",
      members: 6,
      lead: "Daniel Okello",
      instructor: "Grace Namusoke",
      progress: 71,
      evidence: 76,
      branch: "group/trust-safety",
      huddle: "CA-GRP-TRST",
      lab: "Responsible AI workspace",
      status: "Review due",
    },
    {
      id: "grp-api",
      name: "API & Data Boundaries",
      focus: "Data contracts, integration scope and resilience",
      members: 5,
      lead: "Fatima Al-Hassan",
      instructor: "Michael Chen",
      progress: 68,
      evidence: 73,
      branch: "group/api-data",
      huddle: "CA-GRP-API2",
      lab: "Agentic AI Lab",
      status: "On track",
    },
    {
      id: "grp-evaluation",
      name: "Evaluation & Evidence",
      focus: "Test design, evaluation records and credential evidence",
      members: 6,
      lead: "Peter Mugisha",
      instructor: "Michael Chen",
      progress: 59,
      evidence: 64,
      branch: "group/evaluation-evidence",
      huddle: "CA-GRP-EVAL",
      lab: "Data & Predictive AI Lab",
      status: "Needs support",
    },
    {
      id: "grp-agents",
      name: "Automation & Agent Boundaries",
      focus: "Agent permissions, stop controls and approval gates",
      members: 5,
      lead: "Sarah Auma",
      instructor: "Grace Namusoke",
      progress: 75,
      evidence: 79,
      branch: "group/agent-boundaries",
      huddle: "CA-GRP-AGNT",
      lab: "Agentic AI Lab",
      status: "On track",
    },
    {
      id: "grp-demo",
      name: "Demo, Story & Handoff",
      focus: "Live preview, demonstration narrative and operational handoff",
      members: 6,
      lead: "Brian Ouma",
      instructor: "Nadia Birungi",
      progress: 83,
      evidence: 88,
      branch: "group/demo-handoff",
      huddle: "CA-GRP-DEMO",
      lab: "AI-App Builder Lab",
      status: "Ahead",
    },
  ],
  assessments: [
    {
      course: "RAI 304",
      assessment: "Responsible AI System Review",
      cohort: "BAAI Year 3",
      submitted: "76 / 84",
      graded: "63",
      moderation: "12 sampled",
      publish: "Blocked by 2 decisions",
      status: "Attention",
    },
    {
      course: "AIS 502",
      assessment: "Agent Workflow Capstone",
      cohort: "MSIS Year 1",
      submitted: "31 / 34",
      graded: "22",
      moderation: "6 sampled",
      publish: "30 Aug 2026",
      status: "On track",
    },
    {
      course: "AIB 210",
      assessment: "Business AI Decision Brief",
      cohort: "DAIB Cohort 12",
      submitted: "46 / 48",
      graded: "41",
      moderation: "5 sampled",
      publish: "27 Aug 2026",
      status: "On track",
    },
    {
      course: "DPA 220",
      assessment: "Predictive Model Evaluation",
      cohort: "BAAI Year 2",
      submitted: "91 / 96",
      graded: "88",
      moderation: "10 sampled",
      publish: "Ready",
      status: "Ready",
    },
  ],
  accreditations: [
    {
      body: "National Council for Higher Education",
      scope: "BSc Applied Artificial Intelligence",
      status: "Evidence collection",
      progress: 88,
      next: "Evidence review — 18 Sep 2026",
      owner: "Quality Directorate",
    },
    {
      body: "Professional AI Standards Board",
      scope: "Certificate in Responsible AI",
      status: "Site visit scheduled",
      progress: 92,
      next: "Visit — 03 Oct 2026",
      owner: "Institute for Digital Trust",
    },
    {
      body: "Engineering Accreditation Council",
      scope: "MSc Intelligent Systems",
      status: "Self-study",
      progress: 64,
      next: "Draft due — 30 Sep 2026",
      owner: "School of Engineering",
    },
    {
      body: "Institutional Quality Review",
      scope: "Whole institution",
      status: "Annual cycle",
      progress: 73,
      next: "Senate report — 12 Nov 2026",
      owner: "Academic Quality Office",
    },
  ],
  integrations: [
    {
      name: "Chatoner Class",
      type: "Live learning",
      domain: "class.chatoner.com",
      status: "Connected",
      health: "99.98%",
      scope: "Sessions, attendance summaries, recordings metadata",
      owner: "Digital Learning",
    },
    {
      name: "Chatoner AI Labs",
      type: "Practical learning",
      domain: "labs.chatoner.com",
      status: "Connected",
      health: "99.95%",
      scope: "Entitlements, aggregate runs, consented evidence",
      owner: "Digital Learning",
    },
    {
      name: "Student Information System",
      type: "SIS",
      domain: "Institution managed",
      status: "Connected",
      health: "99.92%",
      scope: "Students, programs, enrolments, results",
      owner: "Academic Registry",
    },
    {
      name: "Learning Management System",
      type: "LMS",
      domain: "Institution managed",
      status: "Connected",
      health: "99.89%",
      scope: "Courses, rosters, resources, selected grades",
      owner: "Digital Learning",
    },
    {
      name: "Identity Provider",
      type: "SSO / SCIM",
      domain: "Institution managed",
      status: "Connected",
      health: "100%",
      scope: "Identity, groups, lifecycle, MFA claims",
      owner: "Information Security",
    },
    {
      name: "Library Platform",
      type: "Library",
      domain: "Institution managed",
      status: "Attention",
      health: "97.2%",
      scope: "Resource access and licences",
      owner: "University Library",
    },
    {
      name: "Finance System",
      type: "Finance",
      domain: "Institution managed",
      status: "Connected",
      health: "99.74%",
      scope: "Budgets, cost centres, purchase orders",
      owner: "Finance",
    },
    {
      name: "Research Repository",
      type: "Repository",
      domain: "Institution managed",
      status: "Connected",
      health: "99.61%",
      scope: "Approved outputs and metadata",
      owner: "Research Office",
    },
  ],
  notifications: [
    {
      id: "n1",
      title: "Moderation decision required",
      body: "Two grade disagreements require an authorized assessment-board decision.",
      time: "9 min ago",
      area: "assessment",
      page: "moderation",
      unread: true,
    },
    {
      id: "n2",
      title: "Labs entitlement capacity at 86%",
      body: "AI-App Builder Lab is approaching the sponsored monthly capacity.",
      time: "26 min ago",
      area: "labs",
      page: "lab-entitlements",
      unread: true,
    },
    {
      id: "n3",
      title: "Student support escalation",
      body: "A consented high-priority support case needs a named owner before 12:00 EAT.",
      time: "41 min ago",
      area: "success",
      page: "risk-interventions",
      unread: true,
    },
    {
      id: "n4",
      title: "Accreditation evidence updated",
      body: "The BSc Applied AI evidence set is now 88% complete.",
      time: "1 h ago",
      area: "quality",
      page: "accreditation",
      unread: false,
    },
    {
      id: "n5",
      title: "Chatoner Class schedule changed",
      body: "The Research Methods Clinic moved to 16:00 EAT on 27 August.",
      time: "2 h ago",
      area: "class",
      page: "class-schedule",
      unread: false,
    },
  ],
};
export const LANGUAGES = LANGUAGE_OPTIONS.map(({ value, code }) => [value, code] as const);

export const PAGE_SPECS: Record<string, WorkspaceSpec> = {
  "command-overview": {
    id: "command-overview",
    area: "command",
    areaLabel: "Institution Command",
    title: "Executive Overview",
    description:
      "A unified view of academic delivery, student success, institutional risk, capacity, quality and connected learning platforms.",
    group: "Institution Command",
    icon: "home",
    layout: "dashboard",
    primaryAction: "Create institutional item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active learners",
        value: "89",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Programs on track",
        value: "61",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Priority decisions",
        value: "30",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Platform health",
        value: "92%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COMM-100",
        name: "Student progression",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 61,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "162",
        evidence: "92%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COM-2026",
      },
      {
        id: "COMM-101",
        name: "Teaching capacity",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 72,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "179",
        evidence: "65%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COM-2027",
      },
      {
        id: "COMM-102",
        name: "Accreditation readiness",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 83,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "196",
        evidence: "72%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COM-2028",
      },
      {
        id: "COMM-103",
        name: "Connected-platform health",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 94,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "213",
        evidence: "79%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COM-2029",
      },
      {
        id: "COMM-104",
        name: "Financial sustainability",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 60,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "230",
        evidence: "86%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COM-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Executive Overview actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution command and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [73, 70, 69, 79, 82, 61, 77, 92],
    external: null,
    signature: "command::command-overview::dashboard::5",
  },
  "action-centre": {
    id: "action-centre",
    area: "command",
    areaLabel: "Institution Command",
    title: "Priority Action Centre",
    description: "Approvals, risks, exceptions and time-sensitive institutional work requiring named owners.",
    group: "Institution Command",
    icon: "bolt",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "63",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "71",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "7",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "70%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACTI-100",
        name: "Moderation disagreement",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Urgent",
        progress: 59,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "280",
        evidence: "66%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACT-2026",
      },
      {
        id: "ACTI-101",
        name: "Safeguarding escalation",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Review due",
        progress: 70,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "297",
        evidence: "73%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACT-2027",
      },
      {
        id: "ACTI-102",
        name: "Curriculum proposal",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 81,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "314",
        evidence: "80%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACT-2028",
      },
      {
        id: "ACTI-103",
        name: "Labs capacity exception",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 92,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "331",
        evidence: "87%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACT-2029",
      },
      {
        id: "ACTI-104",
        name: "External examiner access",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 58,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "348",
        evidence: "94%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACT-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Priority Action Centre actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution command and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [65, 78, 71, 80, 61, 56, 76, 70],
    external: null,
    signature: "command::action-centre::queue::5",
  },
  "institution-health": {
    id: "institution-health",
    area: "command",
    areaLabel: "Institution Command",
    title: "Institutional Health",
    description: "Monitor academic, operational, financial, quality and learner-success indicators together.",
    group: "Institution Command",
    icon: "pulse",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "93%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "76%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "24",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "86",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "INST-100",
        name: "Academic delivery",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Review due",
        progress: 93,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "344",
        evidence: "84%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "INS-2026",
      },
      {
        id: "INST-101",
        name: "Student success",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Ready",
        progress: 59,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "361",
        evidence: "91%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "INS-2027",
      },
      {
        id: "INST-102",
        name: "Quality assurance",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 70,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "378",
        evidence: "64%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "INS-2028",
      },
      {
        id: "INST-103",
        name: "Financial stewardship",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 81,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "395",
        evidence: "71%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "INS-2029",
      },
      {
        id: "INST-104",
        name: "Digital learning services",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Active",
        progress: 92,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "412",
        evidence: "78%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "INS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Institutional Health actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution command and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [87, 73, 70, 79, 92, 85, 62, 77],
    external: null,
    signature: "command::institution-health::scorecard::5",
  },
  "academic-calendar": {
    id: "academic-calendar",
    area: "command",
    areaLabel: "Institution Command",
    title: "Academic Calendar",
    description:
      "Terms, intakes, examinations, live classes, Labs windows, review panels and institution milestones.",
    group: "Institution Command",
    icon: "calendar",
    layout: "calendar",
    primaryAction: "Add calendar item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Scheduled items",
        value: "54",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "This week",
        value: "59",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Conflicts",
        value: "82",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Confirmed",
        value: "37",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACAD-100",
        name: "Semester 1 teaching",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "On track",
        progress: 95,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "271",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACA-2026",
      },
      {
        id: "ACAD-101",
        name: "Mid-semester assessment",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 61,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "288",
        evidence: "90%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACA-2027",
      },
      {
        id: "ACAD-102",
        name: "Research ethics panel",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 72,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "305",
        evidence: "97%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACA-2028",
      },
      {
        id: "ACAD-103",
        name: "Graduation board",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 83,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "322",
        evidence: "70%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACA-2029",
      },
      {
        id: "ACAD-104",
        name: "Institution closure",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Draft",
        progress: 94,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "339",
        evidence: "77%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Academic Calendar actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution command and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [56, 55, 55, 86, 62, 88, 63, 78],
    external: null,
    signature: "command::academic-calendar::calendar::5",
  },
  approvals: {
    id: "approvals",
    area: "command",
    areaLabel: "Institution Command",
    title: "Approvals & Decisions",
    description:
      "Curriculum, assessment, access, policy, funding and connected-platform approvals with audit history.",
    group: "Institution Command",
    icon: "check",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "96",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "55",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "47",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "72%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "APPR-100",
        name: "BAAI curriculum revision",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Urgent",
        progress: 78,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "119",
        evidence: "77%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "APP-2026",
      },
      {
        id: "APPR-101",
        name: "External examiner appointment",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 89,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "136",
        evidence: "84%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "APP-2027",
      },
      {
        id: "APPR-102",
        name: "Scholarship exception",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 55,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "153",
        evidence: "91%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "APP-2028",
      },
      {
        id: "APPR-103",
        name: "High-risk Labs entitlement",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 66,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "170",
        evidence: "64%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "APP-2029",
      },
      {
        id: "APPR-104",
        name: "Assessment result publication",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 77,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "187",
        evidence: "71%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "APP-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Approvals & Decisions actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution command and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [72, 80, 71, 90, 95, 65, 57, 66],
    external: null,
    signature: "command::approvals::queue::5",
  },
  "institution-profile": {
    id: "institution-profile",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Institution Profile",
    description:
      "Institution identity, accreditation references, operating model, contacts and public academic information.",
    group: "Institution Structure",
    icon: "building",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "—",
        trend: "Not reported",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "—",
        trend: "Not reported",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "—",
        trend: "Not reported",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "—",
        trend: "Not reported",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Institution Profile actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution structure and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Institution name",
      "Institution code",
      "Legal name",
      "Primary domain",
      "Time zone",
      "Primary language",
      "Accreditation reference",
      "Principal contact",
      "Approved change reason",
    ],
    chart: [],
    external: null,
    signature: "structure::institution-profile::form::5",
  },
  campuses: {
    id: "campuses",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Campuses & Centres",
    description:
      "Manage campuses, satellite centres, learning hubs, time zones, accessibility and local contacts.",
    group: "Institution Structure",
    icon: "map",
    layout: "hierarchy",
    primaryAction: "Add structure item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active entities",
        value: "—",
        trend: "Not reported",
        icon: "pulse",
      },
      {
        label: "Delegated leads",
        value: "—",
        trend: "Not reported",
        icon: "users",
      },
      {
        label: "Programs linked",
        value: "—",
        trend: "Not reported",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "—",
        trend: "Not reported",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Entity",
      },
      {
        key: "owner",
        label: "Leader",
      },
      {
        key: "people",
        label: "People",
      },
      {
        key: "progress",
        label: "Readiness",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Campuses & Centres actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution structure and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [],
    external: null,
    signature: "structure::campuses::hierarchy::5",
  },
  "schools-faculties": {
    id: "schools-faculties",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Schools & Faculties",
    description:
      "Academic schools and faculties with leadership, programs, quality owners and reporting lines.",
    group: "Institution Structure",
    icon: "layers",
    layout: "hierarchy",
    primaryAction: "Add structure item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active entities",
        value: "—",
        trend: "Not reported",
        icon: "pulse",
      },
      {
        label: "Delegated leads",
        value: "—",
        trend: "Not reported",
        icon: "users",
      },
      {
        label: "Programs linked",
        value: "—",
        trend: "Not reported",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "—",
        trend: "Not reported",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Entity",
      },
      {
        key: "owner",
        label: "Leader",
      },
      {
        key: "people",
        label: "People",
      },
      {
        key: "progress",
        label: "Readiness",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Schools & Faculties actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution structure and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [],
    external: null,
    signature: "structure::schools-faculties::hierarchy::5",
  },
  departments: {
    id: "departments",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Departments",
    description: "Departments, subject areas, academic leads, staff capacity and program relationships.",
    group: "Institution Structure",
    icon: "network",
    layout: "hierarchy",
    primaryAction: "Add structure item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active entities",
        value: "—",
        trend: "Not reported",
        icon: "pulse",
      },
      {
        label: "Delegated leads",
        value: "—",
        trend: "Not reported",
        icon: "users",
      },
      {
        label: "Programs linked",
        value: "—",
        trend: "Not reported",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "—",
        trend: "Not reported",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Entity",
      },
      {
        key: "owner",
        label: "Leader",
      },
      {
        key: "people",
        label: "People",
      },
      {
        key: "progress",
        label: "Readiness",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Departments actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution structure and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [],
    external: null,
    signature: "structure::departments::hierarchy::5",
  },
  "academic-units": {
    id: "academic-units",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Academic Units",
    description: "Institutes, centres, research units, libraries and specialist teaching entities.",
    group: "Institution Structure",
    icon: "grid",
    layout: "hierarchy",
    primaryAction: "Add structure item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active entities",
        value: "—",
        trend: "Not reported",
        icon: "pulse",
      },
      {
        label: "Delegated leads",
        value: "—",
        trend: "Not reported",
        icon: "users",
      },
      {
        label: "Programs linked",
        value: "—",
        trend: "Not reported",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "—",
        trend: "Not reported",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Entity",
      },
      {
        key: "owner",
        label: "Leader",
      },
      {
        key: "people",
        label: "People",
      },
      {
        key: "progress",
        label: "Readiness",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Academic Units actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution structure and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [],
    external: null,
    signature: "structure::academic-units::hierarchy::5",
  },
  committees: {
    id: "committees",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Committees & Boards",
    description: "Senate, academic boards, assessment boards, ethics committees and quality committees.",
    group: "Governance Structure",
    icon: "users",
    layout: "hierarchy",
    primaryAction: "Add structure item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active entities",
        value: "44",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Delegated leads",
        value: "76",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Programs linked",
        value: "31",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "10",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Entity",
      },
      {
        key: "owner",
        label: "Leader",
      },
      {
        key: "people",
        label: "People",
      },
      {
        key: "progress",
        label: "Readiness",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COMM-100",
        name: "University Senate",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Ready",
        progress: 58,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "279",
        evidence: "69%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COM-2026",
      },
      {
        id: "COMM-101",
        name: "Academic Board",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 69,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "296",
        evidence: "76%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COM-2027",
      },
      {
        id: "COMM-102",
        name: "Assessment Board",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 80,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "313",
        evidence: "83%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COM-2028",
      },
      {
        id: "COMM-103",
        name: "Research Ethics Committee",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 91,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "330",
        evidence: "90%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COM-2029",
      },
      {
        id: "COMM-104",
        name: "Quality & Accreditation Committee",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 57,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "347",
        evidence: "97%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COM-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Committees & Boards actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance structure and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [64, 57, 66, 89, 95, 75, 70, 58],
    external: null,
    signature: "structure::committees::hierarchy::5",
  },
  "terms-calendars": {
    id: "terms-calendars",
    area: "structure",
    areaLabel: "Academic Structure",
    title: "Terms & Academic Years",
    description: "Academic years, semesters, terms, teaching weeks, examination periods and closure dates.",
    group: "Academic Calendar",
    icon: "calendar",
    layout: "calendar",
    primaryAction: "Add calendar item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Scheduled items",
        value: "90",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "This week",
        value: "14",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Conflicts",
        value: "21",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Confirmed",
        value: "21",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "TERM-100",
        name: "Academic Year 2026/27",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Ready",
        progress: 79,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "75",
        evidence: "73%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "TER-2026",
      },
      {
        id: "TERM-101",
        name: "Semester One",
        owner: "University Library",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 90,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "92",
        evidence: "80%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "TER-2027",
      },
      {
        id: "TERM-102",
        name: "Semester Two",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 56,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "109",
        evidence: "87%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "TER-2028",
      },
      {
        id: "TERM-103",
        name: "Examination Period",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 67,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "126",
        evidence: "94%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "TER-2029",
      },
      {
        id: "TERM-104",
        name: "Graduation and Recess",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "On track",
        progress: 78,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "143",
        evidence: "67%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "TER-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Terms & Academic Years actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for academic calendar and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [70, 90, 74, 70, 69, 90, 63, 57],
    external: null,
    signature: "structure::terms-calendars::calendar::5",
  },
  "people-directory": {
    id: "people-directory",
    area: "people",
    areaLabel: "People & Access",
    title: "Institution Directory",
    description:
      "Students, instructors, academic leaders, advisors, administrators and authorized external reviewers.",
    group: "People",
    icon: "users",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "29",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "86",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "8",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "38",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PEOP-100",
        name: "Amina Nanyonga",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 91,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "192",
        evidence: "64%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PEO-2026",
      },
      {
        id: "PEOP-101",
        name: "Daniel Okello",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 57,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "209",
        evidence: "71%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PEO-2027",
      },
      {
        id: "PEOP-102",
        name: "Grace Namusoke",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 68,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "226",
        evidence: "78%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PEO-2028",
      },
      {
        id: "PEOP-103",
        name: "Michael Chen",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 79,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "243",
        evidence: "85%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PEO-2029",
      },
      {
        id: "PEOP-104",
        name: "Sarah Atim",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 90,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "260",
        evidence: "92%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PEO-2030",
      },
      {
        id: "PEOP-105",
        name: "Dr. Ibrahim Musa",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "Draft",
        progress: 56,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "277",
        evidence: "65%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PEO-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Institution Directory actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for people and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [61, 56, 86, 83, 62, 67, 68, 89],
    external: null,
    signature: "people::people-directory::directory::6",
  },
  students: {
    id: "students",
    area: "people",
    areaLabel: "People & Access",
    title: "Students & Learners",
    description:
      "Enrolment, program status, attendance, progress, support, accommodations and credential readiness.",
    group: "People",
    icon: "student",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "53",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "23",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "16",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "68",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "STUD-100",
        name: "Amina Nanyonga",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 89,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "250",
        evidence: "80%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "STU-2026",
      },
      {
        id: "STUD-101",
        name: "Daniel Okello",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Draft",
        progress: 55,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "267",
        evidence: "87%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "STU-2027",
      },
      {
        id: "STUD-102",
        name: "Peter Mugisha",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 66,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "284",
        evidence: "94%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "STU-2028",
      },
      {
        id: "STUD-103",
        name: "Fatima Al-Hassan",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 77,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "301",
        evidence: "67%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "STU-2029",
      },
      {
        id: "STUD-104",
        name: "Sarah Auma",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Review due",
        progress: 88,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "318",
        evidence: "74%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "STU-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Students & Learners actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for people and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [77, 92, 95, 65, 68, 68, 58, 87],
    external: null,
    signature: "people::students::directory::5",
  },
  instructors: {
    id: "instructors",
    area: "people",
    areaLabel: "People & Access",
    title: "Instructors & Faculty",
    description:
      "Teaching assignments, workload, class access, assessment scope, Labs role and professional development.",
    group: "People",
    icon: "teacher",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "8",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "62",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "30",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "69",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "INST-100",
        name: "Grace Namusoke",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "Ready",
        progress: 70,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "291",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "INS-2026",
      },
      {
        id: "INST-101",
        name: "Michael Chen",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 81,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "308",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "INS-2027",
      },
      {
        id: "INST-102",
        name: "Nadia Birungi",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 92,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "325",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "INS-2028",
      },
      {
        id: "INST-103",
        name: "Dr. Ibrahim Musa",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 58,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "342",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "INS-2029",
      },
      {
        id: "INST-104",
        name: "Prof. Mercy Auma",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "On track",
        progress: 69,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "359",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "INS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Instructors & Faculty actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for people and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [76, 81, 72, 69, 90, 74, 91, 64],
    external: null,
    signature: "people::instructors::directory::5",
  },
  "advisors-support": {
    id: "advisors-support",
    area: "people",
    areaLabel: "People & Access",
    title: "Advisors & Support Staff",
    description: "Academic advisors, counsellors, accessibility teams, librarians and student-success staff.",
    group: "People",
    icon: "heart",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "95",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "79",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "78",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "37",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ADVI-100",
        name: "Sarah Atim",
        owner: "University Library",
        scope: "Whole institution",
        status: "Active",
        progress: 55,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "426",
        evidence: "84%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ADV-2026",
      },
      {
        id: "ADVI-101",
        name: "Rita Achieng",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "On track",
        progress: 66,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "23",
        evidence: "91%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ADV-2027",
      },
      {
        id: "ADVI-102",
        name: "Joseph Kato",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 77,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "40",
        evidence: "64%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ADV-2028",
      },
      {
        id: "ADVI-103",
        name: "Mariam Kintu",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 88,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "57",
        evidence: "71%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ADV-2029",
      },
      {
        id: "ADVI-104",
        name: "David Ochieng",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 54,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "74",
        evidence: "78%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ADV-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Advisors & Support Staff actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for people and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [85, 94, 75, 60, 56, 55, 55, 76],
    external: null,
    signature: "people::advisors-support::directory::5",
  },
  "external-reviewers": {
    id: "external-reviewers",
    area: "people",
    areaLabel: "People & Access",
    title: "External Examiners & Reviewers",
    description: "Time-bound external access for moderation, accreditation, ethics and credential review.",
    group: "People",
    icon: "shield",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "36",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "75",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "25",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "10",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "EXTE-100",
        name: "Prof. Eleanor Wright",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "Ready",
        progress: 85,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "111",
        evidence: "97%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "EXT-2026",
      },
      {
        id: "EXTE-101",
        name: "Dr. James Mwangi",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 96,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "128",
        evidence: "70%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "EXT-2027",
      },
      {
        id: "EXTE-102",
        name: "Prof. Aisha Bello",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 62,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "145",
        evidence: "77%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "EXT-2028",
      },
      {
        id: "EXTE-103",
        name: "Dr. Samuel Kofi",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 73,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "162",
        evidence: "84%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "EXT-2029",
      },
      {
        id: "EXTE-104",
        name: "Ms. Lin Chen",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "On track",
        progress: 84,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "179",
        evidence: "91%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "EXT-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "External Examiners & Reviewers actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for people and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [64, 57, 55, 65, 89, 74, 70, 90],
    external: null,
    signature: "people::external-reviewers::directory::5",
  },
  invitations: {
    id: "invitations",
    area: "people",
    areaLabel: "People & Access",
    title: "Invitations & Onboarding",
    description:
      "Invite, import and onboard people with role-aware steps and connected-platform preparation.",
    group: "Access",
    icon: "mail",
    layout: "onboarding",
    primaryAction: "Start onboarding",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Invitations open",
        value: "5",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "In progress",
        value: "79%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Ready to activate",
        value: "9",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Blocked",
        value: "26",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Journey",
      },
      {
        key: "owner",
        label: "Sponsor / owner",
      },
      {
        key: "progress",
        label: "Completion",
      },
      {
        key: "decision",
        label: "Activation",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "INVI-100",
        name: "BAAI Year 1 student intake",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 61,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "282",
        evidence: "70%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "INV-2026",
      },
      {
        id: "INVI-101",
        name: "New instructor onboarding",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 72,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "299",
        evidence: "77%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "INV-2027",
      },
      {
        id: "INVI-102",
        name: "External examiner access",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 83,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "316",
        evidence: "84%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "INV-2028",
      },
      {
        id: "INVI-103",
        name: "Registrar delegate",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 94,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "333",
        evidence: "91%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "INV-2029",
      },
      {
        id: "INVI-104",
        name: "Accessibility reviewer",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 60,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "350",
        evidence: "64%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "INV-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Invitations & Onboarding actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for access and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [67, 79, 61, 88, 63, 88, 94, 96],
    external: null,
    signature: "people::invitations::onboarding::5",
  },
  "roles-permissions": {
    id: "roles-permissions",
    area: "people",
    areaLabel: "People & Access",
    title: "Roles & Permissions",
    description: "Institutional roles, scoped permissions, segregation of duties and least-privilege access.",
    group: "Access",
    icon: "lock",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "87%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "84",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "8",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "9",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ROLE-100",
        name: "Principal Administrator",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Review due",
        progress: 87,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "308",
        evidence: "94%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROL-2026",
      },
      {
        id: "ROLE-101",
        name: "Registrar",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Ready",
        progress: 53,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "325",
        evidence: "67%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROL-2027",
      },
      {
        id: "ROLE-102",
        name: "Dean",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 64,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "342",
        evidence: "74%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROL-2028",
      },
      {
        id: "ROLE-103",
        name: "Head of Department",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 75,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "359",
        evidence: "81%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROL-2029",
      },
      {
        id: "ROLE-104",
        name: "Quality & Accreditation",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Active",
        progress: 86,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "376",
        evidence: "88%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROL-2030",
      },
      {
        id: "ROLE-105",
        name: "Assessment Board",
        owner: "Research Office",
        scope: "Whole institution",
        status: "On track",
        progress: 52,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "393",
        evidence: "95%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROL-2031",
      },
      {
        id: "ROLE-106",
        name: "Student Success",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 63,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "410",
        evidence: "68%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROL-2032",
      },
      {
        id: "ROLE-107",
        name: "Research & Ethics",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 74,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "427",
        evidence: "75%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROL-2033",
      },
      {
        id: "ROLE-108",
        name: "Finance & Procurement",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 85,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "24",
        evidence: "82%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROL-2034",
      },
      {
        id: "ROLE-109",
        name: "Library Resources",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 96,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "41",
        evidence: "89%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROL-2035",
      },
      {
        id: "ROLE-110",
        name: "IT & Integration",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Active",
        progress: 62,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "58",
        evidence: "96%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROL-2036",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Roles & Permissions actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for access and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [93, 85, 94, 75, 60, 87, 63, 67],
    external: null,
    signature: "people::roles-permissions::matrix::11",
  },
  "access-reviews": {
    id: "access-reviews",
    area: "people",
    areaLabel: "People & Access",
    title: "Access Reviews",
    description: "Periodic reviews, inactive accounts, role changes, exceptions and immediate revocation.",
    group: "Access",
    icon: "eye",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "75%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "14",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "74",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "42",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACCE-100",
        name: "Registrar quarterly certification",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "Draft",
        progress: 75,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "251",
        evidence: "77%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACC-2026",
      },
      {
        id: "ACCE-101",
        name: "External examiner expiry review",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Active",
        progress: 86,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "268",
        evidence: "84%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACC-2027",
      },
      {
        id: "ACCE-102",
        name: "Department administrator scope",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 52,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "285",
        evidence: "91%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACC-2028",
      },
      {
        id: "ACCE-103",
        name: "Dormant student accounts",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 63,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "302",
        evidence: "64%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACC-2029",
      },
      {
        id: "ACCE-104",
        name: "Emergency-access attestation",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Ready",
        progress: 74,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "319",
        evidence: "71%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Access Reviews actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for access and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [78, 71, 90, 63, 78, 81, 82, 61],
    external: null,
    signature: "people::access-reviews::matrix::5",
  },
  "directory-imports": {
    id: "directory-imports",
    area: "people",
    areaLabel: "People & Access",
    title: "Directory Imports",
    description:
      "SIS, HRIS, CSV and API imports with mapping, validation, duplicate handling and audit evidence.",
    group: "Provisioning",
    icon: "upload",
    layout: "import",
    primaryAction: "Start import",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Rows staged",
        value: "86",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Validated",
        value: "25",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Duplicates",
        value: "22",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Errors",
        value: "10",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Import batch",
      },
      {
        key: "code",
        label: "Source",
      },
      {
        key: "people",
        label: "Rows",
      },
      {
        key: "progress",
        label: "Validated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "DIRE-100",
        name: "SIS student roster",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Draft",
        progress: 93,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "419",
        evidence: "85%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "DIR-2026",
      },
      {
        id: "DIRE-101",
        name: "HRIS instructor roster",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Active",
        progress: 59,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "436",
        evidence: "92%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "DIR-2027",
      },
      {
        id: "DIRE-102",
        name: "External reviewer CSV",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 70,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "33",
        evidence: "65%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "DIR-2028",
      },
      {
        id: "DIRE-103",
        name: "Faculty structure API",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 81,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "50",
        evidence: "72%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "DIR-2029",
      },
      {
        id: "DIRE-104",
        name: "Alumni data migration",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 92,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "67",
        evidence: "79%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "DIR-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Directory Imports actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for provisioning and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [78, 92, 95, 65, 68, 79, 82, 82],
    external: null,
    signature: "people::directory-imports::import::5",
  },
  "program-portfolio": {
    id: "program-portfolio",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Program Portfolio",
    description: "Degrees, diplomas, certificates, short courses and Academy-connected programs.",
    group: "Programs",
    icon: "book",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "51",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "17",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "96%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "21",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PROG-100",
        name: "BSc Applied Artificial Intelligence",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 59,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "130",
        evidence: "68%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PRO-2026",
      },
      {
        id: "PROG-101",
        name: "MSc Intelligent Systems",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Draft",
        progress: 70,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "147",
        evidence: "75%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PRO-2027",
      },
      {
        id: "PROG-102",
        name: "Diploma in AI for Business",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 81,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "164",
        evidence: "82%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PRO-2028",
      },
      {
        id: "PROG-103",
        name: "Certificate in Responsible AI",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 92,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "181",
        evidence: "89%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PRO-2029",
      },
      {
        id: "PROG-104",
        name: "AI Practical Skills Foundation",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Review due",
        progress: 58,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "198",
        evidence: "96%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PRO-2030",
      },
      {
        id: "PROG-105",
        name: "MEd AI-Enabled Teaching",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Ready",
        progress: 69,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "215",
        evidence: "69%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PRO-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Program Portfolio actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for programs and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [83, 62, 77, 81, 61, 56, 55, 86],
    external: null,
    signature: "academics::program-portfolio::portfolio::6",
  },
  "curriculum-maps": {
    id: "curriculum-maps",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Curriculum Maps",
    description:
      "Program outcomes, modules, courses, prerequisites, assessments, Labs and credential evidence.",
    group: "Curriculum",
    icon: "map",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "91%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "52",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "6",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "20",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CURR-100",
        name: "BAAI program outcomes",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 91,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "252",
        evidence: "68%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CUR-2026",
      },
      {
        id: "CURR-101",
        name: "MSIS research pathway",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 57,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "269",
        evidence: "75%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CUR-2027",
      },
      {
        id: "CURR-102",
        name: "DAIB business outcomes",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 68,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "286",
        evidence: "82%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CUR-2028",
      },
      {
        id: "CURR-103",
        name: "CRAI governance outcomes",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 79,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "303",
        evidence: "89%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CUR-2029",
      },
      {
        id: "CURR-104",
        name: "MAET teaching outcomes",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 90,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "320",
        evidence: "96%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CUR-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Curriculum Maps actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for curriculum and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [79, 92, 64, 57, 66, 68, 68, 68],
    external: null,
    signature: "academics::curriculum-maps::matrix::5",
  },
  "course-catalogue": {
    id: "course-catalogue",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Course Catalogue",
    description: "Course specifications, credits, delivery modes, owners, versions and availability.",
    group: "Curriculum",
    icon: "library",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "24",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "57",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "18",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "74",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COUR-100",
        name: "RAI 304 Responsible AI Systems",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "On track",
        progress: 86,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "277",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COU-2026",
      },
      {
        id: "COUR-101",
        name: "AIS 502 Agent Workflow Engineering",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Review due",
        progress: 52,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "294",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COU-2027",
      },
      {
        id: "COUR-102",
        name: "AIB 210 AI Business Decisions",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 63,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "311",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COU-2028",
      },
      {
        id: "COUR-103",
        name: "DPA 220 Predictive Model Evaluation",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 74,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "328",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COU-2029",
      },
      {
        id: "COUR-104",
        name: "LSE 112 AI Literacy",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "Draft",
        progress: 85,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "345",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COU-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Course Catalogue actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for curriculum and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [62, 77, 71, 69, 90, 84, 62, 67],
    external: null,
    signature: "academics::course-catalogue::table::5",
  },
  "cohorts-intakes": {
    id: "cohorts-intakes",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Cohorts & Intakes",
    description:
      "Create and monitor intakes, cohorts, capacity, instructors, schedules and student progression.",
    group: "Delivery",
    icon: "users",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "26",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "86",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "96%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "91",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COHO-100",
        name: "BAAI September 2026",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "Draft",
        progress: 60,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "161",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COH-2026",
      },
      {
        id: "COHO-101",
        name: "MSIS January 2027",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Active",
        progress: 71,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "178",
        evidence: "90%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COH-2027",
      },
      {
        id: "COHO-102",
        name: "DAIB Cohort 12",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 82,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "195",
        evidence: "97%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COH-2028",
      },
      {
        id: "COHO-103",
        name: "CRAI August 2026",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 93,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "212",
        evidence: "70%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COH-2029",
      },
      {
        id: "COHO-104",
        name: "AIPSF October 2026",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Ready",
        progress: 59,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "229",
        evidence: "77%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COH-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Cohorts & Intakes actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for delivery and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [72, 69, 58, 66, 68, 79, 61, 56],
    external: null,
    signature: "academics::cohorts-intakes::portfolio::5",
  },
  timetables: {
    id: "timetables",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Timetables",
    description: "Classes, rooms, instructors, online sessions, Labs windows and conflict detection.",
    group: "Delivery",
    icon: "calendar",
    layout: "timetable",
    primaryAction: "Add schedule item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Scheduled sessions",
        value: "82",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Rooms / channels",
        value: "84",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Conflicts",
        value: "72",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Coverage",
        value: "82%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "TIME-100",
        name: "BAAI Year 3 timetable",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Draft",
        progress: 63,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "239",
        evidence: "77%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "TIM-2026",
      },
      {
        id: "TIME-101",
        name: "MSIS research timetable",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Active",
        progress: 74,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "256",
        evidence: "84%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "TIM-2027",
      },
      {
        id: "TIME-102",
        name: "DAIB Cohort 12 timetable",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 85,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "273",
        evidence: "91%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "TIM-2028",
      },
      {
        id: "TIME-103",
        name: "Faculty office hours",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 96,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "290",
        evidence: "64%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "TIM-2029",
      },
      {
        id: "TIME-104",
        name: "Shared Labs schedule",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 62,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "307",
        evidence: "71%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "TIM-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Timetables actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for delivery and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [66, 68, 58, 55, 86, 73, 91, 85],
    external: null,
    signature: "academics::timetables::timetable::5",
  },
  "learning-pathways": {
    id: "learning-pathways",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Learning Pathways",
    description: "Core, elective, bridging, remedial, professional and multidisciplinary pathways.",
    group: "Curriculum",
    icon: "path",
    layout: "map",
    primaryAction: "Create mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Mapped outcomes",
        value: "29",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Pathways",
        value: "22",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "74",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Approval status",
        value: "48",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Path / equivalency",
      },
      {
        key: "scope",
        label: "Applies to",
      },
      {
        key: "evidence",
        label: "Coverage",
      },
      {
        key: "decision",
        label: "Decision",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LEAR-100",
        name: "BAAI core pathway",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 86,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "142",
        evidence: "86%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LEA-2026",
      },
      {
        id: "LEAR-101",
        name: "Responsible AI specialization",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Draft",
        progress: 52,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "159",
        evidence: "93%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LEA-2027",
      },
      {
        id: "LEAR-102",
        name: "AI Product Builder pathway",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 63,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "176",
        evidence: "66%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LEA-2028",
      },
      {
        id: "LEAR-103",
        name: "Data and Predictive pathway",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 74,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "193",
        evidence: "73%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LEA-2029",
      },
      {
        id: "LEAR-104",
        name: "Bridging mathematics route",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Review due",
        progress: 85,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "210",
        evidence: "80%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LEA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Learning Pathways actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for curriculum and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [95, 86, 94, 85, 62, 67, 68, 58],
    external: null,
    signature: "academics::learning-pathways::map::5",
  },
  "curriculum-approvals": {
    id: "curriculum-approvals",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Curriculum Approvals",
    description: "Proposal, review, senate approval, versioning, publication and retirement workflows.",
    group: "Governance",
    icon: "check",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "24",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "68",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "7",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "88%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CURR-100",
        name: "BAAI v4.2 revision",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "Urgent",
        progress: 65,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "181",
        evidence: "73%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CUR-2026",
      },
      {
        id: "CURR-101",
        name: "New AI Governance certificate",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 76,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "198",
        evidence: "80%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CUR-2027",
      },
      {
        id: "CURR-102",
        name: "DAIB assessment redesign",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 87,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "215",
        evidence: "87%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CUR-2028",
      },
      {
        id: "CURR-103",
        name: "MAET accessibility update",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 53,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "232",
        evidence: "94%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CUR-2029",
      },
      {
        id: "CURR-104",
        name: "Retirement of legacy module",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 64,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "249",
        evidence: "67%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CUR-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Curriculum Approvals actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [92, 74, 80, 61, 67, 89, 63, 67],
    external: null,
    signature: "academics::curriculum-approvals::queue::5",
  },
  "course-equivalencies": {
    id: "course-equivalencies",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Equivalencies & Credit Transfer",
    description: "Course matching, recognition of prior learning, exemptions and credit-transfer decisions.",
    group: "Records",
    icon: "swap",
    layout: "map",
    primaryAction: "Create mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Mapped outcomes",
        value: "61",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Pathways",
        value: "24",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "21",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Approval status",
        value: "21",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Path / equivalency",
      },
      {
        key: "scope",
        label: "Applies to",
      },
      {
        key: "evidence",
        label: "Coverage",
      },
      {
        key: "decision",
        label: "Decision",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COUR-100",
        name: "External AI Ethics course",
        owner: "University Library",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 65,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "166",
        evidence: "70%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COU-2026",
      },
      {
        id: "COUR-101",
        name: "Prior data-science diploma",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "Draft",
        progress: 76,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "183",
        evidence: "77%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COU-2027",
      },
      {
        id: "COUR-102",
        name: "Industry certification recognition",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 87,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "200",
        evidence: "84%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COU-2028",
      },
      {
        id: "COUR-103",
        name: "Exchange-semester credits",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 53,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "217",
        evidence: "91%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COU-2029",
      },
      {
        id: "COUR-104",
        name: "Experiential learning portfolio",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Review due",
        progress: 64,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "234",
        evidence: "64%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COU-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Equivalencies & Credit Transfer actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for records and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [77, 92, 64, 88, 73, 70, 79, 61],
    external: null,
    signature: "academics::course-equivalencies::map::5",
  },
  "academic-resources": {
    id: "academic-resources",
    area: "academics",
    areaLabel: "Programs & Curriculum",
    title: "Teaching & Learning Resources",
    description: "Approved readings, media, datasets, briefs, templates and accessibility-ready resources.",
    group: "Resources",
    icon: "folder",
    layout: "library",
    primaryAction: "Add resource",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Available items",
        value: "87",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Accessible",
        value: "72",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Licensed",
        value: "71",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Review due",
        value: "95",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Resource",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "scope",
        label: "Audience",
      },
      {
        key: "evidence",
        label: "Accessibility",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACAD-100",
        name: "Accessible AI handbook",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 56,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "352",
        evidence: "68%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACA-2026",
      },
      {
        id: "ACAD-101",
        name: "Responsible AI case library",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Draft",
        progress: 67,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "369",
        evidence: "75%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACA-2027",
      },
      {
        id: "ACAD-102",
        name: "Kijani project brief",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 78,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "386",
        evidence: "82%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACA-2028",
      },
      {
        id: "ACAD-103",
        name: "Synthetic finance dataset",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 89,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "403",
        evidence: "89%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACA-2029",
      },
      {
        id: "ACAD-104",
        name: "Assessment rubric pack",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Review due",
        progress: 55,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "420",
        evidence: "96%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Teaching & Learning Resources actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for resources and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [95, 96, 96, 96, 75, 70, 69, 79],
    external: null,
    signature: "academics::academic-resources::library::5",
  },
  "class-connection": {
    id: "class-connection",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Class Connection",
    description:
      "Independent Chatoner Class connection, signed handoff readiness and institutional visibility boundaries.",
    group: "Chatoner Class",
    icon: "link",
    layout: "connection",
    primaryAction: "Test connection",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connected services",
        value: "45",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Handoff success",
        value: "76%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Scope checks",
        value: "53",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "17",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CLAS-100",
        name: "Institution Class tenant",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Active",
        progress: 79,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "120",
        evidence: "76%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CLA-2026",
      },
      {
        id: "CLAS-101",
        name: "Signed learner handoff",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "On track",
        progress: 90,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "137",
        evidence: "83%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CLA-2027",
      },
      {
        id: "CLAS-102",
        name: "Instructor rehearsal access",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 56,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "154",
        evidence: "90%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CLA-2028",
      },
      {
        id: "CLAS-103",
        name: "Attendance return channel",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 67,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "171",
        evidence: "97%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CLA-2029",
      },
      {
        id: "CLAS-104",
        name: "Recording metadata channel",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 78,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "188",
        evidence: "70%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CLA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Class Connection actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for chatoner class and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [73, 80, 82, 72, 69, 58, 55, 76],
    external: {
      domain: "class.chatoner.com",
      mode: "signed-handoff",
    },
    signature: "class::class-connection::connection::5",
  },
  "live-classes": {
    id: "live-classes",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Live Classes",
    description:
      "Upcoming and active classes with automatically prepared links and codes for signed-in users.",
    group: "Chatoner Class",
    icon: "video",
    layout: "sessions",
    primaryAction: "Create session",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Live / upcoming",
        value: "24",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Expected learners",
        value: "57",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Ready sessions",
        value: "18",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Needs setup",
        value: "74",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Session",
      },
      {
        key: "date",
        label: "Date",
      },
      {
        key: "time",
        label: "Time",
      },
      {
        key: "people",
        label: "Expected",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LIVE-100",
        name: "Responsible AI: Human Authority",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Ready",
        progress: 58,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "369",
        evidence: "65%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LIV-2026",
      },
      {
        id: "LIVE-101",
        name: "Reliable Agent Workflows",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 69,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "386",
        evidence: "72%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LIV-2027",
      },
      {
        id: "LIVE-102",
        name: "AI Business Decision Design",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 80,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "403",
        evidence: "79%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LIV-2028",
      },
      {
        id: "LIVE-103",
        name: "Research Methods Clinic",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 91,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "420",
        evidence: "86%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LIV-2029",
      },
      {
        id: "LIVE-104",
        name: "Accessible Product Design",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 57,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "437",
        evidence: "93%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LIV-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Live Classes actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for chatoner class and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [70, 79, 61, 77, 92, 74, 70, 69],
    external: {
      domain: "class.chatoner.com",
      mode: "signed-handoff",
    },
    signature: "class::live-classes::sessions::5",
  },
  "class-schedule": {
    id: "class-schedule",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Class Schedule",
    description: "Institution, campus, program, cohort and instructor live-session schedule.",
    group: "Chatoner Class",
    icon: "calendar",
    layout: "calendar",
    primaryAction: "Add calendar item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Scheduled items",
        value: "44",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "This week",
        value: "34",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Conflicts",
        value: "22",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Confirmed",
        value: "39",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CLAS-100",
        name: "BAAI live schedule",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "On track",
        progress: 65,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "301",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CLA-2026",
      },
      {
        id: "CLAS-101",
        name: "MSIS research clinics",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 76,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "318",
        evidence: "90%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CLA-2027",
      },
      {
        id: "CLAS-102",
        name: "DAIB live studio",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 87,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "335",
        evidence: "97%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CLA-2028",
      },
      {
        id: "CLAS-103",
        name: "Instructor tutorials",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 53,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "352",
        evidence: "70%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CLA-2029",
      },
      {
        id: "CLAS-104",
        name: "Assessment review sessions",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Draft",
        progress: 64,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "369",
        evidence: "77%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CLA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Class Schedule actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for chatoner class and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [86, 62, 56, 55, 86, 62, 67, 68],
    external: {
      domain: "class.chatoner.com",
      mode: "signed-handoff",
    },
    signature: "class::class-schedule::calendar::5",
  },
  "create-class": {
    id: "create-class",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Create Class",
    description:
      "Create a session, assign its instructor and cohort, and generate a unique Class link and code.",
    group: "Teaching Operations",
    icon: "plus",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "10",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "3",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "20",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CREA-100",
        name: "Responsible AI seminar",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Ready",
        progress: 82,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "93",
        evidence: "81%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CRE-2026",
      },
      {
        id: "CREA-101",
        name: "Agent workflow workshop",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 93,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "110",
        evidence: "88%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CRE-2027",
      },
      {
        id: "CREA-102",
        name: "Research clinic",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 59,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "127",
        evidence: "95%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CRE-2028",
      },
      {
        id: "CREA-103",
        name: "Project review huddle",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 70,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "144",
        evidence: "68%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CRE-2029",
      },
      {
        id: "CREA-104",
        name: "Student advising session",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 81,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "161",
        evidence: "75%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CRE-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Create Class actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for teaching operations and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Class title",
      "Program",
      "Cohort",
      "Instructor",
      "Date",
      "Start time",
      "Duration",
      "Language",
      "Recording policy",
      "Attendance return",
    ],
    chart: [88, 94, 75, 91, 74, 80, 92, 74],
    external: {
      domain: "class.chatoner.com",
      mode: "signed-handoff",
    },
    signature: "class::create-class::form::5",
  },
  attendance: {
    id: "attendance",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Attendance",
    description:
      "Session attendance summaries, late arrivals, excused absences and privacy-aware interventions.",
    group: "Teaching Operations",
    icon: "check",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "27",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "33",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "10",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "85",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ATTE-100",
        name: "Responsible AI seminar",
        owner: "University Library",
        scope: "Whole institution",
        status: "Active",
        progress: 70,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "96",
        evidence: "78%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ATT-2026",
      },
      {
        id: "ATTE-101",
        name: "Agent workflow workshop",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "On track",
        progress: 81,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "113",
        evidence: "85%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ATT-2027",
      },
      {
        id: "ATTE-102",
        name: "AI Business Decisions",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 92,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "130",
        evidence: "92%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ATT-2028",
      },
      {
        id: "ATTE-103",
        name: "Research Methods Clinic",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 58,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "147",
        evidence: "65%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ATT-2029",
      },
      {
        id: "ATTE-104",
        name: "Accessibility tutorial",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 69,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "164",
        evidence: "72%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ATT-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Attendance actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for teaching operations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [91, 74, 59, 56, 86, 83, 93, 96],
    external: null,
    signature: "class::attendance::table::5",
  },
  recordings: {
    id: "recordings",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Recordings & Captions",
    description: "Authorized recordings, transcripts, captions, languages, retention and access scope.",
    group: "Teaching Operations",
    icon: "play",
    layout: "media",
    primaryAction: "Add recording",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Recordings",
        value: "28",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Captioned",
        value: "69",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Languages",
        value: "48",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Retention due",
        value: "70",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Recording / media",
      },
      {
        key: "date",
        label: "Published",
      },
      {
        key: "scope",
        label: "Access",
      },
      {
        key: "evidence",
        label: "Caption status",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RECO-100",
        name: "Human Authority in Practice",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Draft",
        progress: 87,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "113",
        evidence: "65%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "REC-2026",
      },
      {
        id: "RECO-101",
        name: "Reliable Agent Workflows",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Active",
        progress: 53,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "130",
        evidence: "72%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "REC-2027",
      },
      {
        id: "RECO-102",
        name: "AI Decision Design",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 64,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "147",
        evidence: "79%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "REC-2028",
      },
      {
        id: "RECO-103",
        name: "Research Methods Clinic",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 75,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "164",
        evidence: "86%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "REC-2029",
      },
      {
        id: "RECO-104",
        name: "Inclusive Product Review",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 86,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "181",
        evidence: "93%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "REC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Recordings & Captions actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for teaching operations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [66, 57, 87, 84, 62, 56, 76, 70],
    external: null,
    signature: "class::recordings::media::5",
  },
  "office-hours": {
    id: "office-hours",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Office Hours & Tutorials",
    description: "Instructor office hours, advising sessions, tutorials and smaller-group huddles.",
    group: "Teaching Operations",
    icon: "clock",
    layout: "timetable",
    primaryAction: "Add schedule item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Scheduled sessions",
        value: "53",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Rooms / channels",
        value: "53",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Conflicts",
        value: "88",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Coverage",
        value: "88%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "OFFI-100",
        name: "Amina Mutesi office hours",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 91,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "72",
        evidence: "94%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "OFF-2026",
      },
      {
        id: "OFFI-101",
        name: "Daniel Mwesigwa mentor clinic",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 57,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "89",
        evidence: "67%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "OFF-2027",
      },
      {
        id: "OFFI-102",
        name: "Data & AI department tutorial",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 68,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "106",
        evidence: "74%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "OFF-2028",
      },
      {
        id: "OFFI-103",
        name: "Registrar records clinic",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 79,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "123",
        evidence: "81%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "OFF-2029",
      },
      {
        id: "OFFI-104",
        name: "Accessibility support hour",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 90,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "140",
        evidence: "88%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "OFF-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Office Hours & Tutorials actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for teaching operations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [67, 89, 95, 96, 86, 73, 70, 90],
    external: null,
    signature: "class::office-hours::timetable::5",
  },
  "class-policies": {
    id: "class-policies",
    area: "class",
    areaLabel: "Chatoner Class",
    title: "Class Policies",
    description: "Recording, attendance, captions, language, safeguarding and data-retention policies.",
    group: "Governance",
    icon: "shield",
    layout: "policy",
    primaryAction: "Create policy version",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active controls",
        value: "46",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Acknowledged",
        value: "70",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Exceptions",
        value: "54",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Review due",
        value: "23",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Policy / control",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Next review",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CLAS-100",
        name: "Recording and consent policy",
        owner: "Information Security",
        scope: "Whole institution",
        status: "On track",
        progress: 59,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "55",
        evidence: "97%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CLA-2026",
      },
      {
        id: "CLAS-101",
        name: "Attendance and late-arrival policy",
        owner: "University Library",
        scope: "School of Computing",
        status: "Review due",
        progress: 70,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "72",
        evidence: "70%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CLA-2027",
      },
      {
        id: "CLAS-102",
        name: "Captions and language policy",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 81,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "89",
        evidence: "77%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CLA-2028",
      },
      {
        id: "CLAS-103",
        name: "Safeguarding in live classes",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 92,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "106",
        evidence: "84%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CLA-2029",
      },
      {
        id: "CLAS-104",
        name: "Class data-retention policy",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "Draft",
        progress: 58,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "123",
        evidence: "91%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CLA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Class Policies actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [92, 64, 78, 81, 72, 80, 71, 59],
    external: null,
    signature: "class::class-policies::policy::5",
  },
  "labs-connection": {
    id: "labs-connection",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Labs Connection",
    description:
      "Independent labs.chatoner.com connection, entitlement sync and institutional data boundaries.",
    group: "Chatoner AI Labs",
    icon: "link",
    layout: "connection",
    primaryAction: "Test connection",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connected services",
        value: "70",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Handoff success",
        value: "90%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Scope checks",
        value: "63",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "71",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LABS-100",
        name: "Institution Labs tenant",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "On track",
        progress: 80,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "61",
        evidence: "79%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LAB-2026",
      },
      {
        id: "LABS-101",
        name: "Entitlement synchronization",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 91,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "78",
        evidence: "86%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LAB-2027",
      },
      {
        id: "LABS-102",
        name: "Learner handoff",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 57,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "95",
        evidence: "93%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LAB-2028",
      },
      {
        id: "LABS-103",
        name: "Evidence return channel",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 68,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "112",
        evidence: "66%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LAB-2029",
      },
      {
        id: "LABS-104",
        name: "Emergency revocation",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Draft",
        progress: 79,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "129",
        evidence: "73%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LAB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Labs Connection actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for chatoner ai labs and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [56, 65, 78, 60, 77, 60, 77, 71],
    external: {
      domain: "labs.chatoner.com",
      mode: "signed-handoff",
    },
    signature: "labs::labs-connection::connection::5",
  },
  "eligible-labs": {
    id: "eligible-labs",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Eligible & Intended Labs",
    description: "Labs connected to programs, courses, assignments and learner entitlements.",
    group: "Chatoner AI Labs",
    icon: "flask",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "91",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "14",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "70%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "90",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ELIG-100",
        name: "AI-App Builder Lab",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 74,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "160",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ELI-2026",
      },
      {
        id: "ELIG-101",
        name: "Agentic AI Lab",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Draft",
        progress: 85,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "177",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ELI-2027",
      },
      {
        id: "ELIG-102",
        name: "Data & Predictive AI Lab",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 96,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "194",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ELI-2028",
      },
      {
        id: "ELIG-103",
        name: "Conversational AI Lab",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 62,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "211",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ELI-2029",
      },
      {
        id: "ELIG-104",
        name: "AI Science Simulation Lab",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Review due",
        progress: 73,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "228",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ELI-2030",
      },
      {
        id: "ELIG-105",
        name: "Graphics, 3D & CAD Lab",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Ready",
        progress: 84,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "245",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ELI-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Eligible & Intended Labs actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for chatoner ai labs and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [71, 90, 74, 70, 79, 82, 82, 61],
    external: null,
    signature: "labs::eligible-labs::portfolio::6",
  },
  "lab-entitlements": {
    id: "lab-entitlements",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Entitlements & Capacity",
    description: "Seats, time, compute, tokens, program access and sponsored practical-learning capacity.",
    group: "Administration",
    icon: "key",
    layout: "capacity",
    primaryAction: "Allocate capacity",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Allocated",
        value: "49",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Used",
        value: "94",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Available",
        value: "28%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Threshold alerts",
        value: "88",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Allocation",
      },
      {
        key: "scope",
        label: "Program",
      },
      {
        key: "people",
        label: "Units",
      },
      {
        key: "progress",
        label: "Utilisation",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LAB--100",
        name: "BAAI App Builder entitlement",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 61,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "222",
        evidence: "84%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LAB-2026",
      },
      {
        id: "LAB--101",
        name: "MSIS Agentic entitlement",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 72,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "239",
        evidence: "91%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LAB-2027",
      },
      {
        id: "LAB--102",
        name: "DAIB Conversational entitlement",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 83,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "256",
        evidence: "64%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LAB-2028",
      },
      {
        id: "LAB--103",
        name: "Engineering Simulation entitlement",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 94,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "273",
        evidence: "71%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LAB-2029",
      },
      {
        id: "LAB--104",
        name: "Research Deep Learning entitlement",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 60,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "290",
        evidence: "78%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LAB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Entitlements & Capacity actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for administration and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [91, 85, 94, 96, 75, 91, 64, 88],
    external: null,
    signature: "labs::lab-entitlements::capacity::5",
  },
  "lab-assignments": {
    id: "lab-assignments",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Lab Assignments",
    description: "Map intended Labs to outcomes, courses, assignments, cohorts and credential evidence.",
    group: "Academic Coordination",
    icon: "clipboard",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "95%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "59",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "82",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "43",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LAB--100",
        name: "Checkout project practice",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "On track",
        progress: 86,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "247",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LAB-2026",
      },
      {
        id: "LAB--101",
        name: "Agent workflow capstone",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Review due",
        progress: 52,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "264",
        evidence: "90%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LAB-2027",
      },
      {
        id: "LAB--102",
        name: "Predictive evaluation practical",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 63,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "281",
        evidence: "97%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LAB-2028",
      },
      {
        id: "LAB--103",
        name: "Conversational assistant brief",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 74,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "298",
        evidence: "70%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LAB-2029",
      },
      {
        id: "LAB--104",
        name: "Science simulation assignment",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "Draft",
        progress: 85,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "315",
        evidence: "77%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LAB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Lab Assignments actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for academic coordination and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [74, 70, 69, 58, 76, 91, 85, 62],
    external: null,
    signature: "labs::lab-assignments::matrix::5",
  },
  "synced-runs": {
    id: "synced-runs",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Synced Cohort Runs",
    description: "Read-only operational status returned from Labs for authorized institution roles.",
    group: "Academic Coordination",
    icon: "pulse",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "35",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "10",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "44",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "87",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SYNC-100",
        name: "BAAI App Builder cohort run",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Review due",
        progress: 96,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "212",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SYN-2026",
      },
      {
        id: "SYNC-101",
        name: "MSIS Agentic cohort run",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Ready",
        progress: 62,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "229",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SYN-2027",
      },
      {
        id: "SYNC-102",
        name: "DAIB Conversational run",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 73,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "246",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SYN-2028",
      },
      {
        id: "SYNC-103",
        name: "Engineering Simulation run",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 84,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "263",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SYN-2029",
      },
      {
        id: "SYNC-104",
        name: "Deep Learning research run",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Active",
        progress: 95,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "280",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SYN-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Synced Cohort Runs actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for academic coordination and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [81, 82, 82, 93, 64, 78, 81, 61],
    external: null,
    signature: "labs::synced-runs::table::5",
  },
  "returned-evidence": {
    id: "returned-evidence",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Returned Evidence",
    description: "Consented completion evidence, verification status and Academy record relationships.",
    group: "Evidence",
    icon: "certificate",
    layout: "evidence",
    primaryAction: "Request review",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Evidence items",
        value: "73",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Verified",
        value: "95",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending",
        value: "67",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Rejected",
        value: "89",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Evidence / credential",
      },
      {
        key: "owner",
        label: "Reviewer",
      },
      {
        key: "evidence",
        label: "Completeness",
      },
      {
        key: "decision",
        label: "Decision",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RETU-100",
        name: "Checkout accessibility proof",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Urgent",
        progress: 68,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "94",
        evidence: "72%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RET-2026",
      },
      {
        id: "RETU-101",
        name: "Agent workflow trace",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Review due",
        progress: 79,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "111",
        evidence: "79%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RET-2027",
      },
      {
        id: "RETU-102",
        name: "Predictive model evaluation",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 90,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "128",
        evidence: "86%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RET-2028",
      },
      {
        id: "RETU-103",
        name: "Conversation safety test",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 56,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "145",
        evidence: "93%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RET-2029",
      },
      {
        id: "RETU-104",
        name: "Simulation experiment record",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 67,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "162",
        evidence: "66%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Returned Evidence actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for evidence and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [89, 95, 75, 81, 61, 88, 63, 78],
    external: null,
    signature: "labs::returned-evidence::evidence::5",
  },
  "labs-analytics": {
    id: "labs-analytics",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Aggregate Labs Analytics",
    description:
      "Privacy-thresholded practical-learning participation, completion, safety and capacity trends.",
    group: "Reporting",
    icon: "chart",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "87%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "92%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "70",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "65",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LABS-100",
        name: "Participation trend",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Draft",
        progress: 78,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "149",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LAB-2026",
      },
      {
        id: "LABS-101",
        name: "Completion trend",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Active",
        progress: 89,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "166",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LAB-2027",
      },
      {
        id: "LABS-102",
        name: "Evidence return rate",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 55,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "183",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LAB-2028",
      },
      {
        id: "LABS-103",
        name: "Compute utilisation",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 66,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "200",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LAB-2029",
      },
      {
        id: "LABS-104",
        name: "Safety intervention rate",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 77,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "217",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LAB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Aggregate Labs Analytics actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for reporting and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [60, 87, 84, 72, 80, 92, 95, 75],
    external: null,
    signature: "labs::labs-analytics::scorecard::5",
  },
  "labs-safety-sync": {
    id: "labs-safety-sync",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Access & Safety Sync",
    description: "Policy, entitlement, monitoring mode, data-scope and emergency-revocation synchronization.",
    group: "Governance",
    icon: "shield",
    layout: "policy",
    primaryAction: "Create policy version",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active controls",
        value: "30",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Acknowledged",
        value: "16",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Exceptions",
        value: "44",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Review due",
        value: "29",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Policy / control",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Next review",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LABS-100",
        name: "Allowed data classes",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Draft",
        progress: 93,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "389",
        evidence: "65%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LAB-2026",
      },
      {
        id: "LABS-101",
        name: "Internet-access policy",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Active",
        progress: 59,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "406",
        evidence: "72%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LAB-2027",
      },
      {
        id: "LABS-102",
        name: "Human approval gates",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 70,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "423",
        evidence: "79%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LAB-2028",
      },
      {
        id: "LABS-103",
        name: "Emergency stop policy",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 81,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "20",
        evidence: "86%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LAB-2029",
      },
      {
        id: "LABS-104",
        name: "Evidence-return consent",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 92,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "37",
        evidence: "93%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LAB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Access & Safety Sync actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [90, 84, 62, 56, 86, 73, 91, 85],
    external: null,
    signature: "labs::labs-safety-sync::policy::5",
  },
  "open-labs-workspace": {
    id: "open-labs-workspace",
    area: "labs",
    areaLabel: "Chatoner AI Labs",
    title: "Open Labs Workspace",
    description:
      "Secure handoff to the independent Labs workspace appropriate to the user’s authorized role.",
    group: "Chatoner AI Labs",
    icon: "external",
    layout: "connection",
    primaryAction: "Test connection",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connected services",
        value: "82",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Handoff success",
        value: "70%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Scope checks",
        value: "11",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "32",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "OPEN-100",
        name: "Principal administration view",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Ready",
        progress: 73,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "129",
        evidence: "79%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "OPE-2026",
      },
      {
        id: "OPEN-101",
        name: "Program manager view",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 84,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "146",
        evidence: "86%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "OPE-2027",
      },
      {
        id: "OPEN-102",
        name: "Instructor supervision view",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 95,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "163",
        evidence: "93%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "OPE-2028",
      },
      {
        id: "OPEN-103",
        name: "Learner intended Lab",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 61,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "180",
        evidence: "66%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "OPE-2029",
      },
      {
        id: "OPEN-104",
        name: "Safety reviewer view",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 72,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "197",
        evidence: "73%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "OPE-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Open Labs Workspace actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for chatoner ai labs and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [82, 61, 67, 68, 79, 92, 74, 59],
    external: {
      domain: "labs.chatoner.com",
      mode: "signed-handoff",
    },
    signature: "labs::open-labs-workspace::connection::5",
  },
  "room-portfolio": {
    id: "room-portfolio",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Build Room Portfolio",
    description: "Parent class rooms, course rooms, project rooms and connected learning evidence.",
    group: "Build Rooms",
    icon: "message",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "24",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "33",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "71%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "83",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ROOM-100",
        name: "BAAI Year 3 parent room",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Ready",
        progress: 82,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "33",
        evidence: "67%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROO-2026",
      },
      {
        id: "ROOM-101",
        name: "Kijani checkout project",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 93,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "50",
        evidence: "74%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROO-2027",
      },
      {
        id: "ROOM-102",
        name: "MSIS research studio",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 59,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "67",
        evidence: "81%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROO-2028",
      },
      {
        id: "ROOM-103",
        name: "DAIB decision-design room",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 70,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "84",
        evidence: "88%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROO-2029",
      },
      {
        id: "ROOM-104",
        name: "CRAI policy clinic",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 81,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "101",
        evidence: "95%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROO-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Build Room Portfolio actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for build rooms and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [70, 79, 71, 59, 87, 63, 67, 58],
    external: null,
    signature: "build::room-portfolio::portfolio::5",
  },
  "parent-rooms": {
    id: "parent-rooms",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Parent Rooms",
    description:
      "Shared class context, announcements, requirements, plans, resources and whole-group decisions.",
    group: "Build Rooms",
    icon: "users",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "90",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "61",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "76%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "36",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PARE-100",
        name: "BAAI Year 3 parent room",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Draft",
        progress: 72,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "383",
        evidence: "73%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PAR-2026",
      },
      {
        id: "PARE-101",
        name: "MSIS research cohort room",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Active",
        progress: 83,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "400",
        evidence: "80%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PAR-2027",
      },
      {
        id: "PARE-102",
        name: "DAIB Cohort 12 room",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 94,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "417",
        evidence: "87%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PAR-2028",
      },
      {
        id: "PARE-103",
        name: "CRAI August cohort room",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 60,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "434",
        evidence: "94%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PAR-2029",
      },
      {
        id: "PARE-104",
        name: "MAET teaching studio",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 71,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "31",
        evidence: "67%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PAR-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Parent Rooms actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for build rooms and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [84, 62, 67, 58, 87, 73, 80, 82],
    external: null,
    signature: "build::parent-rooms::portfolio::5",
  },
  "smaller-groups": {
    id: "smaller-groups",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Smaller Groups",
    description:
      "Instructor-formed subgroups within a parent class, with institution-approved privacy and AI boundaries.",
    group: "Build Rooms",
    icon: "network",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "10",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "21",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "81%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "84",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SMAL-100",
        name: "Accessibility & Experience",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Ready",
        progress: 82,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "243",
        evidence: "81%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SMA-2026",
      },
      {
        id: "SMAL-101",
        name: "Trust, Safety & Consent",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 93,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "260",
        evidence: "88%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SMA-2027",
      },
      {
        id: "SMAL-102",
        name: "API & Data Boundaries",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 59,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "277",
        evidence: "95%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SMA-2028",
      },
      {
        id: "SMAL-103",
        name: "Evaluation & Evidence",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 70,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "294",
        evidence: "68%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SMA-2029",
      },
      {
        id: "SMAL-104",
        name: "Automation & Agent Boundaries",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 81,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "311",
        evidence: "75%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SMA-2030",
      },
      {
        id: "SMAL-105",
        name: "Demo, Story & Handoff",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Review due",
        progress: 92,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "328",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SMA-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Smaller Groups actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for build rooms and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [70, 69, 69, 69, 58, 55, 76, 81],
    external: null,
    signature: "build::smaller-groups::portfolio::6",
  },
  "cross-group-progress": {
    id: "cross-group-progress",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Cross-group Progress",
    description:
      "Aggregate progress, evidence, blockers and review status without exposing private conversations.",
    group: "Oversight",
    icon: "chart",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "84%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "39",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "40",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "52",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CROS-100",
        name: "Accessibility group",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "Draft",
        progress: 75,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "341",
        evidence: "81%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CRO-2026",
      },
      {
        id: "CROS-101",
        name: "Trust and consent group",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Active",
        progress: 86,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "358",
        evidence: "88%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CRO-2027",
      },
      {
        id: "CROS-102",
        name: "API boundaries group",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 52,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "375",
        evidence: "95%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CRO-2028",
      },
      {
        id: "CROS-103",
        name: "Evaluation group",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 63,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "392",
        evidence: "68%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CRO-2029",
      },
      {
        id: "CROS-104",
        name: "Agent boundaries group",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Ready",
        progress: 74,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "409",
        evidence: "75%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CRO-2030",
      },
      {
        id: "CROS-105",
        name: "Demo and handoff group",
        owner: "University Library",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 85,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "426",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CRO-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Cross-group Progress actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for oversight and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [84, 93, 75, 81, 82, 72, 80, 71],
    external: null,
    signature: "build::cross-group-progress::matrix::6",
  },
  "evidence-coverage": {
    id: "evidence-coverage",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Evidence Coverage",
    description: "Requirement-to-artifact-to-test-to-evidence traceability across rooms and groups.",
    group: "Evidence",
    icon: "certificate",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "81%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "77",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "42",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "23",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "EVID-100",
        name: "UX-04 accessible checkout",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Review due",
        progress: 54,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "230",
        evidence: "74%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "EVI-2026",
      },
      {
        id: "EVID-101",
        name: "ACC-02 keyboard flow",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Ready",
        progress: 65,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "247",
        evidence: "81%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "EVI-2027",
      },
      {
        id: "EVID-102",
        name: "SEC-06 data boundaries",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 76,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "264",
        evidence: "88%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "EVI-2028",
      },
      {
        id: "EVID-103",
        name: "EVAL-03 evaluation proof",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 87,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "281",
        evidence: "95%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "EVI-2029",
      },
      {
        id: "EVID-104",
        name: "CRED-01 credential readiness",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Active",
        progress: 53,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "298",
        evidence: "68%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "EVI-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Evidence Coverage actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for evidence and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [57, 87, 63, 88, 94, 75, 81, 82],
    external: null,
    signature: "build::evidence-coverage::matrix::5",
  },
  "room-templates": {
    id: "room-templates",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Room Templates",
    description: "Institution-approved room, subgroup, permission, evidence and review templates.",
    group: "Configuration",
    icon: "layout",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "57",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "29",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "96%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "59",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ROOM-100",
        name: "Project build room",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Active",
        progress: 52,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "408",
        evidence: "76%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROO-2026",
      },
      {
        id: "ROOM-101",
        name: "Parent course room",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "On track",
        progress: 63,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "425",
        evidence: "83%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROO-2027",
      },
      {
        id: "ROOM-102",
        name: "Assessment review room",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 74,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "22",
        evidence: "90%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROO-2028",
      },
      {
        id: "ROOM-103",
        name: "Research supervision room",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 85,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "39",
        evidence: "97%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROO-2029",
      },
      {
        id: "ROOM-104",
        name: "Industry challenge room",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 96,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "56",
        evidence: "70%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROO-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Room Templates actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for configuration and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [67, 68, 89, 84, 83, 93, 85, 62],
    external: null,
    signature: "build::room-templates::portfolio::5",
  },
  "room-approvals": {
    id: "room-approvals",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Room Policies & Approvals",
    description: "Approve high-risk templates, external access, AI boundaries and safeguarding exceptions.",
    group: "Governance",
    icon: "check",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "15",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "15",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "27",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "71%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ROOM-100",
        name: "External mentor access",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Urgent",
        progress: 96,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "242",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROO-2026",
      },
      {
        id: "ROOM-101",
        name: "High-risk AI-agent scope",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 62,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "259",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROO-2027",
      },
      {
        id: "ROOM-102",
        name: "Safeguarding exception",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 73,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "276",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROO-2028",
      },
      {
        id: "ROOM-103",
        name: "Cross-program room",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 84,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "293",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROO-2029",
      },
      {
        id: "ROOM-104",
        name: "Evidence export request",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 95,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "310",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROO-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Room Policies & Approvals actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [69, 69, 79, 82, 93, 64, 88, 73],
    external: null,
    signature: "build::room-approvals::queue::5",
  },
  "room-review-queue": {
    id: "room-review-queue",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Review Queue",
    description: "Instructor and authorized reviewer work awaiting academic judgement.",
    group: "Oversight",
    icon: "inbox",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "81",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "37",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "34",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "73%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ROOM-100",
        name: "Checkout staging evidence",
        owner: "University Library",
        scope: "Whole institution",
        status: "Urgent",
        progress: 95,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "346",
        evidence: "86%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROO-2026",
      },
      {
        id: "ROOM-101",
        name: "Agent capstone reflection",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 61,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "363",
        evidence: "93%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROO-2027",
      },
      {
        id: "ROOM-102",
        name: "Predictive model evaluation",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 72,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "380",
        evidence: "66%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROO-2028",
      },
      {
        id: "ROOM-103",
        name: "Research ethics update",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 83,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "397",
        evidence: "73%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROO-2029",
      },
      {
        id: "ROOM-104",
        name: "Final demo recording",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 94,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "414",
        evidence: "80%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROO-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Review Queue actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for oversight and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [89, 74, 91, 85, 62, 67, 79, 61],
    external: null,
    signature: "build::room-review-queue::queue::5",
  },
  "room-members": {
    id: "room-members",
    area: "build",
    areaLabel: "Build Rooms",
    title: "Member Directory",
    description:
      "Large-room searchable directory, role filters, group assignments and scoped contact actions.",
    group: "Build Rooms",
    icon: "users",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "39",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "87",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "67",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "48",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ROOM-100",
        name: "Amina Nanyonga",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Review due",
        progress: 87,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "218",
        evidence: "88%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROO-2026",
      },
      {
        id: "ROOM-101",
        name: "Daniel Okello",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Ready",
        progress: 53,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "235",
        evidence: "95%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ROO-2027",
      },
      {
        id: "ROOM-102",
        name: "Grace Namusoke",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 64,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "252",
        evidence: "68%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ROO-2028",
      },
      {
        id: "ROOM-103",
        name: "Michael Chen",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 75,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "269",
        evidence: "75%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ROO-2029",
      },
      {
        id: "ROOM-104",
        name: "Sarah Atim",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Active",
        progress: 86,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "286",
        evidence: "82%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ROO-2030",
      },
      {
        id: "ROOM-105",
        name: "External reviewer pool",
        owner: "Research Office",
        scope: "Whole institution",
        status: "On track",
        progress: 52,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "303",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ROO-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Member Directory actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for build rooms and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [87, 63, 67, 79, 61, 77, 92, 95],
    external: null,
    signature: "build::room-members::directory::6",
  },
  "assessment-calendar": {
    id: "assessment-calendar",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Assessment Calendar",
    description: "Assignments, examinations, practicals, panels, moderation windows and publication dates.",
    group: "Assessment",
    icon: "calendar",
    layout: "calendar",
    primaryAction: "Add calendar item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Scheduled items",
        value: "31",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "This week",
        value: "45",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Conflicts",
        value: "23",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Confirmed",
        value: "68",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ASSE-100",
        name: "RAI 304 submission window",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 83,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "424",
        evidence: "72%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ASS-2026",
      },
      {
        id: "ASSE-101",
        name: "AIS 502 capstone panel",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Draft",
        progress: 94,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "21",
        evidence: "79%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ASS-2027",
      },
      {
        id: "ASSE-102",
        name: "AIB 210 decision brief",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 60,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "38",
        evidence: "86%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ASS-2028",
      },
      {
        id: "ASSE-103",
        name: "DPA 220 practical exam",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 71,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "55",
        evidence: "93%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ASS-2029",
      },
      {
        id: "ASSE-104",
        name: "Progression board",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Review due",
        progress: 82,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "72",
        evidence: "66%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ASS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Assessment Calendar actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for assessment and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [83, 72, 69, 90, 95, 96, 96, 65],
    external: null,
    signature: "assessment::assessment-calendar::calendar::5",
  },
  assignments: {
    id: "assignments",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Assignments",
    description:
      "Institution-approved assignments, outcomes, briefs, rubrics, evidence and delivery relationships.",
    group: "Assessment",
    icon: "clipboard",
    layout: "board",
    primaryAction: "Add work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Backlog",
        value: "6",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "In progress",
        value: "73%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Human review",
        value: "94",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Complete",
        value: "43",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "decision",
        label: "Gate",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ASSI-100",
        name: "Responsible AI System Review",
        owner: "Research Office",
        scope: "Whole institution",
        status: "On track",
        progress: 77,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "283",
        evidence: "67%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ASS-2026",
      },
      {
        id: "ASSI-101",
        name: "Agent Workflow Capstone",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 88,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "300",
        evidence: "74%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ASS-2027",
      },
      {
        id: "ASSI-102",
        name: "Business AI Decision Brief",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 54,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "317",
        evidence: "81%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ASS-2028",
      },
      {
        id: "ASSI-103",
        name: "Predictive Model Evaluation",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 65,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "334",
        evidence: "88%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ASS-2029",
      },
      {
        id: "ASSI-104",
        name: "Accessible Checkout Project",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 76,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "351",
        evidence: "95%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ASS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Assignments actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for assessment and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [68, 79, 82, 82, 72, 90, 63, 78],
    external: null,
    signature: "assessment::assignments::board::5",
  },
  gradebook: {
    id: "gradebook",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Gradebook",
    description:
      "Authorized marks, statuses, moderation indicators, missing results and publication readiness.",
    group: "Assessment",
    icon: "table",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "39",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "22",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "57",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "76",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "GRAD-100",
        name: "RAI 304 gradebook",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Review due",
        progress: 81,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "392",
        evidence: "80%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "GRA-2026",
      },
      {
        id: "GRAD-101",
        name: "AIS 502 gradebook",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Ready",
        progress: 92,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "409",
        evidence: "87%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "GRA-2027",
      },
      {
        id: "GRAD-102",
        name: "AIB 210 gradebook",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 58,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "426",
        evidence: "94%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "GRA-2028",
      },
      {
        id: "GRAD-103",
        name: "DPA 220 gradebook",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 69,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "23",
        evidence: "67%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "GRA-2029",
      },
      {
        id: "GRAD-104",
        name: "MAET assessment record",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Active",
        progress: 80,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "40",
        evidence: "74%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "GRA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Gradebook actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for assessment and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [93, 64, 88, 63, 57, 76, 70, 58],
    external: null,
    signature: "assessment::gradebook::table::5",
  },
  rubrics: {
    id: "rubrics",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Rubrics & Standards",
    description: "Outcome-aligned rubrics, grade descriptors, calibration and version control.",
    group: "Assessment",
    icon: "sliders",
    layout: "rubric",
    primaryAction: "Create rubric version",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Criteria",
        value: "22",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Outcome links",
        value: "57",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Calibrated",
        value: "6",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Draft changes",
        value: "91",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Criterion",
      },
      {
        key: "scope",
        label: "Outcome",
      },
      {
        key: "evidence",
        label: "Calibration",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RUBR-100",
        name: "Responsible AI review rubric",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "On track",
        progress: 80,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "331",
        evidence: "81%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RUB-2026",
      },
      {
        id: "RUBR-101",
        name: "Agent capstone rubric",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 91,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "348",
        evidence: "88%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RUB-2027",
      },
      {
        id: "RUBR-102",
        name: "Business decision rubric",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 57,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "365",
        evidence: "95%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RUB-2028",
      },
      {
        id: "RUBR-103",
        name: "Predictive evaluation rubric",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 68,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "382",
        evidence: "68%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RUB-2029",
      },
      {
        id: "RUBR-104",
        name: "Capstone presentation rubric",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Draft",
        progress: 79,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "399",
        evidence: "75%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RUB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Rubrics & Standards actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for assessment and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [74, 91, 85, 62, 88, 84, 83, 62],
    external: null,
    signature: "assessment::rubrics::rubric::5",
  },
  examinations: {
    id: "examinations",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Examinations",
    description: "Exam timetables, venues, access arrangements, invigilation and controlled materials.",
    group: "Assessment",
    icon: "file",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "25",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "39",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "58",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "35",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "EXAM-100",
        name: "DPA 220 practical examination",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 62,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "208",
        evidence: "88%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "EXA-2026",
      },
      {
        id: "EXAM-101",
        name: "RAI 304 controlled review",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Draft",
        progress: 73,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "225",
        evidence: "95%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "EXA-2027",
      },
      {
        id: "EXAM-102",
        name: "MSIS research viva",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 84,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "242",
        evidence: "68%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "EXA-2028",
      },
      {
        id: "EXAM-103",
        name: "DAIB final case examination",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 95,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "259",
        evidence: "75%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "EXA-2029",
      },
      {
        id: "EXAM-104",
        name: "Special sitting",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Review due",
        progress: 61,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "276",
        evidence: "82%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "EXA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Examinations actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for assessment and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [77, 81, 61, 77, 92, 64, 67, 58],
    external: null,
    signature: "assessment::examinations::table::5",
  },
  moderation: {
    id: "moderation",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Moderation & External Review",
    description: "Sampling, second marking, external examination, disagreements and decision records.",
    group: "Quality",
    icon: "shield",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "55",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "65",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "53",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "83%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "MODE-100",
        name: "RAI 304 disagreement",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Urgent",
        progress: 92,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "58",
        evidence: "88%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "MOD-2026",
      },
      {
        id: "MODE-101",
        name: "AIS 502 sample moderation",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Review due",
        progress: 58,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "75",
        evidence: "95%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "MOD-2027",
      },
      {
        id: "MODE-102",
        name: "AIB 210 second marking",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 69,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "92",
        evidence: "68%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "MOD-2028",
      },
      {
        id: "MODE-103",
        name: "DPA 220 external review",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 80,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "109",
        evidence: "75%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "MOD-2029",
      },
      {
        id: "MODE-104",
        name: "MAET standards calibration",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 91,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "126",
        evidence: "82%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "MOD-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Moderation & External Review actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for quality and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [95, 65, 89, 84, 93, 85, 73, 59],
    external: null,
    signature: "assessment::moderation::queue::5",
  },
  "academic-appeals": {
    id: "academic-appeals",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Appeals & Special Consideration",
    description: "Documented student requests, evidence, panel decisions and protected records.",
    group: "Student Rights",
    icon: "gavel",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "51",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "41",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "28",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "89%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACAD-100",
        name: "Result review request STU-20496",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Urgent",
        progress: 76,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "102",
        evidence: "90%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACA-2026",
      },
      {
        id: "ACAD-101",
        name: "Special consideration STU-20513",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 87,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "119",
        evidence: "97%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACA-2027",
      },
      {
        id: "ACAD-102",
        name: "Assessment-access appeal",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 53,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "136",
        evidence: "70%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACA-2028",
      },
      {
        id: "ACAD-103",
        name: "Progression decision appeal",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 64,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "153",
        evidence: "77%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACA-2029",
      },
      {
        id: "ACAD-104",
        name: "Academic standing correction",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 75,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "170",
        evidence: "84%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Appeals & Special Consideration actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for student rights and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [55, 76, 81, 82, 82, 93, 85, 94],
    external: null,
    signature: "assessment::academic-appeals::queue::5",
  },
  "academic-records": {
    id: "academic-records",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Academic Records",
    description: "Official results, progression, standing, awards, changes and immutable record history.",
    group: "Records",
    icon: "archive",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "67",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "36",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "63",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "36",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACAD-100",
        name: "Amina Nanyonga record",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Review due",
        progress: 78,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "344",
        evidence: "96%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACA-2026",
      },
      {
        id: "ACAD-101",
        name: "Daniel Okello record",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Ready",
        progress: 89,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "361",
        evidence: "69%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACA-2027",
      },
      {
        id: "ACAD-102",
        name: "Peter Mugisha record",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 55,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "378",
        evidence: "76%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACA-2028",
      },
      {
        id: "ACAD-103",
        name: "Fatima Al-Hassan record",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 66,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "395",
        evidence: "83%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACA-2029",
      },
      {
        id: "ACAD-104",
        name: "BAAI Cohort record batch",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Active",
        progress: 77,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "412",
        evidence: "90%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Academic Records actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for records and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [87, 94, 96, 86, 83, 93, 96, 65],
    external: null,
    signature: "assessment::academic-records::table::5",
  },
  transcripts: {
    id: "transcripts",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Transcripts & Statements",
    description: "Authorized transcript generation, statements of results and verification references.",
    group: "Records",
    icon: "file",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "18",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "15",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "44",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "11",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "TRAN-100",
        name: "Amina Nanyonga transcript",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Ready",
        progress: 67,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "93",
        evidence: "65%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "TRA-2026",
      },
      {
        id: "TRAN-101",
        name: "Daniel Okello statement",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 78,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "110",
        evidence: "72%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "TRA-2027",
      },
      {
        id: "TRAN-102",
        name: "Fatima Al-Hassan transcript",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 89,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "127",
        evidence: "79%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "TRA-2028",
      },
      {
        id: "TRAN-103",
        name: "BAAI graduating cohort",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 55,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "144",
        evidence: "86%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "TRA-2029",
      },
      {
        id: "TRAN-104",
        name: "Verified employer copy",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 66,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "161",
        evidence: "93%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "TRA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Transcripts & Statements actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for records and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [88, 73, 59, 66, 78, 60, 77, 92],
    external: null,
    signature: "assessment::transcripts::table::5",
  },
  "progression-awards": {
    id: "progression-awards",
    area: "assessment",
    areaLabel: "Assessment & Records",
    title: "Progression & Awards",
    description: "Progression rules, boards, completion decisions, awards and graduation readiness.",
    group: "Records",
    icon: "award",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "75",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "36",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "5",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "32",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PROG-100",
        name: "BAAI Year 1 progression board",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Review due",
        progress: 93,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "404",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PRO-2026",
      },
      {
        id: "PROG-101",
        name: "BAAI Year 3 award board",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Ready",
        progress: 59,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "421",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PRO-2027",
      },
      {
        id: "PROG-102",
        name: "MSIS dissertation completion",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 70,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "18",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PRO-2028",
      },
      {
        id: "PROG-103",
        name: "DAIB completion board",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 81,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "35",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PRO-2029",
      },
      {
        id: "PROG-104",
        name: "CRAI credential award",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Active",
        progress: 92,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "52",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PRO-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Progression & Awards actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for records and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [63, 88, 94, 75, 81, 93, 64, 88],
    external: null,
    signature: "assessment::progression-awards::table::5",
  },
  "research-overview": {
    id: "research-overview",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Research Overview",
    description: "Student and faculty research portfolio, supervision, ethics, milestones and outputs.",
    group: "Research",
    icon: "rocket",
    layout: "dashboard",
    primaryAction: "Create institutional item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active learners",
        value: "53",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Programs on track",
        value: "29",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Priority decisions",
        value: "81",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Platform health",
        value: "96%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RESE-100",
        name: "Student research portfolio",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Review due",
        progress: 63,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "404",
        evidence: "96%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RES-2026",
      },
      {
        id: "RESE-101",
        name: "Faculty research portfolio",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Ready",
        progress: 74,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "421",
        evidence: "69%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RES-2027",
      },
      {
        id: "RESE-102",
        name: "Ethics pipeline",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 85,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "18",
        evidence: "76%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RES-2028",
      },
      {
        id: "RESE-103",
        name: "Industry collaboration",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 96,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "35",
        evidence: "83%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RES-2029",
      },
      {
        id: "RESE-104",
        name: "Institutional repository",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Active",
        progress: 62,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "52",
        evidence: "90%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RES-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Research Overview actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for research and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [63, 67, 89, 95, 65, 57, 76, 81],
    external: null,
    signature: "research::research-overview::dashboard::5",
  },
  "research-projects": {
    id: "research-projects",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Research Projects",
    description: "Project proposals, teams, supervisors, timelines, evidence and dissemination.",
    group: "Research",
    icon: "folder",
    layout: "board",
    primaryAction: "Add work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Backlog",
        value: "60",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "In progress",
        value: "77%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Human review",
        value: "4",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Complete",
        value: "38",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "decision",
        label: "Gate",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RESE-100",
        name: "Low-bandwidth AI tutoring",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Draft",
        progress: 84,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "185",
        evidence: "87%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RES-2026",
      },
      {
        id: "RESE-101",
        name: "Responsible AI procurement",
        owner: "University Library",
        scope: "School of Computing",
        status: "Active",
        progress: 95,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "202",
        evidence: "94%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RES-2027",
      },
      {
        id: "RESE-102",
        name: "Predictive agriculture assistant",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 61,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "219",
        evidence: "67%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RES-2028",
      },
      {
        id: "RESE-103",
        name: "Multilingual public-service bot",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 72,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "236",
        evidence: "74%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RES-2029",
      },
      {
        id: "RESE-104",
        name: "Accessible digital assessment",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "Ready",
        progress: 83,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "253",
        evidence: "81%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RES-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Research Projects actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for research and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [96, 75, 60, 56, 76, 60, 56, 76],
    external: null,
    signature: "research::research-projects::board::5",
  },
  "ethics-approvals": {
    id: "ethics-approvals",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Research Ethics",
    description: "Applications, risk classification, consent, data management and committee decisions.",
    group: "Research Governance",
    icon: "shield",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "92",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "20",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "39",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "94%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ETHI-100",
        name: "Low-bandwidth tutoring study",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "Urgent",
        progress: 86,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "307",
        evidence: "87%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ETH-2026",
      },
      {
        id: "ETHI-101",
        name: "Public-service chatbot study",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Review due",
        progress: 52,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "324",
        evidence: "94%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ETH-2027",
      },
      {
        id: "ETHI-102",
        name: "Student analytics study",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 63,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "341",
        evidence: "67%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ETH-2028",
      },
      {
        id: "ETHI-103",
        name: "AI procurement interviews",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 74,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "358",
        evidence: "74%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ETH-2029",
      },
      {
        id: "ETHI-104",
        name: "Accessibility testing study",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 85,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "375",
        evidence: "81%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ETH-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Research Ethics actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for research governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [92, 64, 78, 92, 85, 94, 96, 75],
    external: null,
    signature: "research::ethics-approvals::queue::5",
  },
  "industry-partnerships": {
    id: "industry-partnerships",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Industry & Community Partnerships",
    description: "External briefs, placements, simulations, sponsors, permissions and outcomes.",
    group: "Partnerships",
    icon: "handshake",
    layout: "partnership",
    primaryAction: "Add partnership",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active partners",
        value: "31",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Learners placed",
        value: "87",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Open briefs",
        value: "55",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "6",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Partner / brief",
      },
      {
        key: "owner",
        label: "Institution lead",
      },
      {
        key: "people",
        label: "Learners",
      },
      {
        key: "date",
        label: "Milestone",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "INDU-100",
        name: "Kijani Market challenge",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Review due",
        progress: 57,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "218",
        evidence: "86%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "IND-2026",
      },
      {
        id: "INDU-101",
        name: "Ministry service-design lab",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Ready",
        progress: 68,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "235",
        evidence: "93%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "IND-2027",
      },
      {
        id: "INDU-102",
        name: "Agritech placement program",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 79,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "252",
        evidence: "66%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "IND-2028",
      },
      {
        id: "INDU-103",
        name: "Banking AI simulation",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 90,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "269",
        evidence: "73%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "IND-2029",
      },
      {
        id: "INDU-104",
        name: "Community accessibility clinic",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Active",
        progress: 56,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "286",
        evidence: "80%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "IND-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Industry & Community Partnerships actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for partnerships and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [87, 63, 57, 66, 89, 74, 70, 79],
    external: null,
    signature: "research::industry-partnerships::partnership::5",
  },
  capstones: {
    id: "capstones",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Capstones & Final Projects",
    description: "Capstone requirements, panels, demos, repositories, evidence and final decisions.",
    group: "Projects",
    icon: "trophy",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "22",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "80",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "91%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "59",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CAPS-100",
        name: "Kijani accessible checkout",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "On track",
        progress: 53,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "199",
        evidence: "77%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CAP-2026",
      },
      {
        id: "CAPS-101",
        name: "Public-service assistant",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 64,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "216",
        evidence: "84%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CAP-2027",
      },
      {
        id: "CAPS-102",
        name: "Agriculture prediction tool",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 75,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "233",
        evidence: "91%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CAP-2028",
      },
      {
        id: "CAPS-103",
        name: "Learning-support workflow",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 86,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "250",
        evidence: "64%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CAP-2029",
      },
      {
        id: "CAPS-104",
        name: "AI governance dashboard",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 52,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "267",
        evidence: "71%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CAP-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Capstones & Final Projects actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for projects and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [68, 79, 92, 64, 88, 63, 57, 66],
    external: null,
    signature: "research::capstones::portfolio::5",
  },
  publications: {
    id: "publications",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Publications & Outputs",
    description:
      "Approved publications, posters, datasets, demonstrations and institutional repository links.",
    group: "Research Outputs",
    icon: "library",
    layout: "publications",
    primaryAction: "Add output",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Approved outputs",
        value: "82",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Repository ready",
        value: "37",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Under review",
        value: "11",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Embargoed",
        value: "74",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Output",
      },
      {
        key: "owner",
        label: "Author / office",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "decision",
        label: "Repository",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PUBL-100",
        name: "Responsible AI procurement paper",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "On track",
        progress: 71,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "127",
        evidence: "75%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PUB-2026",
      },
      {
        id: "PUBL-101",
        name: "Accessible checkout case study",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Review due",
        progress: 82,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "144",
        evidence: "82%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PUB-2027",
      },
      {
        id: "PUBL-102",
        name: "Multilingual assistant dataset",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 93,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "161",
        evidence: "89%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PUB-2028",
      },
      {
        id: "PUBL-103",
        name: "Agriculture model poster",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 59,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "178",
        evidence: "96%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PUB-2029",
      },
      {
        id: "PUBL-104",
        name: "Learning analytics report",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "Draft",
        progress: 70,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "195",
        evidence: "69%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PUB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Publications & Outputs actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for research outputs and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [80, 82, 93, 75, 81, 93, 96, 86],
    external: null,
    signature: "research::publications::publications::5",
  },
  credentials: {
    id: "credentials",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Credentials & Awards",
    description: "Academy and institution credentials, evidence readiness, decisions and verification.",
    group: "Credentials",
    icon: "certificate",
    layout: "portfolio",
    primaryAction: "Create portfolio item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active items",
        value: "95",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Participants",
        value: "79",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Average progress",
        value: "82%",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "41",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Portfolio item",
      },
      {
        key: "category",
        label: "Type",
      },
      {
        key: "people",
        label: "Participants",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CRED-100",
        name: "AI Product Builder credential",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Active",
        progress: 64,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "150",
        evidence: "84%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CRE-2026",
      },
      {
        id: "CRED-101",
        name: "Responsible AI Practitioner",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "On track",
        progress: 75,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "167",
        evidence: "91%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CRE-2027",
      },
      {
        id: "CRED-102",
        name: "Data & Predictive AI",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 86,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "184",
        evidence: "64%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CRE-2028",
      },
      {
        id: "CRED-103",
        name: "Agent Workflow Engineer",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 52,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "201",
        evidence: "71%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CRE-2029",
      },
      {
        id: "CRED-104",
        name: "AI-Enabled Teaching",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 63,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "218",
        evidence: "78%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CRE-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Credentials & Awards actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for credentials and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [61, 88, 63, 67, 68, 58, 55, 65],
    external: null,
    signature: "research::credentials::portfolio::5",
  },
  "credential-verification": {
    id: "credential-verification",
    area: "research",
    areaLabel: "Research, Projects & Credentials",
    title: "Credential Verification",
    description: "Verification IDs, status, issuer authority, evidence boundaries and employer checks.",
    group: "Credentials",
    icon: "check",
    layout: "verification",
    primaryAction: "Verify record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Verification checks",
        value: "27",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Valid",
        value: "22",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Revoked",
        value: "39",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Expired",
        value: "93",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CRED-100",
        name: "CAA-AIPB-2026-0184",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 56,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "232",
        evidence: "68%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CRE-2026",
      },
      {
        id: "CRED-101",
        name: "CAA-RAI-2026-0042",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Draft",
        progress: 67,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "249",
        evidence: "75%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CRE-2027",
      },
      {
        id: "CRED-102",
        name: "CAA-DPA-2026-0098",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 78,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "266",
        evidence: "82%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CRE-2028",
      },
      {
        id: "CRED-103",
        name: "CUAA-BAAI-2026-0112",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 89,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "283",
        evidence: "89%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CRE-2029",
      },
      {
        id: "CRED-104",
        name: "CUAA-MSIS-2026-0027",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Review due",
        progress: 55,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "300",
        evidence: "96%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CRE-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Credential Verification actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for credentials and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [59, 87, 84, 72, 59, 66, 89, 74],
    external: null,
    signature: "research::credential-verification::verification::5",
  },
  "student-success-dashboard": {
    id: "student-success-dashboard",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Student Success Overview",
    description: "Engagement, attendance, progression, support, wellbeing and intervention indicators.",
    group: "Student Success",
    icon: "heart",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "85%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "75%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "26",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "39",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "STUD-100",
        name: "Engagement and attendance",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Active",
        progress: 67,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "48",
        evidence: "76%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "STU-2026",
      },
      {
        id: "STUD-101",
        name: "Academic progression",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "On track",
        progress: 78,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "65",
        evidence: "83%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "STU-2027",
      },
      {
        id: "STUD-102",
        name: "Support and advising",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 89,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "82",
        evidence: "90%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "STU-2028",
      },
      {
        id: "STUD-103",
        name: "Wellbeing and safeguarding",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 55,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "99",
        evidence: "97%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "STU-2029",
      },
      {
        id: "STUD-104",
        name: "Accessibility readiness",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 66,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "116",
        evidence: "70%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "STU-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Student Success Overview actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for student success and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [85, 83, 83, 62, 88, 73, 59, 66],
    external: null,
    signature: "success::student-success-dashboard::scorecard::5",
  },
  advising: {
    id: "advising",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Academic Advising",
    description: "Advisor caseloads, plans, referrals, appointments and documented next actions.",
    group: "Support",
    icon: "message",
    layout: "advising",
    primaryAction: "Create advising plan",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active advisees",
        value: "8",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Appointments",
        value: "67",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Plans updated",
        value: "42",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Referrals",
        value: "11",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Advising plan",
      },
      {
        key: "owner",
        label: "Advisor",
      },
      {
        key: "progress",
        label: "Plan progress",
      },
      {
        key: "date",
        label: "Next meeting",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ADVI-100",
        name: "Daniel Okello success plan",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Urgent",
        progress: 72,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "293",
        evidence: "77%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ADV-2026",
      },
      {
        id: "ADVI-101",
        name: "Peter Mugisha recovery plan",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 83,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "310",
        evidence: "84%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ADV-2027",
      },
      {
        id: "ADVI-102",
        name: "Fatima Al-Hassan research plan",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 94,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "327",
        evidence: "91%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ADV-2028",
      },
      {
        id: "ADVI-103",
        name: "Amina Nanyonga career plan",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 60,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "344",
        evidence: "64%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ADV-2029",
      },
      {
        id: "ADVI-104",
        name: "BAAI Year 1 group advising",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 71,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "361",
        evidence: "71%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ADV-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Academic Advising actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for support and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [78, 60, 87, 84, 62, 88, 63, 78],
    external: null,
    signature: "success::advising::advising::5",
  },
  "risk-interventions": {
    id: "risk-interventions",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Risk & Interventions",
    description: "Privacy-aware signals, triage, named owners, consent, action plans and follow-up.",
    group: "Support",
    icon: "alert",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "46",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "23",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "86",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "80%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RISK-100",
        name: "Peter Mugisha attendance risk",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Urgent",
        progress: 89,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "175",
        evidence: "73%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RIS-2026",
      },
      {
        id: "RISK-101",
        name: "Daniel Okello progression risk",
        owner: "University Library",
        scope: "School of Computing",
        status: "Review due",
        progress: 55,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "192",
        evidence: "80%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RIS-2027",
      },
      {
        id: "RISK-102",
        name: "Assessment non-submission cluster",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 66,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "209",
        evidence: "87%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RIS-2028",
      },
      {
        id: "RISK-103",
        name: "Labs access barrier",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 77,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "226",
        evidence: "94%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RIS-2029",
      },
      {
        id: "RISK-104",
        name: "Financial hold escalation",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 88,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "243",
        evidence: "67%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RIS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Risk & Interventions actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for support and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [86, 94, 75, 81, 82, 93, 64, 88],
    external: null,
    signature: "success::risk-interventions::queue::5",
  },
  wellbeing: {
    id: "wellbeing",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Wellbeing & Safeguarding",
    description: "Protected support pathways, urgent escalation, safeguarding and minimum necessary access.",
    group: "Support",
    icon: "shield",
    layout: "wellbeing",
    primaryAction: "Open protected case",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open cases",
        value: "22",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Urgent",
        value: "4",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned",
        value: "67",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Follow-ups due",
        value: "18",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Protected case",
      },
      {
        key: "owner",
        label: "Named owner",
      },
      {
        key: "risk",
        label: "Severity",
      },
      {
        key: "updated",
        label: "Next action",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "WELL-100",
        name: "Urgent safeguarding pathway",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Urgent",
        progress: 88,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "309",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "WEL-2026",
      },
      {
        id: "WELL-101",
        name: "Counselling referral",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 54,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "326",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "WEL-2027",
      },
      {
        id: "WELL-102",
        name: "Wellbeing check-in",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 65,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "343",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "WEL-2028",
      },
      {
        id: "WELL-103",
        name: "Harassment report",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 76,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "360",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "WEL-2029",
      },
      {
        id: "WELL-104",
        name: "Crisis-support coordination",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 87,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "377",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "WEL-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Wellbeing & Safeguarding actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for support and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [94, 85, 94, 85, 83, 62, 56, 76],
    external: null,
    signature: "success::wellbeing::wellbeing::5",
  },
  accessibility: {
    id: "accessibility",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Accessibility & Accommodations",
    description:
      "Approved adjustments, accessible materials, Class captions, assessment arrangements and Labs access.",
    group: "Inclusion",
    icon: "accessibility",
    layout: "wellbeing",
    primaryAction: "Open protected case",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open cases",
        value: "90",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Urgent",
        value: "84",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned",
        value: "31",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Follow-ups due",
        value: "16",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Protected case",
      },
      {
        key: "owner",
        label: "Named owner",
      },
      {
        key: "risk",
        label: "Severity",
      },
      {
        key: "updated",
        label: "Next action",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACCE-100",
        name: "Screen-reader accommodation",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Urgent",
        progress: 58,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "219",
        evidence: "85%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACC-2026",
      },
      {
        id: "ACCE-101",
        name: "Live captions and language",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 69,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "236",
        evidence: "92%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACC-2027",
      },
      {
        id: "ACCE-102",
        name: "Extended assessment time",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 80,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "253",
        evidence: "65%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACC-2028",
      },
      {
        id: "ACCE-103",
        name: "Accessible course materials",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 91,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "270",
        evidence: "72%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACC-2029",
      },
      {
        id: "ACCE-104",
        name: "Labs input adaptation",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 57,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "287",
        evidence: "79%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Accessibility & Accommodations actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for inclusion and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [88, 84, 72, 59, 87, 63, 88, 73],
    external: null,
    signature: "success::accessibility::wellbeing::5",
  },
  "scholarships-aid": {
    id: "scholarships-aid",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Scholarships & Student Aid",
    description:
      "Eligibility, applications, awards, disbursement milestones and protected finance boundaries.",
    group: "Funding",
    icon: "wallet",
    layout: "aid",
    primaryAction: "Create funding cycle",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Applications",
        value: "39",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Eligible",
        value: "87",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Awarded",
        value: "61",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Disbursement due",
        value: "6",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Fund / application",
      },
      {
        key: "owner",
        label: "Finance owner",
      },
      {
        key: "people",
        label: "Beneficiaries",
      },
      {
        key: "progress",
        label: "Disbursed",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SCHO-100",
        name: "AI Futures Scholarship",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Urgent",
        progress: 67,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "378",
        evidence: "90%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SCH-2026",
      },
      {
        id: "SCHO-101",
        name: "Women in Responsible AI Fund",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Review due",
        progress: 78,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "395",
        evidence: "97%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SCH-2027",
      },
      {
        id: "SCHO-102",
        name: "Regional Access Grant",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 89,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "412",
        evidence: "70%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SCH-2028",
      },
      {
        id: "SCHO-103",
        name: "Disability Support Grant",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 55,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "429",
        evidence: "77%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SCH-2029",
      },
      {
        id: "SCHO-104",
        name: "Research Excellence Award",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 66,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "26",
        evidence: "84%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SCH-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Scholarships & Student Aid actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for funding and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [79, 61, 77, 81, 93, 85, 62, 67],
    external: null,
    signature: "success::scholarships-aid::aid::5",
  },
  "career-readiness": {
    id: "career-readiness",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Career Readiness",
    description: "Skills evidence, portfolios, placements, mentoring, interviews and employer validation.",
    group: "Progression",
    icon: "briefcase",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "48",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "17",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "33",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "40",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CARE-100",
        name: "Kijani portfolio review",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Draft",
        progress: 84,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "335",
        evidence: "93%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CAR-2026",
      },
      {
        id: "CARE-101",
        name: "Employer mock interview",
        owner: "University Library",
        scope: "School of Computing",
        status: "Active",
        progress: 95,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "352",
        evidence: "66%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CAR-2027",
      },
      {
        id: "CARE-102",
        name: "Skills evidence audit",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 61,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "369",
        evidence: "73%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CAR-2028",
      },
      {
        id: "CARE-103",
        name: "Industry placement match",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 72,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "386",
        evidence: "80%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CAR-2029",
      },
      {
        id: "CARE-104",
        name: "Career mentoring plan",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "Ready",
        progress: 83,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "403",
        evidence: "87%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CAR-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Career Readiness actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for progression and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [78, 71, 69, 90, 95, 75, 60, 87],
    external: null,
    signature: "success::career-readiness::directory::5",
  },
  alumni: {
    id: "alumni",
    area: "success",
    areaLabel: "Student Success & Wellbeing",
    title: "Alumni & Continuing Learning",
    description: "Alumni records, verified credentials, continuing programs and community participation.",
    group: "Community",
    icon: "users",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "5",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "9",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "50",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "29",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ALUM-100",
        name: "BAAI Alumni Network",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 89,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "250",
        evidence: "96%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ALU-2026",
      },
      {
        id: "ALUM-101",
        name: "Responsible AI Community",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Draft",
        progress: 55,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "267",
        evidence: "69%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ALU-2027",
      },
      {
        id: "ALUM-102",
        name: "Graduate verification requests",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 66,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "284",
        evidence: "76%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ALU-2028",
      },
      {
        id: "ALUM-103",
        name: "Continuing learning offers",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 77,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "301",
        evidence: "83%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ALU-2029",
      },
      {
        id: "ALUM-104",
        name: "Alumni mentor pool",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Review due",
        progress: 88,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "318",
        evidence: "90%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ALU-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Alumni & Continuing Learning actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for community and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [77, 71, 59, 66, 68, 68, 79, 61],
    external: null,
    signature: "success::alumni::directory::5",
  },
  accreditation: {
    id: "accreditation",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Accreditation",
    description: "Standards, evidence, owners, visits, findings, actions and renewal timelines.",
    group: "Quality Assurance",
    icon: "award",
    layout: "evidence",
    primaryAction: "Request review",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Evidence items",
        value: "60",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Verified",
        value: "88",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending",
        value: "14",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Rejected",
        value: "21",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Evidence / credential",
      },
      {
        key: "owner",
        label: "Reviewer",
      },
      {
        key: "evidence",
        label: "Completeness",
      },
      {
        key: "decision",
        label: "Decision",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ACCR-100",
        name: "National Council BAAI review",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Urgent",
        progress: 53,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "19",
        evidence: "71%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ACC-2026",
      },
      {
        id: "ACCR-101",
        name: "Professional AI Standards CRAI",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 64,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "36",
        evidence: "78%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ACC-2027",
      },
      {
        id: "ACCR-102",
        name: "Engineering Council MSIS",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 75,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "53",
        evidence: "85%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ACC-2028",
      },
      {
        id: "ACCR-103",
        name: "Institutional Quality Review",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 86,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "70",
        evidence: "92%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ACC-2029",
      },
      {
        id: "ACCR-104",
        name: "Digital Learning Compliance",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 52,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "87",
        evidence: "65%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ACC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Accreditation actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for quality assurance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [56, 76, 70, 79, 82, 72, 59, 87],
    external: null,
    signature: "quality::accreditation::evidence::5",
  },
  "quality-reviews": {
    id: "quality-reviews",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Quality Reviews",
    description: "Program, course, teaching, assessment and student-experience review cycles.",
    group: "Quality Assurance",
    icon: "search",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "39",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "11",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "32",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "88%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "QUAL-100",
        name: "BAAI annual program review",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Urgent",
        progress: 57,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "428",
        evidence: "74%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "QUA-2026",
      },
      {
        id: "QUAL-101",
        name: "Class teaching-quality review",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Review due",
        progress: 68,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "25",
        evidence: "81%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "QUA-2027",
      },
      {
        id: "QUAL-102",
        name: "Labs practical-learning review",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 79,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "42",
        evidence: "88%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "QUA-2028",
      },
      {
        id: "QUAL-103",
        name: "Assessment quality review",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 90,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "59",
        evidence: "95%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "QUA-2029",
      },
      {
        id: "QUAL-104",
        name: "Student-experience review",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 56,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "76",
        evidence: "68%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "QUA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Quality Reviews actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for quality assurance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [87, 73, 91, 74, 80, 92, 64, 67],
    external: null,
    signature: "quality::quality-reviews::queue::5",
  },
  "outcomes-mapping": {
    id: "outcomes-mapping",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Outcomes Mapping",
    description: "Institution, program, course, assessment, Lab and credential outcome coverage.",
    group: "Quality Assurance",
    icon: "map",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "86%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "51",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "64",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "65",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "OUTC-100",
        name: "Institution graduate attributes",
        owner: "Research Office",
        scope: "Whole institution",
        status: "On track",
        progress: 77,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "283",
        evidence: "91%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "OUT-2026",
      },
      {
        id: "OUTC-101",
        name: "BAAI program outcomes",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 88,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "300",
        evidence: "64%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "OUT-2027",
      },
      {
        id: "OUTC-102",
        name: "RAI 304 course outcomes",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 54,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "317",
        evidence: "71%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "OUT-2028",
      },
      {
        id: "OUTC-103",
        name: "Labs practical outcomes",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 65,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "334",
        evidence: "78%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "OUT-2029",
      },
      {
        id: "OUTC-104",
        name: "Credential evidence outcomes",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 76,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "351",
        evidence: "85%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "OUT-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Outcomes Mapping actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for quality assurance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [68, 58, 87, 84, 72, 59, 87, 63],
    external: null,
    signature: "quality::outcomes-mapping::matrix::5",
  },
  "policy-centre": {
    id: "policy-centre",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Policy Centre",
    description: "Approved academic, assessment, data, AI, safeguarding and operational policies.",
    group: "Governance",
    icon: "book",
    layout: "policy",
    primaryAction: "Create policy version",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active controls",
        value: "70",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Acknowledged",
        value: "60",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Exceptions",
        value: "53",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Review due",
        value: "12",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Policy / control",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Next review",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "POLI-100",
        name: "Academic integrity policy",
        owner: "Research Office",
        scope: "Whole institution",
        status: "On track",
        progress: 62,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "403",
        evidence: "69%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "POL-2026",
      },
      {
        id: "POLI-101",
        name: "Assessment and moderation policy",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 73,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "420",
        evidence: "76%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "POL-2027",
      },
      {
        id: "POLI-102",
        name: "Responsible AI policy",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 84,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "437",
        evidence: "83%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "POL-2028",
      },
      {
        id: "POLI-103",
        name: "Safeguarding policy",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 95,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "34",
        evidence: "90%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "POL-2029",
      },
      {
        id: "POLI-104",
        name: "Data protection and retention",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 61,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "51",
        evidence: "97%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "POL-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Policy Centre actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [62, 67, 58, 87, 73, 91, 64, 67],
    external: null,
    signature: "quality::policy-centre::policy::5",
  },
  compliance: {
    id: "compliance",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Compliance",
    description: "Regulatory obligations, controls, attestations, evidence and exceptions.",
    group: "Governance",
    icon: "check",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "60",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "59",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "18",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "89%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COMP-100",
        name: "Higher-education licence",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Urgent",
        progress: 72,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "173",
        evidence: "71%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COM-2026",
      },
      {
        id: "COMP-101",
        name: "Data-protection controls",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 83,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "190",
        evidence: "78%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COM-2027",
      },
      {
        id: "COMP-102",
        name: "Accessibility standard",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 94,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "207",
        evidence: "85%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COM-2028",
      },
      {
        id: "COMP-103",
        name: "Research ethics compliance",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 60,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "224",
        evidence: "92%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COM-2029",
      },
      {
        id: "COMP-104",
        name: "Financial-aid conditions",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 71,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "241",
        evidence: "65%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COM-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Compliance actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [84, 72, 59, 56, 76, 81, 93, 64],
    external: null,
    signature: "quality::compliance::queue::5",
  },
  "audit-log": {
    id: "audit-log",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Audit Log",
    description:
      "Attributable activity, decisions, permission changes, exports and connected-platform handoffs.",
    group: "Governance",
    icon: "history",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "95",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "96",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "14",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "33",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "AUDI-100",
        name: "Role assignment change",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 74,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "40",
        evidence: "78%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "AUD-2026",
      },
      {
        id: "AUDI-101",
        name: "Class handoff created",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Draft",
        progress: 85,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "57",
        evidence: "85%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "AUD-2027",
      },
      {
        id: "AUDI-102",
        name: "Labs entitlement revoked",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 96,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "74",
        evidence: "92%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "AUD-2028",
      },
      {
        id: "AUDI-103",
        name: "Assessment result published",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 62,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "91",
        evidence: "65%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "AUD-2029",
      },
      {
        id: "AUDI-104",
        name: "Data export downloaded",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Review due",
        progress: 73,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "108",
        evidence: "72%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "AUD-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Audit Log actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [77, 60, 66, 57, 76, 81, 93, 75],
    external: null,
    signature: "quality::audit-log::table::5",
  },
  "privacy-consent": {
    id: "privacy-consent",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Privacy & Consent",
    description: "Purpose, lawful basis, consent, data scope, access, retention and subject rights.",
    group: "Data Governance",
    icon: "lock",
    layout: "policy",
    primaryAction: "Create policy version",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active controls",
        value: "8",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Acknowledged",
        value: "73",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Exceptions",
        value: "54",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Review due",
        value: "70",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Policy / control",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Next review",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PRIV-100",
        name: "Student analytics purpose",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Draft",
        progress: 78,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "59",
        evidence: "93%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PRI-2026",
      },
      {
        id: "PRIV-101",
        name: "Class recording consent",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Active",
        progress: 89,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "76",
        evidence: "66%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PRI-2027",
      },
      {
        id: "PRIV-102",
        name: "Labs evidence return consent",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 55,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "93",
        evidence: "73%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PRI-2028",
      },
      {
        id: "PRIV-103",
        name: "Research participant consent",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 66,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "110",
        evidence: "80%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PRI-2029",
      },
      {
        id: "PRIV-104",
        name: "Alumni communication preference",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 77,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "127",
        evidence: "87%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PRI-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Privacy & Consent actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for data governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [96, 86, 83, 72, 80, 82, 82, 82],
    external: null,
    signature: "quality::privacy-consent::policy::5",
  },
  "responsible-ai": {
    id: "responsible-ai",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Responsible AI",
    description:
      "AI use boundaries, human authority, evaluation, transparency, escalation and emergency stop.",
    group: "AI Governance",
    icon: "bot",
    layout: "policy",
    primaryAction: "Create policy version",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active controls",
        value: "96",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Acknowledged",
        value: "91",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Exceptions",
        value: "73",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Review due",
        value: "72",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Policy / control",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Next review",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RESP-100",
        name: "Human decision authority",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Draft",
        progress: 69,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "275",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RES-2026",
      },
      {
        id: "RESP-101",
        name: "AI-agent access boundaries",
        owner: "University Library",
        scope: "School of Computing",
        status: "Active",
        progress: 80,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "292",
        evidence: "90%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RES-2027",
      },
      {
        id: "RESP-102",
        name: "Model and prompt lineage",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 91,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "309",
        evidence: "97%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RES-2028",
      },
      {
        id: "RESP-103",
        name: "Evaluation and monitoring",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 57,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "326",
        evidence: "70%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RES-2029",
      },
      {
        id: "RESP-104",
        name: "Emergency stop and escalation",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "Ready",
        progress: 68,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "343",
        evidence: "77%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RES-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Responsible AI actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for ai governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [60, 77, 71, 69, 69, 69, 90, 63],
    external: null,
    signature: "quality::responsible-ai::policy::5",
  },
  incidents: {
    id: "incidents",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Incidents & Safeguarding",
    description: "Incident intake, severity, containment, communication, evidence and post-incident review.",
    group: "Risk",
    icon: "alert",
    layout: "queue",
    primaryAction: "Create work item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Open work",
        value: "85",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Due today",
        value: "78",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Assigned owners",
        value: "25",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "SLA health",
        value: "84%",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Work item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "date",
        label: "Due",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "INCI-100",
        name: "Labs data-scope incident",
        owner: "University Library",
        scope: "Whole institution",
        status: "Urgent",
        progress: 65,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "436",
        evidence: "86%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "INC-2026",
      },
      {
        id: "INCI-101",
        name: "Class recording access issue",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "Review due",
        progress: 76,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "33",
        evidence: "93%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "INC-2027",
      },
      {
        id: "INCI-102",
        name: "Assessment material exposure",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 87,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "50",
        evidence: "66%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "INC-2028",
      },
      {
        id: "INCI-103",
        name: "Safeguarding escalation",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 53,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "67",
        evidence: "73%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "INC-2029",
      },
      {
        id: "INCI-104",
        name: "Identity-provider anomaly",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 64,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "84",
        evidence: "80%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "INC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Incidents & Safeguarding actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for risk and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [95, 75, 70, 90, 95, 96, 86, 62],
    external: null,
    signature: "quality::incidents::queue::5",
  },
  "retention-deletion": {
    id: "retention-deletion",
    area: "quality",
    areaLabel: "Quality, Accreditation & Governance",
    title: "Retention & Deletion",
    description: "Record classes, retention schedules, legal holds, deletion workflows and evidence.",
    group: "Data Governance",
    icon: "trash",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "65",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "18",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "56",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "35",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "RETE-100",
        name: "Academic record schedule",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Review due",
        progress: 57,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "368",
        evidence: "66%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "RET-2026",
      },
      {
        id: "RETE-101",
        name: "Class recording schedule",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Ready",
        progress: 68,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "385",
        evidence: "73%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "RET-2027",
      },
      {
        id: "RETE-102",
        name: "Labs run evidence schedule",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 79,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "402",
        evidence: "80%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "RET-2028",
      },
      {
        id: "RETE-103",
        name: "Support case schedule",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 90,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "419",
        evidence: "87%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "RET-2029",
      },
      {
        id: "RETE-104",
        name: "External reviewer data deletion",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Active",
        progress: 56,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "436",
        evidence: "94%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "RET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Retention & Deletion actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for data governance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [69, 79, 92, 64, 88, 73, 59, 87],
    external: null,
    signature: "quality::retention-deletion::table::5",
  },
  "executive-analytics": {
    id: "executive-analytics",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Executive Analytics",
    description: "Academic outcomes, student success, quality, capacity, finance and platform adoption.",
    group: "Analytics",
    icon: "chart",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "76%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "70%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "60",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "12",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "EXEC-100",
        name: "Academic outcomes",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Active",
        progress: 94,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "60",
        evidence: "78%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "EXE-2026",
      },
      {
        id: "EXEC-101",
        name: "Student success",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "On track",
        progress: 60,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "77",
        evidence: "85%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "EXE-2027",
      },
      {
        id: "EXEC-102",
        name: "Quality and accreditation",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 71,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "94",
        evidence: "92%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "EXE-2028",
      },
      {
        id: "EXEC-103",
        name: "Capacity and workload",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 82,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "111",
        evidence: "65%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "EXE-2029",
      },
      {
        id: "EXEC-104",
        name: "Finance and adoption",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 93,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "128",
        evidence: "72%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "EXE-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Executive Analytics actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [55, 86, 94, 85, 94, 64, 78, 92],
    external: null,
    signature: "analytics::executive-analytics::scorecard::5",
  },
  "enrolment-analytics": {
    id: "enrolment-analytics",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Admissions & Enrolment",
    description: "Applications, offers, enrolments, yield, capacity and demographic fairness indicators.",
    group: "Analytics",
    icon: "users",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "76%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "91%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "86",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "14",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ENRO-100",
        name: "Applications",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Active",
        progress: 94,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "270",
        evidence: "90%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ENR-2026",
      },
      {
        id: "ENRO-101",
        name: "Offers",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "On track",
        progress: 60,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "287",
        evidence: "97%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ENR-2027",
      },
      {
        id: "ENRO-102",
        name: "Accepted offers",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 71,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "304",
        evidence: "70%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ENR-2028",
      },
      {
        id: "ENRO-103",
        name: "Enrolments",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 82,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "321",
        evidence: "77%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ENR-2029",
      },
      {
        id: "ENRO-104",
        name: "Yield and capacity",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 93,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "338",
        evidence: "84%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ENR-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Admissions & Enrolment actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [55, 55, 76, 60, 56, 55, 76, 91],
    external: null,
    signature: "analytics::enrolment-analytics::scorecard::5",
  },
  "learning-outcomes": {
    id: "learning-outcomes",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Learning Outcomes",
    description: "Outcome attainment, evidence coverage, course performance and improvement actions.",
    group: "Analytics",
    icon: "target",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "80%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "91%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "48",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "93",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LEAR-100",
        name: "Program outcomes",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 62,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "238",
        evidence: "64%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LEA-2026",
      },
      {
        id: "LEAR-101",
        name: "Course outcomes",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Draft",
        progress: 73,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "255",
        evidence: "71%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LEA-2027",
      },
      {
        id: "LEAR-102",
        name: "Assessment evidence",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 84,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "272",
        evidence: "78%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LEA-2028",
      },
      {
        id: "LEAR-103",
        name: "Labs practical evidence",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 95,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "289",
        evidence: "85%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LEA-2029",
      },
      {
        id: "LEAR-104",
        name: "Credential readiness",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Review due",
        progress: 61,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "306",
        evidence: "92%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LEA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Learning Outcomes actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [65, 68, 89, 95, 86, 62, 77, 92],
    external: null,
    signature: "analytics::learning-outcomes::scorecard::5",
  },
  "attendance-engagement": {
    id: "attendance-engagement",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Attendance & Engagement",
    description: "Class, Build Room and Labs participation with privacy-aware thresholds.",
    group: "Analytics",
    icon: "pulse",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "78%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "71%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "4",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "20",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "ATTE-100",
        name: "Chatoner Class attendance",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Review due",
        progress: 69,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "50",
        evidence: "86%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "ATT-2026",
      },
      {
        id: "ATTE-101",
        name: "Build Room participation",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Ready",
        progress: 80,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "67",
        evidence: "93%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "ATT-2027",
      },
      {
        id: "ATTE-102",
        name: "Labs participation",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 91,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "84",
        evidence: "66%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "ATT-2028",
      },
      {
        id: "ATTE-103",
        name: "Assignment activity",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 57,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "101",
        evidence: "73%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "ATT-2029",
      },
      {
        id: "ATTE-104",
        name: "Advising engagement",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Active",
        progress: 68,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "118",
        evidence: "80%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "ATT-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Attendance & Engagement actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [87, 63, 67, 89, 74, 59, 66, 68],
    external: null,
    signature: "analytics::attendance-engagement::scorecard::5",
  },
  "completion-progression": {
    id: "completion-progression",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Completion & Progression",
    description: "Retention, progression, completion forecasts, award readiness and time to completion.",
    group: "Analytics",
    icon: "path",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "71%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "76%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "38",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "16",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "COMP-100",
        name: "Retention",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "On track",
        progress: 80,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "61",
        evidence: "83%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "COM-2026",
      },
      {
        id: "COMP-101",
        name: "Progression",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 91,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "78",
        evidence: "90%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "COM-2027",
      },
      {
        id: "COMP-102",
        name: "Completion forecast",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 57,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "95",
        evidence: "97%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "COM-2028",
      },
      {
        id: "COMP-103",
        name: "Award readiness",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 68,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "112",
        evidence: "70%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "COM-2029",
      },
      {
        id: "COMP-104",
        name: "Time to completion",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Draft",
        progress: 79,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "129",
        evidence: "77%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "COM-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Completion & Progression actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [56, 86, 73, 59, 66, 78, 60, 56],
    external: null,
    signature: "analytics::completion-progression::scorecard::5",
  },
  "faculty-workload": {
    id: "faculty-workload",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Faculty Workload",
    description: "Teaching, advising, assessment, supervision and review workload with capacity alerts.",
    group: "Analytics",
    icon: "briefcase",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "73%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "80%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "67",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "12",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "FACU-100",
        name: "Teaching load",
        owner: "University Library",
        scope: "Whole institution",
        status: "Active",
        progress: 55,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "366",
        evidence: "90%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "FAC-2026",
      },
      {
        id: "FACU-101",
        name: "Assessment load",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "On track",
        progress: 66,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "383",
        evidence: "97%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "FAC-2027",
      },
      {
        id: "FACU-102",
        name: "Advising load",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 77,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "400",
        evidence: "70%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "FAC-2028",
      },
      {
        id: "FACU-103",
        name: "Research supervision",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 88,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "417",
        evidence: "77%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "FAC-2029",
      },
      {
        id: "FACU-104",
        name: "Quality review load",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 54,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "434",
        evidence: "84%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "FAC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Faculty Workload actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [67, 58, 87, 63, 67, 68, 68, 68],
    external: null,
    signature: "analytics::faculty-workload::scorecard::5",
  },
  "platform-activity": {
    id: "platform-activity",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Class, Labs & Build Rooms",
    description: "Cross-platform aggregate usage, handoff success, evidence return and service health.",
    group: "Analytics",
    icon: "network",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "92%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "96%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "7",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "26",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PLAT-100",
        name: "Academy activity",
        owner: "Digital Learning",
        scope: "Whole institution",
        status: "On track",
        progress: 65,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "61",
        evidence: "97%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PLA-2026",
      },
      {
        id: "PLAT-101",
        name: "Class handoff success",
        owner: "Student Success",
        scope: "School of Computing",
        status: "Review due",
        progress: 76,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "78",
        evidence: "70%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PLA-2027",
      },
      {
        id: "PLAT-102",
        name: "Labs evidence return",
        owner: "Research Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 87,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "95",
        evidence: "77%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PLA-2028",
      },
      {
        id: "PLAT-103",
        name: "Build Room review activity",
        owner: "Finance Office",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 53,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "112",
        evidence: "84%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PLA-2029",
      },
      {
        id: "PLAT-104",
        name: "Integration health",
        owner: "Information Security",
        scope: "Assigned cases",
        status: "Draft",
        progress: 64,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "129",
        evidence: "91%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PLA-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Class, Labs & Build Rooms actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for analytics and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [56, 65, 57, 66, 89, 74, 80, 82],
    external: null,
    signature: "analytics::platform-activity::scorecard::5",
  },
  "report-builder": {
    id: "report-builder",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Report Builder",
    description: "Compose governed reports with filters, minimum-group thresholds and export controls.",
    group: "Reporting",
    icon: "sliders",
    layout: "analytics",
    primaryAction: "Generate report",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Measures",
        value: "62",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Filters",
        value: "71",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Preview groups",
        value: "30",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Export controls",
        value: "10",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "REPO-100",
        name: "Executive monthly report",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Draft",
        progress: 63,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "239",
        evidence: "97%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "REP-2026",
      },
      {
        id: "REPO-101",
        name: "Program quality report",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Active",
        progress: 74,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "256",
        evidence: "70%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "REP-2027",
      },
      {
        id: "REPO-102",
        name: "Student success report",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 85,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "273",
        evidence: "77%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "REP-2028",
      },
      {
        id: "REPO-103",
        name: "Class and Labs adoption",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 96,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "290",
        evidence: "84%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "REP-2029",
      },
      {
        id: "REPO-104",
        name: "Accreditation evidence pack",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 62,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "307",
        evidence: "91%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "REP-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Report Builder actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for reporting and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [66, 68, 79, 82, 82, 93, 64, 57],
    external: null,
    signature: "analytics::report-builder::analytics::5",
  },
  "scheduled-reports": {
    id: "scheduled-reports",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Scheduled Reports",
    description: "Audience, frequency, secure delivery, expiry, owners and audit history.",
    group: "Reporting",
    icon: "clock",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "96",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "55",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "41",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "5",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SCHE-100",
        name: "Senate monthly dashboard",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Ready",
        progress: 73,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "99",
        evidence: "75%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SCH-2026",
      },
      {
        id: "SCHE-101",
        name: "Registrar enrolment report",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 84,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "116",
        evidence: "82%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SCH-2027",
      },
      {
        id: "SCHE-102",
        name: "Dean workload report",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 95,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "133",
        evidence: "89%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SCH-2028",
      },
      {
        id: "SCHE-103",
        name: "Quality evidence digest",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 61,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "150",
        evidence: "96%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SCH-2029",
      },
      {
        id: "SCHE-104",
        name: "Finance capacity report",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 72,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "167",
        evidence: "69%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SCH-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Scheduled Reports actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for reporting and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [94, 96, 75, 81, 61, 56, 55, 76],
    external: null,
    signature: "analytics::scheduled-reports::table::5",
  },
  "benchmarking-forecasts": {
    id: "benchmarking-forecasts",
    area: "analytics",
    areaLabel: "Analytics & Reports",
    title: "Benchmarking & Forecasts",
    description: "Internal trends, approved benchmarks, scenario assumptions and capacity forecasts.",
    group: "Planning",
    icon: "sparkles",
    layout: "scorecard",
    primaryAction: "Open drill-down",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Current score",
        value: "93%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Target",
        value: "83%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Trend",
        value: "42",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Exceptions",
        value: "34",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "BENC-100",
        name: "Enrolment forecast",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Review due",
        progress: 84,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "350",
        evidence: "94%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "BEN-2026",
      },
      {
        id: "BENC-101",
        name: "Instructor capacity forecast",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Ready",
        progress: 95,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "367",
        evidence: "67%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "BEN-2027",
      },
      {
        id: "BENC-102",
        name: "Labs capacity forecast",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 61,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "384",
        evidence: "74%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "BEN-2028",
      },
      {
        id: "BENC-103",
        name: "Completion forecast",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 72,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "401",
        evidence: "81%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "BEN-2029",
      },
      {
        id: "BENC-104",
        name: "Budget scenario",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Active",
        progress: 83,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "418",
        evidence: "88%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "BEN-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Benchmarking & Forecasts actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for planning and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [93, 75, 91, 95, 96, 86, 62, 88],
    external: null,
    signature: "analytics::benchmarking-forecasts::scorecard::5",
  },
  library: {
    id: "library",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Library & Repository",
    description: "Learning resources, institutional repository, licences, access and usage.",
    group: "Academic Resources",
    icon: "library",
    layout: "table",
    primaryAction: "Add record",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "40",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Ready",
        value: "52",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs review",
        value: "94",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Updated today",
        value: "38",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Record",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "updated",
        label: "Updated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LIBR-100",
        name: "Responsible AI collection",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "Ready",
        progress: 88,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "279",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LIB-2026",
      },
      {
        id: "LIBR-101",
        name: "Applied AI journal licences",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 54,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "296",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LIB-2027",
      },
      {
        id: "LIBR-102",
        name: "Institutional repository",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 65,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "313",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LIB-2028",
      },
      {
        id: "LIBR-103",
        name: "Accessible media collection",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 76,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "330",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LIB-2029",
      },
      {
        id: "LIBR-104",
        name: "Research datasets",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "On track",
        progress: 87,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "347",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LIB-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Library & Repository actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for academic resources and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [64, 78, 92, 95, 86, 73, 70, 58],
    external: null,
    signature: "resources::library::table::5",
  },
  facilities: {
    id: "facilities",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Facilities & Learning Spaces",
    description: "Rooms, studios, accessibility, equipment, capacity and maintenance.",
    group: "Operations",
    icon: "building",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "18",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "80",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "78",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "25",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "FACI-100",
        name: "Kampala AI Studio",
        owner: "Information Security",
        scope: "Whole institution",
        status: "Ready",
        progress: 79,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "195",
        evidence: "75%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "FAC-2026",
      },
      {
        id: "FACI-101",
        name: "Jinja Learning Lab",
        owner: "University Library",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 90,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "212",
        evidence: "82%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "FAC-2027",
      },
      {
        id: "FACI-102",
        name: "Mbarara Seminar Hall",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 56,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "229",
        evidence: "89%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "FAC-2028",
      },
      {
        id: "FACI-103",
        name: "Accessible Assessment Centre",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 67,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "246",
        evidence: "96%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "FAC-2029",
      },
      {
        id: "FACI-104",
        name: "Media Production Studio",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "On track",
        progress: 78,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "263",
        evidence: "69%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "FAC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Facilities & Learning Spaces actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for operations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [64, 78, 92, 64, 78, 92, 85, 83],
    external: null,
    signature: "resources::facilities::directory::5",
  },
  "technology-assets": {
    id: "technology-assets",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Technology & Devices",
    description: "Devices, software entitlements, lending, support and responsible-use controls.",
    group: "Operations",
    icon: "monitor",
    layout: "directory",
    primaryAction: "Add or invite",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Active records",
        value: "95",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Online / available",
        value: "96",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Needs attention",
        value: "20",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Access reviewed",
        value: "74",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Person / entity",
      },
      {
        key: "code",
        label: "Identifier",
      },
      {
        key: "scope",
        label: "Unit / scope",
      },
      {
        key: "owner",
        label: "Responsible office",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "TECH-100",
        name: "Learner laptop pool",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 92,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "28",
        evidence: "90%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "TEC-2026",
      },
      {
        id: "TECH-101",
        name: "Assistive technology kit",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "Draft",
        progress: 58,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "45",
        evidence: "97%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "TEC-2027",
      },
      {
        id: "TECH-102",
        name: "GPU workstation cluster",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 69,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "62",
        evidence: "70%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "TEC-2028",
      },
      {
        id: "TECH-103",
        name: "Classroom capture devices",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "79",
        evidence: "77%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "TEC-2029",
      },
      {
        id: "TECH-104",
        name: "Secure examination laptops",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Review due",
        progress: 91,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "96",
        evidence: "84%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "TEC-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Technology & Devices actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for operations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [65, 78, 60, 77, 60, 77, 60, 56],
    external: null,
    signature: "resources::technology-assets::directory::5",
  },
  "funding-budgets": {
    id: "funding-budgets",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Funding & Budgets",
    description: "Program budgets, departments, cost centres, commitments and utilization.",
    group: "Finance",
    icon: "wallet",
    layout: "finance",
    primaryAction: "Create finance item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Budget / invoice items",
        value: "45",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Committed",
        value: "29",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Available",
        value: "28",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Approvals due",
        value: "39",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Budget / transaction",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Cost centre",
      },
      {
        key: "progress",
        label: "Utilisation",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "FUND-100",
        name: "BAAI delivery budget",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Review due",
        progress: 54,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "80",
        evidence: "70%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "FUN-2026",
      },
      {
        id: "FUND-101",
        name: "Digital Learning budget",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Ready",
        progress: 65,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "97",
        evidence: "77%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "FUN-2027",
      },
      {
        id: "FUND-102",
        name: "AI Labs capacity budget",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 76,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "114",
        evidence: "84%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "FUN-2028",
      },
      {
        id: "FUND-103",
        name: "Student support budget",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 87,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "131",
        evidence: "91%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "FUN-2029",
      },
      {
        id: "FUND-104",
        name: "Research innovation fund",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Active",
        progress: 53,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "148",
        evidence: "64%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "FUN-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Funding & Budgets actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for finance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [75, 91, 85, 83, 62, 88, 73, 80],
    external: null,
    signature: "resources::funding-budgets::finance::5",
  },
  "scholarships-funding": {
    id: "scholarships-funding",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Scholarships & Grants",
    description: "Institutional funds, donor conditions, research grants and student aid.",
    group: "Finance",
    icon: "gift",
    layout: "finance",
    primaryAction: "Create finance item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Budget / invoice items",
        value: "53",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Committed",
        value: "6",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Available",
        value: "32",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Approvals due",
        value: "28",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Budget / transaction",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Cost centre",
      },
      {
        key: "progress",
        label: "Utilisation",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SCHO-100",
        name: "AI Futures Scholarship Fund",
        owner: "University Library",
        scope: "Whole institution",
        status: "Review due",
        progress: 60,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "356",
        evidence: "92%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SCH-2026",
      },
      {
        id: "SCHO-101",
        name: "Responsible AI Donor Fund",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "Ready",
        progress: 71,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "373",
        evidence: "65%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SCH-2027",
      },
      {
        id: "SCHO-102",
        name: "Regional Access Grant",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 82,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "390",
        evidence: "72%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SCH-2028",
      },
      {
        id: "SCHO-103",
        name: "Research Seed Fund",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 93,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "407",
        evidence: "79%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SCH-2029",
      },
      {
        id: "SCHO-104",
        name: "Accessibility Support Fund",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Active",
        progress: 59,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "424",
        evidence: "86%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SCH-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Scholarships & Grants actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for finance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [57, 55, 76, 81, 82, 72, 80, 71],
    external: null,
    signature: "resources::scholarships-funding::finance::5",
  },
  procurement: {
    id: "procurement",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Procurement",
    description: "Requests, approvals, suppliers, purchase orders and delivery evidence.",
    group: "Finance",
    icon: "cart",
    layout: "finance",
    primaryAction: "Create finance item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Budget / invoice items",
        value: "95",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Committed",
        value: "91",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Available",
        value: "14",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Approvals due",
        value: "38",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Budget / transaction",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Cost centre",
      },
      {
        key: "progress",
        label: "Utilisation",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "PROC-100",
        name: "GPU workstation request",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Review due",
        progress: 63,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "74",
        evidence: "86%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "PRO-2026",
      },
      {
        id: "PROC-101",
        name: "Captioning service renewal",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Ready",
        progress: 74,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "91",
        evidence: "93%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "PRO-2027",
      },
      {
        id: "PROC-102",
        name: "Library database subscription",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 85,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "108",
        evidence: "66%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "PRO-2028",
      },
      {
        id: "PROC-103",
        name: "Accessible device purchase",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 96,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "125",
        evidence: "73%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "PRO-2029",
      },
      {
        id: "PROC-104",
        name: "Classroom microphone replacement",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Active",
        progress: 62,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "142",
        evidence: "80%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "PRO-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Procurement actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for finance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [69, 69, 69, 58, 66, 78, 60, 77],
    external: null,
    signature: "resources::procurement::finance::5",
  },
  billing: {
    id: "billing",
    area: "resources",
    areaLabel: "Resources & Funding",
    title: "Billing & Invoices",
    description: "Academy subscriptions, seats, Class and Labs capacity, invoices and finance contacts.",
    group: "Finance",
    icon: "receipt",
    layout: "finance",
    primaryAction: "Create finance item",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Budget / invoice items",
        value: "27",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Committed",
        value: "39",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Available",
        value: "22",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Approvals due",
        value: "39",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Budget / transaction",
      },
      {
        key: "code",
        label: "Reference",
      },
      {
        key: "owner",
        label: "Cost centre",
      },
      {
        key: "progress",
        label: "Utilisation",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "BILL-100",
        name: "Academy platform subscription",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Review due",
        progress: 78,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "254",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "BIL-2026",
      },
      {
        id: "BILL-101",
        name: "Chatoner Class capacity",
        owner: "Information Security",
        scope: "School of Computing",
        status: "Ready",
        progress: 89,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "271",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "BIL-2027",
      },
      {
        id: "BILL-102",
        name: "AI Labs entitlements",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 55,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "288",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "BIL-2028",
      },
      {
        id: "BILL-103",
        name: "Credential verification service",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 66,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "305",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "BIL-2029",
      },
      {
        id: "BILL-104",
        name: "Professional support package",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Active",
        progress: 77,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "322",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "BIL-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Billing & Invoices actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for finance and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [81, 72, 69, 58, 76, 70, 69, 90],
    external: null,
    signature: "resources::billing::finance::5",
  },
  "connected-apps": {
    id: "connected-apps",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Connected Applications",
    description: "Class, Labs, SIS, LMS, identity, library, finance and approved institutional systems.",
    group: "Integrations",
    icon: "link",
    layout: "connection",
    primaryAction: "Test connection",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connected services",
        value: "96",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Handoff success",
        value: "76%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Scope checks",
        value: "85",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "66",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "CONN-100",
        name: "Chatoner Class",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "Ready",
        progress: 76,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "297",
        evidence: "69%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CON-2026",
      },
      {
        id: "CONN-101",
        name: "Chatoner AI Labs",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 87,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "314",
        evidence: "76%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "CON-2027",
      },
      {
        id: "CONN-102",
        name: "Student Information System",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 53,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "331",
        evidence: "83%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "CON-2028",
      },
      {
        id: "CONN-103",
        name: "Learning Management System",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 64,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "348",
        evidence: "90%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "CON-2029",
      },
      {
        id: "CONN-104",
        name: "Identity Provider",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "On track",
        progress: 75,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "365",
        evidence: "97%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "CON-2030",
      },
      {
        id: "CONN-105",
        name: "Library Platform",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Review due",
        progress: 86,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "382",
        evidence: "70%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "CON-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Connected Applications actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for integrations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [82, 61, 56, 86, 83, 72, 90, 95],
    external: {
      domain: "Multiple institution-approved systems",
      mode: "connector-catalogue",
    },
    signature: "integrations::connected-apps::connection::6",
  },
  sis: {
    id: "sis",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Student Information System",
    description: "Student, program, enrolment, result and academic-record synchronization.",
    group: "Institution Systems",
    icon: "database",
    layout: "import",
    primaryAction: "Start import",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Rows staged",
        value: "79",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Validated",
        value: "95",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Duplicates",
        value: "96",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Errors",
        value: "67",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Import batch",
      },
      {
        key: "code",
        label: "Source",
      },
      {
        key: "people",
        label: "Rows",
      },
      {
        key: "progress",
        label: "Validated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SIS-100",
        name: "Student master data",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Active",
        progress: 91,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "192",
        evidence: "72%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SIS-2026",
      },
      {
        id: "SIS-101",
        name: "Program catalogue sync",
        owner: "Research Office",
        scope: "School of Computing",
        status: "On track",
        progress: 57,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "209",
        evidence: "79%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SIS-2027",
      },
      {
        id: "SIS-102",
        name: "Enrolment sync",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 68,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "226",
        evidence: "86%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SIS-2028",
      },
      {
        id: "SIS-103",
        name: "Result publication sync",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 79,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "243",
        evidence: "93%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SIS-2029",
      },
      {
        id: "SIS-104",
        name: "Progression decision sync",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 90,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "260",
        evidence: "66%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SIS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Student Information System actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution systems and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [61, 56, 65, 78, 60, 77, 81, 72],
    external: null,
    signature: "integrations::sis::import::5",
  },
  lms: {
    id: "lms",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Learning Management System",
    description: "Course, roster, resource, activity and assessment-exchange boundaries.",
    group: "Institution Systems",
    icon: "book",
    layout: "import",
    primaryAction: "Start import",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Rows staged",
        value: "17",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Validated",
        value: "62",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Duplicates",
        value: "53",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Errors",
        value: "47",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Import batch",
      },
      {
        key: "code",
        label: "Source",
      },
      {
        key: "people",
        label: "Rows",
      },
      {
        key: "progress",
        label: "Validated",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "LMS-100",
        name: "Course catalogue exchange",
        owner: "University Library",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 95,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "196",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "LMS-2026",
      },
      {
        id: "LMS-101",
        name: "Roster synchronization",
        owner: "Assessment Office",
        scope: "School of Computing",
        status: "Draft",
        progress: 61,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "213",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "LMS-2027",
      },
      {
        id: "LMS-102",
        name: "Resource links",
        owner: "Academic Registry",
        scope: "BAAI Year 3",
        status: "Active",
        progress: 72,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "230",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "LMS-2028",
      },
      {
        id: "LMS-103",
        name: "Assignment exchange",
        owner: "Quality Directorate",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 83,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "247",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "LMS-2029",
      },
      {
        id: "LMS-104",
        name: "Grade return boundary",
        owner: "School of Computing",
        scope: "Assigned cases",
        status: "Review due",
        progress: 94,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "264",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "LMS-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Learning Management System actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for institution systems and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [65, 57, 76, 91, 95, 86, 73, 59],
    external: null,
    signature: "integrations::lms::import::5",
  },
  "identity-sso": {
    id: "identity-sso",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Identity, SSO & SCIM",
    description: "Single sign-on, lifecycle provisioning, groups, claims and emergency revocation.",
    group: "Identity",
    icon: "key",
    layout: "connection",
    primaryAction: "Test connection",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connected services",
        value: "72",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Handoff success",
        value: "88%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Scope checks",
        value: "59",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "12",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "IDEN-100",
        name: "Institution SSO",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "On track",
        progress: 71,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "247",
        evidence: "97%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "IDE-2026",
      },
      {
        id: "IDEN-101",
        name: "SCIM learner provisioning",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Review due",
        progress: 82,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "264",
        evidence: "70%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "IDE-2027",
      },
      {
        id: "IDEN-102",
        name: "Faculty group mapping",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 93,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "281",
        evidence: "77%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "IDE-2028",
      },
      {
        id: "IDEN-103",
        name: "MFA claims",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 59,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "298",
        evidence: "84%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "IDE-2029",
      },
      {
        id: "IDEN-104",
        name: "Emergency revocation",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "Draft",
        progress: 70,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "315",
        evidence: "91%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "IDE-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Identity, SSO & SCIM actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for identity and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [74, 91, 64, 78, 81, 82, 72, 80],
    external: {
      domain: "Institution identity provider",
      mode: "OIDC-SAML-SCIM",
    },
    signature: "integrations::identity-sso::connection::5",
  },
  "apis-webhooks": {
    id: "apis-webhooks",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "APIs & Webhooks",
    description: "Scoped credentials, event subscriptions, schemas, replay, idempotency and health.",
    group: "Developer",
    icon: "code",
    layout: "connection",
    primaryAction: "Test connection",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connected services",
        value: "33",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Handoff success",
        value: "87%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Scope checks",
        value: "49",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "23",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "APIS-100",
        name: "Class event webhook",
        owner: "Finance Office",
        scope: "Whole institution",
        status: "Active",
        progress: 73,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "24",
        evidence: "70%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "API-2026",
      },
      {
        id: "APIS-101",
        name: "Labs evidence webhook",
        owner: "Information Security",
        scope: "School of Computing",
        status: "On track",
        progress: 84,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "41",
        evidence: "77%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "API-2027",
      },
      {
        id: "APIS-102",
        name: "SIS enrolment API",
        owner: "University Library",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 95,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "58",
        evidence: "84%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "API-2028",
      },
      {
        id: "APIS-103",
        name: "Credential verification API",
        owner: "Assessment Office",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 61,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "75",
        evidence: "91%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "API-2029",
      },
      {
        id: "APIS-104",
        name: "Audit export API",
        owner: "Academic Registry",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 72,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "92",
        evidence: "64%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "API-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "APIs & Webhooks actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for developer and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [61, 77, 71, 90, 95, 65, 57, 66],
    external: {
      domain: "api.academy.chatoner.com",
      mode: "OAuth2 + signed webhooks",
    },
    signature: "integrations::apis-webhooks::connection::5",
  },
  "data-exports": {
    id: "data-exports",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Data Exports",
    description: "Purpose-limited exports, approvals, masking, expiry and downloadable evidence.",
    group: "Data",
    icon: "download",
    layout: "export",
    primaryAction: "Request export",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Export requests",
        value: "45",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Approved",
        value: "93",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Expiring",
        value: "73",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Downloaded",
        value: "72",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Export",
      },
      {
        key: "owner",
        label: "Requester",
      },
      {
        key: "scope",
        label: "Purpose",
      },
      {
        key: "date",
        label: "Expires",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "DATA-100",
        name: "Accreditation evidence export",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Review due",
        progress: 54,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "170",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "DAT-2026",
      },
      {
        id: "DATA-101",
        name: "Registrar enrolment export",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Ready",
        progress: 65,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "187",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "DAT-2027",
      },
      {
        id: "DATA-102",
        name: "Finance capacity export",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 76,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "204",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "DAT-2028",
      },
      {
        id: "DATA-103",
        name: "Research repository export",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 87,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "221",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "DAT-2029",
      },
      {
        id: "DATA-104",
        name: "Student success aggregate",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Active",
        progress: 53,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "238",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "DAT-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Data Exports actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for data and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [81, 72, 69, 90, 95, 65, 68, 68],
    external: null,
    signature: "integrations::data-exports::export::5",
  },
  "integration-health": {
    id: "integration-health",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Integration Health",
    description: "Connection status, latency, failures, retries, last sync and named owners.",
    group: "Operations",
    icon: "pulse",
    layout: "integration-health",
    primaryAction: "Run diagnostics",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Connections",
        value: "28",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Healthy",
        value: "91%",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Retries",
        value: "17",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Incidents",
        value: "9",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Connection",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "evidence",
        label: "Health",
      },
      {
        key: "updated",
        label: "Last sync",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "INTE-100",
        name: "Chatoner Class connection",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Draft",
        progress: 72,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "203",
        evidence: "69%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "INT-2026",
      },
      {
        id: "INTE-101",
        name: "Chatoner AI Labs connection",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Active",
        progress: 83,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "220",
        evidence: "76%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "INT-2027",
      },
      {
        id: "INTE-102",
        name: "SIS synchronization",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 94,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "237",
        evidence: "83%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "INT-2028",
      },
      {
        id: "INTE-103",
        name: "LMS synchronization",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 60,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "254",
        evidence: "90%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "INT-2029",
      },
      {
        id: "INTE-104",
        name: "Identity provider",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 71,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "271",
        evidence: "97%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "INT-2030",
      },
      {
        id: "INTE-105",
        name: "Library platform",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Needs attention",
        progress: 82,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "288",
        evidence: "70%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "INT-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Integration Health actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for operations and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [72, 80, 71, 59, 77, 92, 95, 65],
    external: null,
    signature: "integrations::integration-health::integration-health::6",
  },
  "data-dictionary": {
    id: "data-dictionary",
    area: "integrations",
    areaLabel: "Integrations & Data",
    title: "Data Dictionary & Lineage",
    description: "Definitions, ownership, sensitivity, source, transformations and destination lineage.",
    group: "Data",
    icon: "database",
    layout: "matrix",
    primaryAction: "Edit mapping",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Coverage",
        value: "90%",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Complete mappings",
        value: "12",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Gaps",
        value: "9",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Reviews due",
        value: "91",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Outcome / role",
      },
      {
        key: "scope",
        label: "Coverage",
      },
      {
        key: "evidence",
        label: "Evidence",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "DATA-100",
        name: "Student identifier",
        owner: "Student Success",
        scope: "Whole institution",
        status: "Review due",
        progress: 81,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "212",
        evidence: "96%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "DAT-2026",
      },
      {
        id: "DATA-101",
        name: "Program code",
        owner: "Research Office",
        scope: "School of Computing",
        status: "Ready",
        progress: 92,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "229",
        evidence: "69%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "DAT-2027",
      },
      {
        id: "DATA-102",
        name: "Cohort start date",
        owner: "Finance Office",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 58,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "246",
        evidence: "76%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "DAT-2028",
      },
      {
        id: "DATA-103",
        name: "Assessment decision",
        owner: "Information Security",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 69,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "263",
        evidence: "83%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "DAT-2029",
      },
      {
        id: "DATA-104",
        name: "Labs evidence status",
        owner: "University Library",
        scope: "Assigned cases",
        status: "Active",
        progress: 80,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "280",
        evidence: "90%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "DAT-2030",
      },
      {
        id: "DATA-105",
        name: "Credential verification ID",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "On track",
        progress: 91,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "297",
        evidence: "97%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "DAT-2031",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Data Dictionary & Lineage actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for data and uses server-enforced authorization.",
      },
    ],
    fields: [],
    chart: [81, 82, 82, 82, 93, 75, 91, 64],
    external: null,
    signature: "integrations::data-dictionary::matrix::6",
  },
  "settings-profile": {
    id: "settings-profile",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Institution Profile & Branding",
    description: "Names, logos, colours, domains, contacts and public identity.",
    group: "Settings",
    icon: "building",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "32",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "22",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "21",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Institution identity",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "On track",
        progress: 71,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "337",
        evidence: "95%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Branding and domains",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Review due",
        progress: 82,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "354",
        evidence: "68%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Principal contacts",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 93,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "371",
        evidence: "75%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Public information",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 59,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "388",
        evidence: "82%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Accreditation references",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "Draft",
        progress: 70,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "405",
        evidence: "89%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Institution Profile & Branding actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Public institution name",
      "Short name",
      "Logo",
      "Primary colour",
      "Secondary colour",
      "Public domain",
      "Support email",
      "Principal contact",
    ],
    chart: [80, 92, 64, 78, 81, 82, 61, 67],
    external: null,
    signature: "settings::settings-profile::form::5",
  },
  "settings-structure": {
    id: "settings-structure",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Campuses & Academic Structure",
    description: "Default structure, codes, naming and delegated administration.",
    group: "Settings",
    icon: "network",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "22",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "33",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "46",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Campus codes",
        owner: "Information Security",
        scope: "Whole institution",
        status: "On track",
        progress: 89,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "295",
        evidence: "73%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Faculty naming",
        owner: "University Library",
        scope: "School of Computing",
        status: "Review due",
        progress: 55,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "312",
        evidence: "80%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Department delegation",
        owner: "Assessment Office",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 66,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "329",
        evidence: "87%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Academic-unit hierarchy",
        owner: "Academic Registry",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 77,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "346",
        evidence: "94%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Committee authority",
        owner: "Quality Directorate",
        scope: "Assigned cases",
        status: "Draft",
        progress: 88,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "363",
        evidence: "67%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Campuses & Academic Structure actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Campus code format",
      "Faculty code format",
      "Department code format",
      "Default delegation",
      "Academic unit types",
      "Committee naming",
    ],
    chart: [80, 61, 77, 71, 80, 71, 69, 90],
    external: null,
    signature: "settings::settings-structure::form::5",
  },
  "settings-language": {
    id: "settings-language",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Language & Accessibility",
    description: "Primary and secondary languages, translation, captions and accessibility defaults.",
    group: "Settings",
    icon: "globe",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "14",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "80",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "7",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Primary language",
        owner: "Research Office",
        scope: "Whole institution",
        status: "Draft",
        progress: 57,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "203",
        evidence: "81%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Secondary language",
        owner: "Finance Office",
        scope: "School of Computing",
        status: "Active",
        progress: 68,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "220",
        evidence: "88%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Translation defaults",
        owner: "Information Security",
        scope: "BAAI Year 3",
        status: "On track",
        progress: 79,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "237",
        evidence: "95%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Caption defaults",
        owner: "University Library",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 90,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "254",
        evidence: "68%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Right-to-left support",
        owner: "Assessment Office",
        scope: "Assigned cases",
        status: "Ready",
        progress: 56,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "271",
        evidence: "75%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Language & Accessibility actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Primary language",
      "Secondary language",
      "Translation review",
      "Caption default",
      "Right-to-left support",
      "Accessible document standard",
    ],
    chart: [72, 59, 56, 76, 91, 74, 80, 92],
    external: null,
    signature: "settings::settings-language::form::5",
  },
  "settings-academic": {
    id: "settings-academic",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Academic Rules",
    description: "Terms, credit values, grading scales, progression rules and award classifications.",
    group: "Settings",
    icon: "sliders",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "8",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "44",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "46",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Academic year",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "On track",
        progress: 53,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "109",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Credit framework",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 64,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "126",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Grading scale",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 75,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "143",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Progression rules",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 86,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "160",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Award classification",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 52,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "177",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Academic Rules actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Academic year start",
      "Credit framework",
      "Pass mark",
      "Grading scale",
      "Progression threshold",
      "Award classification",
    ],
    chart: [62, 56, 76, 91, 74, 91, 64, 78],
    external: null,
    signature: "settings::settings-academic::form::5",
  },
  "settings-notifications": {
    id: "settings-notifications",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Notifications",
    description: "Institution, role, risk, class, Labs, assessment and report notification preferences.",
    group: "Settings",
    icon: "bell",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "6",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "67",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "95",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Urgent risk alerts",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "On track",
        progress: 53,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "319",
        evidence: "85%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Assessment decisions",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 64,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "336",
        evidence: "92%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Class reminders",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 75,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "353",
        evidence: "65%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Labs capacity warnings",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 86,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "370",
        evidence: "72%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Scheduled reports",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 52,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "387",
        evidence: "79%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Notifications actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Urgent risk alerts",
      "Assessment decisions",
      "Class reminders",
      "Labs warnings",
      "Report delivery",
      "Escalation channel",
    ],
    chart: [62, 88, 73, 59, 77, 81, 93, 85],
    external: null,
    signature: "settings::settings-notifications::form::5",
  },
  "settings-security": {
    id: "settings-security",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Security & Sessions",
    description: "Session policy, MFA, allowed domains, high-risk actions and emergency access.",
    group: "Settings",
    icon: "lock",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "89",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "61",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "59",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Session duration",
        owner: "School of Computing",
        scope: "Whole institution",
        status: "Review due",
        progress: 69,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "50",
        evidence: "92%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "MFA policy",
        owner: "Digital Learning",
        scope: "School of Computing",
        status: "Ready",
        progress: 80,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "67",
        evidence: "65%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Allowed domains",
        owner: "Student Success",
        scope: "BAAI Year 3",
        status: "Needs attention",
        progress: 91,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "84",
        evidence: "72%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "High-risk action step-up",
        owner: "Research Office",
        scope: "October 2026 cohort",
        status: "Draft",
        progress: 57,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "101",
        evidence: "79%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Emergency access",
        owner: "Finance Office",
        scope: "Assigned cases",
        status: "Active",
        progress: 68,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "118",
        evidence: "86%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Security & Sessions actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Session duration",
      "MFA enforcement",
      "Allowed domains",
      "Step-up actions",
      "Emergency access",
      "Service accounts",
    ],
    chart: [87, 84, 83, 83, 93, 96, 75, 70],
    external: null,
    signature: "settings::settings-security::form::5",
  },
  "settings-data": {
    id: "settings-data",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Data Regions & Retention",
    description: "Data-region requirements, record classes, retention and deletion defaults.",
    group: "Settings",
    icon: "database",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "41",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "52",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "47",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Primary data region",
        owner: "Academic Registry",
        scope: "Whole institution",
        status: "Active",
        progress: 52,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "108",
        evidence: "82%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Record classes",
        owner: "Quality Directorate",
        scope: "School of Computing",
        status: "On track",
        progress: 63,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "125",
        evidence: "89%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Retention defaults",
        owner: "School of Computing",
        scope: "BAAI Year 3",
        status: "Review due",
        progress: 74,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "142",
        evidence: "96%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Legal hold workflow",
        owner: "Digital Learning",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 85,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "159",
        evidence: "69%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Deletion approvals",
        owner: "Student Success",
        scope: "Assigned cases",
        status: "Needs attention",
        progress: 96,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "176",
        evidence: "76%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Data Regions & Retention actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Primary data region",
      "Backup region",
      "Retention default",
      "Legal hold approval",
      "Deletion quorum",
      "Export expiry",
    ],
    chart: [61, 56, 76, 91, 85, 73, 80, 71],
    external: null,
    signature: "settings::settings-data::form::5",
  },
  "settings-connected-platforms": {
    id: "settings-connected-platforms",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Connected Platform Policies",
    description: "Class, Labs and Build Room handoff, visibility, consent and evidence rules.",
    group: "Settings",
    icon: "link",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "54",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "35",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "46",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Class handoff policy",
        owner: "Assessment Office",
        scope: "Whole institution",
        status: "Ready",
        progress: 76,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "207",
        evidence: "89%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Labs entitlement policy",
        owner: "Academic Registry",
        scope: "School of Computing",
        status: "Needs attention",
        progress: 87,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "224",
        evidence: "96%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Build Room visibility",
        owner: "Quality Directorate",
        scope: "BAAI Year 3",
        status: "Draft",
        progress: 53,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "241",
        evidence: "69%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Evidence return consent",
        owner: "School of Computing",
        scope: "October 2026 cohort",
        status: "Active",
        progress: 64,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "258",
        evidence: "76%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "External access",
        owner: "Digital Learning",
        scope: "Assigned cases",
        status: "On track",
        progress: 75,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "275",
        evidence: "83%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Connected Platform Policies actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Class handoff scope",
      "Labs entitlement scope",
      "Build Room visibility",
      "Evidence return rule",
      "External access expiry",
      "Emergency revocation",
    ],
    chart: [76, 81, 61, 77, 92, 74, 59, 77],
    external: null,
    signature: "settings::settings-connected-platforms::form::5",
  },
  "settings-credentials": {
    id: "settings-credentials",
    area: "settings",
    areaLabel: "Institution Settings",
    title: "Credential & Verification Policies",
    description: "Issuer authority, evidence, approvals, signatures, revocation and verification.",
    group: "Settings",
    icon: "certificate",
    layout: "form",
    primaryAction: "Save changes",
    secondaryActions: ["Export", "Saved views", "Help"],
    metrics: [
      {
        label: "Profile completeness",
        value: "34",
        trend: "+4.2%",
        icon: "pulse",
      },
      {
        label: "Required fields",
        value: "4",
        trend: "Stable",
        icon: "users",
      },
      {
        label: "Pending approvals",
        value: "20",
        trend: "2 due",
        icon: "book",
      },
      {
        label: "Last verified",
        value: "Today",
        trend: "Updated",
        icon: "check",
      },
    ],
    columns: [
      {
        key: "name",
        label: "Item",
      },
      {
        key: "owner",
        label: "Owner",
      },
      {
        key: "scope",
        label: "Scope",
      },
      {
        key: "progress",
        label: "Progress",
      },
      {
        key: "status",
        label: "Status",
      },
    ],
    rows: [
      {
        id: "SETT-100",
        name: "Issuer authority",
        owner: "Quality Directorate",
        scope: "Whole institution",
        status: "On track",
        progress: 83,
        updated: "8 min ago",
        date: "25 Aug 2026",
        time: "09:00 EAT",
        people: "289",
        evidence: "95%",
        risk: "Low",
        category: "Academic",
        decision: "Pending",
        code: "SET-2026",
      },
      {
        id: "SETT-101",
        name: "Evidence minimums",
        owner: "School of Computing",
        scope: "School of Computing",
        status: "Review due",
        progress: 94,
        updated: "42 min ago",
        date: "27 Aug 2026",
        time: "10:30 EAT",
        people: "306",
        evidence: "68%",
        risk: "Medium",
        category: "Operational",
        decision: "Approved",
        code: "SET-2027",
      },
      {
        id: "SETT-102",
        name: "Approval matrix",
        owner: "Digital Learning",
        scope: "BAAI Year 3",
        status: "Ready",
        progress: 60,
        updated: "Today",
        date: "03 Sep 2026",
        time: "14:00 EAT",
        people: "323",
        evidence: "75%",
        risk: "High",
        category: "Quality",
        decision: "Needs information",
        code: "SET-2028",
      },
      {
        id: "SETT-103",
        name: "Digital signature",
        owner: "Student Success",
        scope: "October 2026 cohort",
        status: "Needs attention",
        progress: 71,
        updated: "Yesterday",
        date: "18 Sep 2026",
        time: "16:00 EAT",
        people: "340",
        evidence: "82%",
        risk: "Low",
        category: "Student success",
        decision: "Not required",
        code: "SET-2029",
      },
      {
        id: "SETT-104",
        name: "Revocation and verification",
        owner: "Research Office",
        scope: "Assigned cases",
        status: "Draft",
        progress: 82,
        updated: "18 Aug 2026",
        date: "12 Nov 2026",
        time: "All day",
        people: "357",
        evidence: "89%",
        risk: "Medium",
        category: "Technology",
        decision: "Scheduled",
        code: "SET-2030",
      },
    ],
    tabs: ["Overview", "Active work", "Reviews", "History"],
    notes: [
      {
        title: "Authority boundary",
        body: "Credential & Verification Policies actions are evaluated against institution role, delegated scope, purpose, approval and audit policy.",
      },
      {
        title: "Connected Academy context",
        body: "Academy, Chatoner Class, AI Labs and Build Rooms share only authorized context and retain independent authority.",
      },
      {
        title: "Production integration",
        body: "This workspace maps to the NestJS backend domain for settings and uses server-enforced authorization.",
      },
    ],
    fields: [
      "Issuer name",
      "Signing authority",
      "Evidence minimum",
      "Approval quorum",
      "Verification visibility",
      "Revocation authority",
    ],
    chart: [74, 80, 92, 95, 96, 75, 81, 82],
    external: null,
    signature: "settings::settings-credentials::form::5",
  },
};
export const INSTITUTION_ROLES: RoleProfile[] = [
  {
    id: "principal",
    name: "Institution Owner / Principal Administrator",
    scope: "Whole institution",
    areas: ["*"],
    manage: ["*"],
    approve: ["*"],
  },
  {
    id: "registrar",
    name: "Registrar",
    scope: "Academic registry and official records",
    areas: [
      "command",
      "structure",
      "people",
      "academics",
      "assessment",
      "analytics",
      "integrations",
      "settings",
    ],
    manage: ["people", "academics", "assessment"],
    approve: ["assessment", "academics"],
  },
  {
    id: "dean",
    name: "Dean",
    scope: "Assigned faculty and programs",
    areas: [
      "command",
      "structure",
      "people",
      "academics",
      "class",
      "labs",
      "build",
      "assessment",
      "research",
      "success",
      "quality",
      "analytics",
    ],
    manage: ["academics", "people", "class", "assessment", "research"],
    approve: ["academics", "assessment"],
  },
  {
    id: "hod",
    name: "Head of Department / Program Leader",
    scope: "Assigned department and cohorts",
    areas: ["command", "people", "academics", "class", "labs", "build", "assessment", "success", "analytics"],
    manage: ["academics", "class", "labs", "build", "assessment"],
    approve: ["build"],
  },
  {
    id: "quality",
    name: "Quality & Accreditation Officer",
    scope: "Quality, policy and accreditation",
    areas: [
      "command",
      "academics",
      "assessment",
      "research",
      "quality",
      "analytics",
      "resources",
      "integrations",
    ],
    manage: ["quality", "analytics"],
    approve: ["quality", "academics"],
  },
  {
    id: "assessment",
    name: "Assessment Board / Moderator",
    scope: "Assigned assessments and boards",
    areas: ["command", "people", "academics", "class", "build", "assessment", "quality", "analytics"],
    manage: ["assessment"],
    approve: ["assessment"],
  },
  {
    id: "success",
    name: "Student Success, Wellbeing & Accessibility",
    scope: "Authorized student-support cases",
    areas: ["command", "people", "class", "success", "analytics", "resources", "settings"],
    manage: ["success"],
    approve: ["success"],
  },
  {
    id: "research",
    name: "Research & Ethics Officer",
    scope: "Research portfolio and ethics cases",
    areas: ["command", "people", "research", "quality", "analytics", "resources", "integrations"],
    manage: ["research"],
    approve: ["research"],
  },
  {
    id: "finance",
    name: "Finance & Procurement Officer",
    scope: "Budgets, aid, procurement and invoices",
    areas: ["command", "success", "analytics", "resources", "integrations", "settings"],
    manage: ["resources"],
    approve: ["resources"],
  },
  {
    id: "library",
    name: "Library & Learning Resources Officer",
    scope: "Library, repository and learning resources",
    areas: ["academics", "class", "research", "resources", "integrations"],
    manage: ["resources", "academics"],
    approve: [],
  },
  {
    id: "it",
    name: "Information Security, IT & Integration Administrator",
    scope: "Identity, integrations and technical health",
    areas: [
      "command",
      "class",
      "labs",
      "build",
      "quality",
      "analytics",
      "resources",
      "integrations",
      "settings",
    ],
    manage: ["integrations", "settings"],
    approve: ["integrations"],
  },
];
